import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus, vs } from 'react-syntax-highlighter/dist/esm/styles/prism';
import Editor from 'react-simple-code-editor';
import Prism from 'prismjs';
import 'prismjs/components/prism-python';
import 'prismjs/themes/prism-tomorrow.css';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Send,
  Plus,
  MessageSquare,
  Settings,
  Database,
  History,
  User,
  Eye,
  EyeOff,
  X,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ChevronLeft,
  ThumbsUp,
  ThumbsDown,
  Copy,
  Play,
  Code,
  BarChart3,
  Table,
  Loader2,
  Sun,
  Moon,
  Search,
  Layers,
  Edit,
  Check,
  Menu,
  Zap,
  Activity,
  Sparkles,
  FileText,
  Mountain,
  FolderTree,
  FolderOpen,
  XCircle,
  Info,
  Trash2,
  Download,
  Images,
  CreditCard,
  Terminal,
  Upload,
  PlusCircle,
  BookOpen,
  UserPlus,
  UserMinus,
  RefreshCw,
  Link,
  Bell,
  ShieldCheck,
  Save,
  CheckCircle
} from 'lucide-react';
import { Routes, Route } from 'react-router-dom';
import CodeIcon from './components/CodeIcon';
import EmailFirstAuthModal from './components/EmailFirstAuthModal';
import ResetPasswordPage from './components/ResetPasswordPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import SharedChatView from './components/SharedChatView';
import OAuthCallback from './components/OAuthCallback';
import UserMenu from './components/UserMenu';
// ChatHistory component removed - using built-in History tab instead
import TerminalConsoleModal from './components/TerminalConsoleModal';
import AddDatasourceModal from './components/AddDatasourceModal';
import EditDatasourceModal from './components/EditDatasourceModal';
import DatasourceSelectionModal from './components/DatasourceSelectionModal';
import DataNotesModal from './components/DataNotesModal';
import SnippetModal from './components/SnippetModal';
import EditFootnoteModal from './components/EditFootnoteModal';
import FilePanel from './components/FilePanel';
import DataSourcesPanel from './components/DataSourcesPanel';
import ProjectsPanel from './components/ProjectsPanel';
import ProjectEditor from './components/ProjectEditor';
import { SAMPLE_PROJECTS, EMPTY_PROJECT_TEMPLATE, formatProjectJson, normalizeProjectJson } from './config/projectSchema';
import ChangelogToast from './components/ChangelogToast';
import ChangelogModal from './components/ChangelogModal';
import UpgradePlansModal from './components/UpgradePlansModal';
import FeedbackModal from './components/FeedbackModal';
import TaurusInstallModal from './components/TaurusInstallModal';
import VideoModal from './components/VideoModal';
import ShareChatModal from './components/ShareChatModal';
import ReviewNotesModal from './components/ReviewNotesModal';
import GuidedTour from './components/GuidedTour';
import SkillsPanel from './components/SkillsPanel';
import AuditDashboard from './components/AuditDashboard';
import { trackModeSwitch, trackQuerySubmit, trackFeatureUse, trackDatasource } from './utils/analytics';
import { useAuth } from './contexts/AuthContext';
import { API_ENDPOINTS, APP_INFO } from './config';
import './App.css';

// Backward compatibility alias for dependency arrays
const API = API_ENDPOINTS.main;

// Utility: strip ANSI color codes (with or without ESC prefix)
const stripAnsi = (text) => {
  if (!text) return text;
  try {
    // With ESC prefix
    let cleaned = text.replace(/[\u001B\u009B][[\]()#;?]*(?:[0-9]{1,4}(?:;[0-9]{0,4})*)?[0-9A-ORZcf-nqry=><]/g, '');
    // Fallback for sequences where ESC was dropped but bracket codes remain like "[38;2;...m"
    cleaned = cleaned.replace(/\[[0-9;?]*m/g, '');
    return cleaned;
  } catch {
    return text;
  }
};

// Utility: Format database type for display
const formatDbType = (dbType) => {
  if (!dbType) return '';
  const typeMap = {
    'mysql': 'MySQL',
    'postgresql': 'PostgreSQL',
    'mssql': 'MSSQL',
    'snowflake': 'Snowflake'
  };
  return typeMap[dbType.toLowerCase()] || dbType;
};

// Utility: Format dictionary-like responses from Smolagents into readable markdown
const formatDictResponse = (content) => {
  if (!content || typeof content !== 'string') return content;

  const trimmed = content.trim();

  // Detect pandas DataFrame repr: has ≥2 lines starting with a row index (digit + whitespace)
  // e.g. "0   APPLE INC   COM   227917808   61961735283"
  {
    const lines = trimmed.split('\n').filter(l => l !== '');
    const dataLines = lines.filter(l => /^\s*\d+\s/.test(l));
    const hdrLines  = lines.filter(l => !/^\s*\d+\s/.test(l));
    if (dataLines.length >= 2 && hdrLines.length >= 1) {
      // Format a cell value: numeric strings get comma formatting
      const fmtCell = (v) => {
        const s = (v || '').trim();
        const n = Number(s);
        return s !== '' && !isNaN(n) && isFinite(n) ? n.toLocaleString('en-US') : s;
      };
      // Strip leading index (e.g. "0   ") then split on 2+ spaces
      const parseRow = (line) =>
        line.replace(/^\s*\d+\s+/, '').split(/\s{2,}/).map(v => v.trim()).filter(v => v !== '');
      const dataRows = dataLines.map(parseRow);
      const numCols = dataRows[0]?.length || 0;
      if (numCols > 0) {
        const hdrRaw = hdrLines[hdrLines.length - 1].trim();
        let headers = hdrRaw.split(/\s{2,}/).map(h => h.trim()).filter(h => h);
        // If 2+ space split merges underscore-named columns, try single-space split
        if (headers.length !== numCols) {
          const singleSplit = hdrRaw.split(/\s+/).filter(h => h);
          if (singleSplit.length === numCols) {
            headers = singleSplit;
          } else {
            while (headers.length < numCols) headers.push(`Col${headers.length + 1}`);
            headers = headers.slice(0, numCols);
          }
        }
        const headerRow = `| ${headers.join(' | ')} |`;
        const sep       = `| ${headers.map(() => '---').join(' | ')} |`;
        const rows      = dataRows.map(row => `| ${row.map(fmtCell).join(' | ')} |`);
        return [headerRow, sep, ...rows].join('\n');
      }
    }
  }

  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) return content;

  // snake_case / camelCase → Title Case
  const formatKey = (key) =>
    key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
       .replace(/\b\w/g, c => c.toUpperCase());

  // Format numbers with commas for readability (61961735283 → 61,961,735,283)
  const fmtNum = (v) =>
    typeof v === 'number' && isFinite(v) ? v.toLocaleString('en-US') : (v == null ? '' : String(v));

  // Render any JS value as a markdown string
  const formatValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'string') return value;
    if (typeof value === 'boolean') return String(value);
    if (typeof value === 'number') return fmtNum(value);
    if (Array.isArray(value)) {
      if (value.length === 0) return '(empty)';
      if (typeof value[0] === 'object' && value[0] !== null && !Array.isArray(value[0])) {
        // Array of objects → GFM table
        const keys = Object.keys(value[0]);
        const header = `| ${keys.map(formatKey).join(' | ')} |`;
        const sep    = `| ${keys.map(() => '---').join(' | ')} |`;
        const rows   = value.map(row =>
          `| ${keys.map(k => fmtNum(row[k])).join(' | ')} |`
        );
        return [header, sep, ...rows].join('\n');
      }
      return value.map(v => `- ${fmtNum(v)}`).join('\n');
    }
    if (typeof value === 'object') {
      return Object.entries(value).map(([k, v]) => `- **${formatKey(k)}**: ${fmtNum(v)}`).join('\n');
    }
    return String(value);
  };

  const buildSections = (parsed) => {
    const SUMMARY_KEYS = new Set(['summary', 'Summary', 'results', 'Results', 'conclusions', 'Conclusions', 'analysis', 'Analysis', 'recommendations', 'Recommendations']);
    const sections = [];
    const done = new Set();
    // Render summary-type keys last (data first, conclusions last)
    for (const [key, value] of Object.entries(parsed)) {
      if (!SUMMARY_KEYS.has(key)) { sections.push(`**${formatKey(key)}**\n\n${formatValue(value)}`); done.add(key); }
    }
    for (const [key, value] of Object.entries(parsed)) {
      if (SUMMARY_KEYS.has(key) && !done.has(key)) sections.push(`**${formatKey(key)}**\n\n${formatValue(value)}`);
    }
    return sections.join('\n\n---\n\n');
  };

  // Pre-normalize: strip numpy wrapper types → np.int64(11621) → 11621
  const normalized = trimmed.replace(/np\.\w+\((-?[\d.]+(?:[eE][+-]?\d+)?)\)/g, '$1');

  // Path 1: naive quote swap — works when no apostrophes in string values
  try {
    const jsonString = normalized
      .replace(/'/g, '"')
      .replace(/\bTrue\b/g, 'true')
      .replace(/\bFalse\b/g, 'false')
      .replace(/\bNone\b/g, 'null');
    const parsed = JSON.parse(jsonString);
    if (typeof parsed === 'object' && !Array.isArray(parsed)) return buildSections(parsed);
    return content;
  } catch (_) { /* fall through */ }

  // Path 2: regex extraction — handles apostrophes in values (e.g. "Mother's Day")
  // Matches 'key': "double-quoted value", 'key': 'single-quoted value', or 'key': number
  const extracted = {};
  const re = /'(\w+)'\s*:\s*(?:"([^"]*)"|'([^']*)'|(-?\d+\.?\d*(?:[eE][+-]?\d+)?)|(True|False|None))/g;
  let m;
  while ((m = re.exec(normalized)) !== null) {
    extracted[m[1]] =
      m[2] !== undefined ? m[2] :          // double-quoted string  →  handles apostrophes
      m[3] !== undefined ? m[3] :          // single-quoted string
      m[4] !== undefined ? parseFloat(m[4]) :  // number
      m[5] === 'True'    ? true  :
      m[5] === 'False'   ? false : null;
  }
  if (Object.keys(extracted).length > 0) return buildSections(extracted);

  return content;
};

// Utility: Markdown-Lite renderer for Taurus output
// Only touches specific patterns (bold, headers, tables) - preserves ALL structure/indentation
// Much simpler than react-markdown and avoids breaking tool calls, lists, or formatting
const renderTaurusMarkdown = (text) => {
  if (!text || typeof text !== 'string') return '';

  // Escape HTML to prevent XSS (SDK output is trusted but good practice)
  const escapeHtml = (str) => {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  };

  // Parse markdown tables (lines with | separators)
  // Note: Input is already HTML-escaped, so cells are safe to insert directly
  const parseTable = (tableText) => {
    const lines = tableText.trim().split('\n');
    if (lines.length < 2) return tableText; // Need at least header + separator

    const rows = lines.map(line =>
      line.split('|')
        .map(cell => cell.trim())
        .filter(cell => cell.length > 0)
    );

    // Skip separator row (row with ---)
    const header = rows[0];
    const body = rows.slice(2); // Skip header and separator

    let html = '<table class="border-collapse border border-gray-300 dark:border-gray-600 my-3 min-w-full">';

    // Header
    html += '<thead class="bg-gray-100 dark:bg-gray-700"><tr>';
    header.forEach(cell => {
      html += `<th class="border border-gray-300 dark:border-gray-600 px-3 py-2 text-left text-gray-900 dark:text-white font-semibold">${cell}</th>`;
    });
    html += '</tr></thead>';

    // Body
    html += '<tbody>';
    body.forEach(row => {
      html += '<tr class="hover:bg-gray-50 dark:hover:bg-gray-800">';
      row.forEach(cell => {
        html += `<td class="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-800 dark:text-gray-100">${cell}</td>`;
      });
      html += '</tr>';
    });
    html += '</tbody></table>';

    return html;
  };

  // Escape HTML first to prevent XSS, then process markdown
  let processed = escapeHtml(text);

  // Store tables temporarily (they need special handling)
  const tables = [];
  processed = processed.replace(
    /(?:^|\n)((?:\|.+\|\n)+)/gm,
    (match, tableText) => {
      if (tableText.includes('---')) {
        const placeholder = `__TABLE_${tables.length}__`;
        tables.push(parseTable(tableText));
        return '\n' + placeholder + '\n';
      }
      return match;
    }
  );

  // Process markdown
  processed = processed
    // Headers (## and ###) - only at start of line, reduced spacing
    .replace(/^### (.+)$/gm, (match, content) =>
      `<h3 class="text-lg font-semibold mt-3 mb-1 text-gray-900 dark:text-white">${content}</h3>`)
    .replace(/^## (.+)$/gm, (match, content) =>
      `<h2 class="text-xl font-bold mt-3 mb-1 text-gray-900 dark:text-white">${content}</h2>`)

    // Bold **text** - non-greedy match
    .replace(/\*\*(.+?)\*\*/g, (match, content) =>
      `<strong class="font-semibold text-gray-900 dark:text-white">${content}</strong>`)

    // Preserve newlines as line breaks
    .replace(/\n/g, '<br/>');

  // Restore tables
  tables.forEach((tableHtml, i) => {
    processed = processed.replace(`__TABLE_${i}__`, tableHtml);
  });

  return processed;
};

// Component: Collapsible section for large tool outputs (PDFs, etc.)
const CollapsibleToolOutput = ({ title, content, defaultCollapsed = true }) => {
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

  return (
    <div className="my-1">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium"
      >
        {isCollapsed ? (
          <ChevronRight className="w-3 h-3" />
        ) : (
          <ChevronDown className="w-3 h-3" />
        )}
        {title}
      </button>
      {!isCollapsed && (
        <div className="mt-1 pl-4 border-l-2 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400">
          <pre className="whitespace-pre-wrap text-xs">{content}</pre>
        </div>
      )}
    </div>
  );
};

// Utility: Parse content into parts (text vs collapsible outputs) - pure function, no React
const parseTaurusContent = (content) => {
  if (!content) return [];

  // Combined regex to match FILES_LIST, PDF, and TOOL_CODE blocks in a single pass
  // Group 1-3: FILES_LIST (full match, count, content)
  // Group 4-9: PDF (full match, filename, start page, end page, total pages, content)
  // Group 10-12: TOOL_CODE (full match, title with icon, content)
  const combinedRegex = /(?:(FILES_LIST: \((\d+) files?\)\n={50}\n([\s\S]*?)--- END FILES_LIST ---\n)|(PDF: (.+?) \(pages (\d+)-(\d+) of (\d+)\)\n={50}\n([\s\S]*?)--- END PDF ---\n)|(TOOL_CODE: (.+?)\n={50}\n([\s\S]*?)--- END TOOL_CODE ---\n))/g;

  const parts = [];
  let lastIndex = 0;
  let match;
  let toolCodeCounter = 0;

  while ((match = combinedRegex.exec(content)) !== null) {
    // Add text content before this match
    if (match.index > lastIndex) {
      const beforeContent = content.slice(lastIndex, match.index);
      if (beforeContent.trim()) {
        parts.push({ type: 'text', content: beforeContent, key: `text-${lastIndex}` });
      }
    }

    // Determine which pattern matched and build the part
    if (match[1]) {
      // FILES_LIST matched
      const title = `📁 Available Files (${match[2]})`;
      parts.push({
        type: 'files',
        title: title,
        content: match[3].trim(),
        key: `files-${title}`
      });
    } else if (match[4]) {
      // PDF matched
      const title = `📄 ${match[5]} (pages ${match[6]}-${match[7]} of ${match[8]})`;
      parts.push({
        type: 'pdf',
        title: title,
        content: match[9].trim(),
        key: `pdf-${title}`
      });
    } else if (match[10]) {
      // TOOL_CODE matched (Python or SQL code block)
      toolCodeCounter++;
      const title = match[11].trim(); // e.g., "🐍 Python (1234 chars)" or "🗄️ SQL (456 chars)"
      parts.push({
        type: 'code',
        title: title,
        content: match[12].trim(),
        key: `code-${toolCodeCounter}-${match.index}`
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add remaining content after last match
  if (lastIndex < content.length) {
    const remaining = content.slice(lastIndex);
    if (remaining.trim()) {
      parts.push({ type: 'text', content: remaining, key: `text-${lastIndex}` });
    }
  }

  // If no matches at all, return content as single text part
  if (parts.length === 0 && content.trim()) {
    parts.push({ type: 'text', content: content, key: 'text-0' });
  }

  return parts;
};

// Component: Render Taurus content with collapsible PDFs and markdown formatting
// Memoized to prevent re-renders when parent state changes (e.g., input typing)
const TaurusContentRenderer = React.memo(({ content, enableMarkdown = true }) => {
  // Memoize parsing to avoid re-running regex on every render
  const parts = useMemo(() => parseTaurusContent(content), [content]);

  // If no parts (empty content), return null
  if (parts.length === 0) {
    return null;
  }

  // If no PDF outputs found, render with markdown formatting
  if (parts.length === 1 && parts[0].type === 'text') {
    if (enableMarkdown) {
      return (
        <div
          className="whitespace-pre-wrap"
          dangerouslySetInnerHTML={{ __html: renderTaurusMarkdown(parts[0].content) }}
        />
      );
    }
    return <pre className="whitespace-pre-wrap">{parts[0].content}</pre>;
  }

  // Render mixed content: markdown for text, collapsible for PDFs/files/code
  return (
    <>
      {parts.map(part => {
        if (part.type === 'pdf' || part.type === 'files') {
          return (
            <CollapsibleToolOutput
              key={part.key}
              title={part.title}
              content={part.content}
              defaultCollapsed={true}
            />
          );
        }
        // Code blocks (Python/SQL) - collapsible with syntax-appropriate styling
        if (part.type === 'code') {
          return (
            <details key={part.key} className="my-2 border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden">
              <summary className="cursor-pointer px-3 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
                <span>{part.title}</span>
              </summary>
              <pre className="p-3 bg-gray-900 dark:bg-gray-950 text-gray-100 text-xs overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap">
                {part.content}
              </pre>
            </details>
          );
        }
        // Text parts get markdown formatting
        if (enableMarkdown) {
          return (
            <div
              key={part.key}
              className="whitespace-pre-wrap"
              dangerouslySetInnerHTML={{ __html: renderTaurusMarkdown(part.content) }}
            />
          );
        }
        return <pre key={part.key} className="whitespace-pre-wrap">{part.content}</pre>;
      })}
    </>
  );
});

// Legacy wrapper for backwards compatibility
const renderTaurusStreamingContent = (content) => {
  return <TaurusContentRenderer content={content} enableMarkdown={false} />;
};

// Utility: batched console output update to prevent OOM from excessive re-renders
// Updates UI at most once per 100ms instead of per-character
const scheduleBatchedConsoleUpdate = (bufferRef, timerRef, setConsoleOutput) => {
  if (!timerRef.current) {
    timerRef.current = setTimeout(() => {
      setConsoleOutput(stripAnsi(bufferRef.current));
      timerRef.current = null;
    }, 100); // Update every 100ms - maintains real-time feel while preventing OOM
  }
};

// Utility: flush any pending console output immediately (use on stream complete)
const flushConsoleBuffer = (bufferRef, timerRef, setConsoleOutput) => {
  if (timerRef.current) {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }
  setConsoleOutput(stripAnsi(bufferRef.current));
};

// Utility: convert base64 to blob URL to reduce memory footprint
// Blob URLs are much more memory-efficient than storing base64 strings
const base64ToBlobUrl = (base64) => {
  try {
    // Strip data URL prefix if present
    let cleanBase64 = base64;
    if (base64.startsWith('data:')) {
      cleanBase64 = base64.split(',')[1];
    }

    const byteString = atob(cleanBase64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: 'image/png' });
    return URL.createObjectURL(blob);
  } catch (e) {
    console.error('Failed to convert base64 to blob URL:', e);
    // Fallback to base64 data URL
    const cleanBase64 = base64.startsWith('data:') ? base64 : `data:image/png;base64,${base64}`;
    return cleanBase64;
  }
};

// Component: Answer Navigator - Shows A1, A2, A3 markers for quick navigation to assistant responses
// Appears on hover over scroll arrows, disabled during streaming
const AnswerNavigator = React.memo(({
  messages,
  chatScrollRef,
  visible,
  onNavigate,
  onMouseEnter,
  onMouseLeave
}) => {
  // Get assistant message indices (A1, A2, A3...)
  const answerIndices = useMemo(() => {
    const indices = [];
    messages.forEach((msg, idx) => {
      if (msg.type !== 'user' && msg.type !== 'system') {
        indices.push({ messageIndex: idx, answerNumber: indices.length + 1 });
      }
    });
    return indices;
  }, [messages]);

  // Calculate current answer based on scroll position
  const getCurrentAnswer = useCallback(() => {
    if (!chatScrollRef.current) return 0;
    const container = chatScrollRef.current;
    const containerRect = container.getBoundingClientRect();
    const containerMidpoint = containerRect.top + containerRect.height / 2;

    // Find answer elements and determine which is closest to center
    const answerElements = container.querySelectorAll('[data-answer-index]');
    let currentIdx = 0;
    let minDistance = Infinity;

    answerElements.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const distance = Math.abs(rect.top + rect.height / 2 - containerMidpoint);
      if (distance < minDistance) {
        minDistance = distance;
        currentIdx = parseInt(el.dataset.answerIndex, 10);
      }
    });

    return currentIdx;
  }, [chatScrollRef]);

  const [currentAnswer, setCurrentAnswer] = useState(0);

  // Update current answer when visible
  useEffect(() => {
    if (visible) {
      setCurrentAnswer(getCurrentAnswer());
    }
  }, [visible, getCurrentAnswer]);

  // Build display items (5-7 items with ellipsis, centered on current)
  const displayItems = useMemo(() => {
    if (answerIndices.length === 0) return [];

    const total = answerIndices.length;
    const maxItems = 7;

    if (total <= maxItems) {
      // Show all if 7 or fewer
      return answerIndices.map((a, idx) => ({ ...a, isEllipsis: false, idx }));
    }

    // Center window around current answer
    const items = [];
    const windowSize = 5;
    const halfWindow = Math.floor(windowSize / 2);

    let startIdx = Math.max(0, currentAnswer - halfWindow);
    let endIdx = Math.min(total - 1, startIdx + windowSize - 1);

    // Adjust if we hit the end
    if (endIdx === total - 1) {
      startIdx = Math.max(0, endIdx - windowSize + 1);
    }

    // Add first item + ellipsis if needed
    if (startIdx > 0) {
      items.push({ ...answerIndices[0], isEllipsis: false, idx: 0 });
      if (startIdx > 1) {
        items.push({ isEllipsis: true, idx: -1 });
      }
    }

    // Add window items
    for (let i = startIdx; i <= endIdx; i++) {
      if (i === 0 && startIdx > 0) continue; // Already added first
      items.push({ ...answerIndices[i], isEllipsis: false, idx: i });
    }

    // Add ellipsis + last item if needed
    if (endIdx < total - 1) {
      if (endIdx < total - 2) {
        items.push({ isEllipsis: true, idx: -2 });
      }
      items.push({ ...answerIndices[total - 1], isEllipsis: false, idx: total - 1 });
    }

    return items;
  }, [answerIndices, currentAnswer]);

  const handleClick = useCallback((answerIdx) => {
    if (!chatScrollRef.current) return;
    const el = chatScrollRef.current.querySelector(`[data-answer-index="${answerIdx}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'end' });
      onNavigate?.();
    }
  }, [chatScrollRef, onNavigate]);

  if (answerIndices.length < 2 || displayItems.length === 0) return null;

  return (
    <div
      className={`absolute bottom-full mb-1 left-1/2 -translate-x-1/2 z-50
        bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700
        py-1 px-1 transition-opacity duration-150 ${visible ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex flex-col gap-0.5">
        {displayItems.map((item, i) => (
          item.isEllipsis ? (
            <div key={`ellipsis-${i}`} className="px-2 py-0.5 text-xs text-gray-400 dark:text-gray-500 text-center">
              ···
            </div>
          ) : (
            <button
              key={item.answerNumber}
              onClick={() => handleClick(item.idx)}
              className={`px-2 py-0.5 text-xs rounded transition-colors flex items-center gap-1
                ${item.idx === currentAnswer
                  ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-medium'
                  : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              <span>A{item.answerNumber}</span>
              {item.idx === currentAnswer && <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />}
            </button>
          )
        ))}
      </div>
    </div>
  );
});


// Default fallback sample prompts
const DEFAULT_SAMPLE_PROMPTS = [
  "Show me 10 rows from a table",
  "Analyze the data",
  "Create a visualization",
  "What insights can you find?"
];

// Demo file-specific prompts (keyed by filename pattern)
const DEMO_FILE_PROMPTS = {
  'demo_marketing_campaigns': {
    description: "Explore marketing campaign performance across channels, regions, and audience segments.",
    prompts: [
      "Show total spend by channel",
      "Which campaign had the best ROI?",
      "Compare Email vs Social performance",
      "What's the cost per conversion by channel?",
      "Show monthly spend trend",
      "Which region has the highest conversions?",
      "Calculate ROAS for each campaign",
      "Create a heatmap of conversions by channel and region",
      "Which audience segment performs best?",
      "Show a funnel from impressions to conversions",
      "Predict next month's conversions based on the trend",
      "Which factors most influence revenue?"
    ]
  }
};

// Generic file prompts fallback
const GENERIC_FILE_PROMPTS = [
  "Show me the first 10 rows of the data",
  "What columns are in this dataset?",
  "Summarize the data in my file",
  "Show me basic statistics for all numeric columns",
  "Are there any missing values?",
  "Create a visualization of the data"
];

// Helper to get file-specific prompts based on selected files
const getFilePromptsForSelection = (selectedFiles) => {
  if (!selectedFiles || selectedFiles.length === 0) {
    return { prompts: GENERIC_FILE_PROMPTS, description: null };
  }

  // Check if any selected file matches a demo file pattern
  for (const file of selectedFiles) {
    const filename = file.original_filename || file.filename || '';
    for (const [pattern, config] of Object.entries(DEMO_FILE_PROMPTS)) {
      if (filename.toLowerCase().includes(pattern.toLowerCase())) {
        return { prompts: config.prompts, description: config.description };
      }
    }
  }

  return { prompts: GENERIC_FILE_PROMPTS, description: null };
};

// Tab types
const TAB_TYPES = {
  CHAT: 'chat',
  SETTINGS: 'settings',
  SCHEMA: 'schema',
  HISTORY: 'history'
};

// Taurus Usage Display Component
function TaurusUsageDisplay() {
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taurusRunning, setTaurusRunning] = useState(false);

  useEffect(() => {
    const fetchUsage = async () => {
      try {
        setLoading(true);
        const response = await fetch('http://localhost:48002/api/usage', {
          method: 'GET',
          signal: AbortSignal.timeout(3000) // 3s timeout
        });

        if (response.ok) {
          const data = await response.json();
          setUsageData(data);
          setTaurusRunning(true);
          setError(null);
        } else {
          throw new Error('Failed to fetch usage');
        }
      } catch (err) {
        console.log('[Taurus Usage] Could not connect to Taurus server:', err.message);
        setTaurusRunning(false);
        setError('Taurus server is not running');
      } finally {
        setLoading(false);
      }
    };

    fetchUsage();
    // Refresh every 10 seconds
    const interval = setInterval(fetchUsage, 10000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="mt-8 pt-8 border-t border-gray-200 dark:border-gray-700">
      <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
        Usage Tracking
      </h3>

      {/* Taurus Not Running */}
      {!taurusRunning && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            ⚠️ Taurus server is not running. Start Taurus to view session usage stats.
          </p>
        </div>
      )}

      {/* Taurus Running - Show Stats */}
      {taurusRunning && usageData && (
        <>
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
            <p className="text-xs text-blue-600 dark:text-blue-300 mb-3">
              ℹ️ Session estimates only (resets on server restart). Check official sources for accurate billing.
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {usageData.total_queries}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Total Queries</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  ${usageData.total_cost_usd.toFixed(4)}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Estimated Cost</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {usageData.total_duration_s.toFixed(1)}s
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Total Duration</div>
              </div>
              <div>
                <div className="text-xl font-bold text-purple-600 dark:text-purple-400">
                  ${usageData.total_queries > 0 ? (usageData.total_cost_usd / usageData.total_queries).toFixed(4) : '0.0000'}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400">Avg Cost/Query</div>
              </div>
            </div>

            {/* Recent Queries */}
            {usageData.recent_queries && usageData.recent_queries.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recent Queries</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 dark:bg-gray-800">
                      <tr>
                        <th className="px-2 py-1 text-left text-gray-600 dark:text-gray-400">Time</th>
                        <th className="px-2 py-1 text-left text-gray-600 dark:text-gray-400">Model</th>
                        <th className="px-2 py-1 text-right text-gray-600 dark:text-gray-400">Cost</th>
                        <th className="px-2 py-1 text-right text-gray-600 dark:text-gray-400">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {usageData.recent_queries.slice().reverse().map((q, idx) => (
                        <tr key={idx} className="border-t border-gray-200 dark:border-gray-700">
                          <td className="px-2 py-1 text-gray-700 dark:text-gray-300">
                            {new Date(q.timestamp).toLocaleTimeString()}
                          </td>
                          <td className="px-2 py-1 text-gray-700 dark:text-gray-300 font-mono">
                            {q.model.replace('claude-', '')}
                          </td>
                          <td className="px-2 py-1 text-right text-green-600 dark:text-green-400 font-mono">
                            ${q.cost_usd.toFixed(4)}
                          </td>
                          <td className="px-2 py-1 text-right text-gray-700 dark:text-gray-300">
                            {q.duration_s.toFixed(1)}s
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* No queries yet */}
          {usageData.total_queries === 0 && (
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 mb-4">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                No queries yet in this session. Run a Taurus query to see usage stats.
              </p>
            </div>
          )}
        </>
      )}

      {/* Official Billing Links */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4">
        <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-3">
          📊 Check Official Billing
        </h4>
        <p className="text-xs text-purple-800 dark:text-purple-200 mb-3">
          For accurate billing information, check your official usage dashboard:
        </p>
        <div className="space-y-2">
          <a
            href="https://claude.ai/settings/usage"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:underline"
          >
            → Claude Subscription Usage (OAuth mode)
          </a>
          <a
            href="https://console.anthropic.com/settings/usage"
            target="_blank"
            rel="noopener noreferrer"
            className="block text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 hover:underline"
          >
            → Anthropic API Usage (API Key mode)
          </a>
        </div>
      </div>
    </div>
  );
}

// Taurus image attachment constants (outside component to avoid recreation)
const TAURUS_IMAGE_MAX_SIZE = 5 * 1024 * 1024; // 5MB per image
const TAURUS_IMAGE_MAX_COUNT = 3;
const TAURUS_IMAGE_SUPPORTED_TYPES = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

// =============================================================================
// ModelListEditor — reorderable model list for Owner Settings > Model Lists
// Uses up/down arrow buttons (no external drag-drop dependency)
// =============================================================================

const PROVIDER_OPTIONS = ['openrouter', 'groq', 'cerebras'];
const PROVIDER_COLORS = {
  openrouter: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300',
  groq:       'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
  cerebras:   'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300',
};

function ModelListEditor({ title, models, onChange, maxRetries, onMaxRetriesChange }) {
  const moveUp = (index) => {
    if (index === 0) return;
    const next = [...models];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    onChange(next);
  };

  const moveDown = (index) => {
    if (index === models.length - 1) return;
    const next = [...models];
    [next[index], next[index + 1]] = [next[index + 1], next[index]];
    onChange(next);
  };

  const addModel = () => {
    onChange([...models, {
      _uid: `new_${Date.now()}`,
      id: '',
      provider: 'openrouter',
      label: 'New Model',
      ctx_k: 128,
      cost_in: 0.0,
      cost_out: 0.0,
      enabled: true,
    }]);
  };

  const updateModel = (index, updated) => {
    const next = [...models];
    next[index] = updated;
    onChange(next);
  };

  const removeModel = (index) => {
    onChange(models.filter((_, i) => i !== index));
  };

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
        <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
          <span>Max retries:</span>
          <button
            onClick={() => onMaxRetriesChange(Math.max(1, maxRetries - 1))}
            className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 font-mono font-bold"
          >−</button>
          <span className="w-5 text-center font-mono font-semibold text-gray-900 dark:text-white">{maxRetries}</span>
          <button
            onClick={() => onMaxRetriesChange(Math.min(models.length, maxRetries + 1))}
            className="w-6 h-6 flex items-center justify-center rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 font-mono font-bold"
          >+</button>
        </div>
      </div>

      <div className="space-y-1">
        {models.map((model, index) => (
          <div
            key={model._uid}
            className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800"
          >
            {/* Up/down arrows */}
            <div className="flex flex-col flex-shrink-0">
              <button
                onClick={() => moveUp(index)}
                disabled={index === 0}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-20 leading-none"
                title="Move up"
              >
                <ChevronUp className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => moveDown(index)}
                disabled={index === models.length - 1}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-20 leading-none"
                title="Move down"
              >
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Rank badge */}
            <span className="text-xs font-mono text-gray-400 dark:text-gray-500 w-5 text-center flex-shrink-0">
              {index + 1}
            </span>

            {/* Enable toggle */}
            <input
              type="checkbox"
              checked={model.enabled !== false}
              onChange={(e) => updateModel(index, { ...model, enabled: e.target.checked })}
              className="h-4 w-4 rounded text-blue-600 border-gray-300 dark:border-gray-600 flex-shrink-0"
              title="Enable/disable this model"
            />

            {/* Provider select */}
            <select
              value={model.provider || 'openrouter'}
              onChange={(e) => updateModel(index, { ...model, provider: e.target.value })}
              className={`text-xs font-medium px-2 py-0.5 rounded-full border-0 focus:ring-1 focus:ring-blue-500 flex-shrink-0 ${PROVIDER_COLORS[model.provider] || PROVIDER_COLORS.openrouter}`}
            >
              {PROVIDER_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>

            {/* Label */}
            <input
              type="text"
              value={model.label || ''}
              onChange={(e) => updateModel(index, { ...model, label: e.target.value })}
              placeholder="Display name"
              className="text-sm text-gray-700 dark:text-gray-300 bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500 focus:outline-none w-40 flex-shrink-0"
            />

            {/* Model ID */}
            <input
              type="text"
              value={model.id || ''}
              onChange={(e) => updateModel(index, { ...model, id: e.target.value })}
              placeholder="model/id or model-id"
              className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-transparent border-b border-transparent hover:border-gray-300 dark:hover:border-gray-600 focus:border-blue-500 focus:outline-none flex-1 min-w-0"
            />

            {/* Delete */}
            <button
              onClick={() => removeModel(index)}
              className="text-gray-400 hover:text-red-500 dark:hover:text-red-400 flex-shrink-0"
              title="Remove"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>

      <button
        onClick={addModel}
        className="mt-2 flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
      >
        <Plus className="h-3.5 w-3.5" />
        Add model
      </button>
    </div>
  );
}

/** Attach a stable _uid to each model (needed by dnd-kit as item id) */
function withUids(models) {
  return (models || []).map((m, i) => ({ ...m, _uid: m._uid || `${m.id}_${m.provider}_${i}` }));
}

/** Strip _uid before saving to backend */
function stripUids(models) {
  // eslint-disable-next-line no-unused-vars
  return models.map(({ _uid, ...rest }) => rest);
}

function App() {
  const { user, isAuthenticated, loading: authLoading, refreshUser } = useAuth();
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('login');
  const [demoMode, setDemoMode] = useState(false);
  const [authPromptModalOpen, setAuthPromptModalOpen] = useState(false);

  // Credit limit state
  const [creditStatus, setCreditStatus] = useState(null); // {credits_used, credits_remaining, monthly_limit, is_unlimited, resets_at}
  const [creditPopoverOpen, setCreditPopoverOpen] = useState(false);
  const [creditPolicies, setCreditPolicies] = useState([]); // Owner-configurable credit limit policies
  const [policiesLoading, setPoliciesLoading] = useState(false);
  const [policiesSaved, setPoliciesSaved] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try {
      const saved = localStorage.getItem('darkMode');
      return saved !== null ? JSON.parse(saved) : true; // Default to dark mode
    } catch {
      return true; // Default to dark mode if parsing fails
    }
  });

  // Chat mode: 'virgo' | 'taurus' (Standard and Agentic modes removed)
  const [chatMode, setChatMode] = useState(() => {
    try {
      const saved = localStorage.getItem('chatMode');
      if (saved && (saved === 'virgo' || saved === 'taurus')) return saved;
      // Default to Virgo mode
      return 'virgo';
    } catch {
      return 'virgo';
    }
  });

  // Persist dark mode setting to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('darkMode', JSON.stringify(darkMode));
    } catch (error) {
      console.error('Failed to save dark mode preference:', error);
    }
  }, [darkMode]);

  // Keep the app shell matched to the visible mobile viewport across
  // keyboard, browser chrome, and orientation changes.
  useEffect(() => {
    const isMobileViewport = () =>
      window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;

    const getViewportHeight = () => {
      if (isMobileViewport() && window.visualViewport?.height) {
        return Math.round(window.visualViewport.height);
      }
      return window.innerHeight;
    };

    let frameId = null;
    let settleTimeoutId = null;

    const applyAppHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${getViewportHeight()}px`);
    };

    const scheduleAppHeightUpdate = () => {
      if (frameId) cancelAnimationFrame(frameId);
      if (settleTimeoutId) clearTimeout(settleTimeoutId);

      frameId = requestAnimationFrame(() => {
        applyAppHeight();
        // Safari can report an intermediate height during rotation/keyboard transitions.
        settleTimeoutId = setTimeout(applyAppHeight, 250);
      });
    };

    scheduleAppHeightUpdate();

    window.addEventListener('resize', scheduleAppHeightUpdate);
    window.addEventListener('orientationchange', scheduleAppHeightUpdate);
    window.visualViewport?.addEventListener('resize', scheduleAppHeightUpdate);

    return () => {
      if (frameId) cancelAnimationFrame(frameId);
      if (settleTimeoutId) clearTimeout(settleTimeoutId);
      window.removeEventListener('resize', scheduleAppHeightUpdate);
      window.removeEventListener('orientationchange', scheduleAppHeightUpdate);
      window.visualViewport?.removeEventListener('resize', scheduleAppHeightUpdate);
    };
  }, []);

  // Handle Stripe payment redirect params (?payment=success|cancelled)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get('payment');
    if (payment === 'success') {
      // Clean URL first
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh user session to pick up new tier from webhook
      refreshUser().then(() => {
        showNotification('success', 'Payment successful! Your account has been upgraded to Pro.');
        openAccountModal('plans');
      });
    } else if (payment === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
      showNotification('info', 'Checkout cancelled — no charge was made.');
    }

    const billing = params.get('billing');
    if (billing === 'updated') {
      window.history.replaceState({}, '', window.location.pathname);
      const tierBefore = sessionStorage.getItem('tierBeforeBillingPortal') || '';
      sessionStorage.removeItem('tierBeforeBillingPortal');
      refreshUser().then((updatedUser) => {
        const tierAfter = updatedUser?.tier || '';
        let msg = 'Returned from billing portal.';
        if (tierAfter && tierBefore && tierAfter !== tierBefore) {
          if (tierAfter === 'pro') msg = 'You\'re now on Pro!';
          else if (tierAfter === 'free') msg = 'Your subscription has been cancelled. You\'ve been moved to the Free plan.';
          else msg = `Plan updated to ${tierAfter}.`;
        }
        showNotification(tierAfter === 'pro' ? 'success' : 'info', msg);
        openAccountModal('plans');
      });
    }
  }, []);

  // Persist chat mode setting to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('chatMode', chatMode);
    } catch (error) {
      console.error('Failed to save chat mode preference:', error);
    }
  }, [chatMode]);

  // Check for Taurus availability (Hybrid Architecture - Runtime Detection)
  // Uses consecutive failure counter to prevent flickering from transient timeouts
  const taurusFailureCount = useRef(0);
  useEffect(() => {
    const checkTaurus = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout (generous for busy server)

        await fetch(`${API_ENDPOINTS.taurus}/health`, {
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        taurusFailureCount.current = 0; // Reset on success
        setTaurusAvailable(true);
        setTaurusChecking(false);
      } catch (error) {
        taurusFailureCount.current++;
        // Only mark unavailable after 2 consecutive failures (prevents flickering)
        if (taurusFailureCount.current >= 2) {
          setTaurusAvailable(false);
        }
        setTaurusChecking(false);
      }
    };

    checkTaurus();
    // Re-check every 10 seconds
    const interval = setInterval(checkTaurus, 10000);
    return () => clearInterval(interval);
  }, []);

  // Restore title when user returns to tab (after question notification)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && document.title.startsWith('❓')) {
        document.title = 'VerbaGPT';
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Tab management
  const [tabs, setTabs] = useState([
    {
      id: 'chat-1',
      type: TAB_TYPES.CHAT,
      title: 'Chat',
      sessionId: uuidv4(),
      messages: [],
      inputMessage: '',
      isLoading: false,
      mode: chatMode, // Tag initial tab with current mode
      isSaved: true,  // New tabs start as "saved" (nothing to save yet)
      savedConversationId: null,  // Backend ID after first save
      taurusDatasource: null  // Per-tab Taurus datasource (prevents cross-tab contamination)
    }
  ]);
  const [activeTabId, setActiveTabId] = useState('chat-1');

  // UI states
  // Auto-collapse sidebar on mobile (< 768px)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return window.innerWidth < 768;
    } catch {
      return false;
    }
  });
  const [expandedCodeOutput, setExpandedCodeOutput] = useState({});
  const [activeCodeTab, setActiveCodeTab] = useState({});
  const [activeTabState, setActiveTabState] = useState({});
  const [editingCode, setEditingCode] = useState({});
  const [editedCode, setEditedCode] = useState({});
  const [feedback, setFeedback] = useState({});

  // Deep Review states (Taurus accuracy verification)
  const [deepReviewLoading, setDeepReviewLoading] = useState({});
  const [deepReviewFindings, setDeepReviewFindings] = useState({});
  const [deepReviewExpanded, setDeepReviewExpanded] = useState({});
  const [reviewNotesModalOpen, setReviewNotesModalOpen] = useState(false);
  const [pendingReviewMessage, setPendingReviewMessage] = useState(null); // {message, tabId}
  const [pendingAutoReviewContinue, setPendingAutoReviewContinue] = useState(null); // {messageId, nextIndex, tabId} - for auto-continuing after review

  // Chat history states
  const [chatSessions, setChatSessions] = useState([]);
  const [chatHistoryLoading, setChatHistoryLoading] = useState(false);
  const [chatHistoryLoadingMore, setChatHistoryLoadingMore] = useState(false);
  const [loadingConversationId, setLoadingConversationId] = useState(null);  // Track which conversation is being loaded
  const [chatHistoryHasMore, setChatHistoryHasMore] = useState(true);
  const [chatHistoryOffset, setChatHistoryOffset] = useState(0);
  const [chatHistorySearchQuery, setChatHistorySearchQuery] = useState('');
  const CHAT_HISTORY_PAGE_SIZE = 20;
  const chatHistoryLoadingRef = useRef(false);

  // Copy functionality states
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [copiedCodeId, setCopiedCodeId] = useState(null);

  // Save conversation loading state (keyed by tabId)
  const [savingConversation, setSavingConversation] = useState({});

  // Taurus availability (runtime detection for hybrid architecture)
  const [taurusAvailable, setTaurusAvailable] = useState(false);
  const [taurusChecking, setTaurusChecking] = useState(true);

  // Schema and settings states
  const [schema, setSchema] = useState(null);
  const [schemaSearch, setSchemaSearch] = useState('');
  const [selectedTable, setSelectedTable] = useState(null);
  const [tablePreviewModalOpen, setTablePreviewModalOpen] = useState(false);
  const [samplePrompts, setSamplePrompts] = useState(DEFAULT_SAMPLE_PROMPTS);
  const [samplePromptsDescription, setSamplePromptsDescription] = useState('');
  const [tablePreviewData, setTablePreviewData] = useState(null);
  const [loadingTablePreview, setLoadingTablePreview] = useState(false);
  const [settingsTab, setSettingsTab] = useState('databases'); // Active settings tab
  const [reviewSettingsTab, setReviewSettingsTab] = useState('virgo'); // Sub-tab for review settings (virgo/taurus)
  const [ownerSubTab, setOwnerSubTab] = useState('virgo'); // Sub-tab for Owner tab (virgo/api/credit-limits/database-publishing)
  const [adminSubTab, setAdminSubTab] = useState('prompts'); // Sub-tab for Admin tab (prompts/audit)
  const [taurusSubTab, setTaurusSubTab] = useState('model'); // Sub-tab for Taurus tab (model/usage)
  const [taurusProviderTest, setTaurusProviderTest] = useState(null); // { status: 'testing'|'success'|'warning'|'error', message: string }
  const [openRouterModels, setOpenRouterModels] = useState([]); // Models with pricing from OpenRouter API
  const [loadingOpenRouterModels, setLoadingOpenRouterModels] = useState(false);
  const [apiBalances, setApiBalances] = useState(null); // API provider balances
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [modelStats, setModelStats] = useState([]); // Model failure stats
  const [loadingModelStats, setLoadingModelStats] = useState(false);
  const [availableModels, setAvailableModels] = useState(null); // Live model lists from models_config.py
  const [modelConfig, setModelConfig] = useState(null); // Editable model lists for drag-drop UI
  const [modelConfigSaving, setModelConfigSaving] = useState(false);
  const [modelConfigSaved, setModelConfigSaved] = useState(false);
  const [settings, setSettings] = useState({
    // database_url removed for security - use datasources list instead
    openrouter_api_key: '',
    openai_api_key: '',
    groq_api_key: '',
    cerebras_api_key: '',
    openrouter_model: 'openai/gpt-4o',
    openrouter_multimodal: 'qwen/qwen2.5-vl-32b-instruct:free',
    llm_provider: 'openrouter', // 'openrouter', 'groq', or 'cerebras'
    groq_model: 'llama-3.3-70b-versatile',
    cerebras_model: 'llama3.1-8b',
    baseModel: 'qwen/qwen-2.5-coder-32b-instruct:free',
    // Virgo settings (owner-managed)
    schema_model: 'anthropic/claude-haiku-4.5', // Legacy field, will migrate to schema_openrouter_model
    schema_llm_provider: 'openrouter', // 'openrouter', 'groq', or 'cerebras'
    schema_openrouter_model: 'anthropic/claude-haiku-4.5',
    schema_groq_model: 'llama-3.3-70b-versatile',
    schema_cerebras_model: 'llama3.1-8b',
    smol_model: 'mistralai/mistral-small-3.2-24b-instruct',
    schema_include_similarity: 'yes',
    schema_threshold: '0.70',
    qa_history: '1',
    schema_cache_version: '1',
    openai_embedding_model: 'text-embedding-ada-002',
    schema_db_reuse: 'yes',
    // Virgo Review settings
    use_virgo_review: false,
    virgo_review_model: 'anthropic/claude-haiku-4.5',
    // Taurus Review settings
    use_taurus_review: false,
    taurus_review_model: 'anthropic/claude-haiku-4.5',
    // Taurus settings (owner-managed)
    taurus_llm_provider: 'anthropic',  // 'anthropic' or 'openrouter'
    taurus_openrouter_model: 'google/gemini-2.0-flash-001',  // Model when using OpenRouter
    taurus_openrouter_key: '',  // User's own OpenRouter API key for Taurus
    claude_model: 'claude-haiku-4-5-20251001',
    claude_max_output_tokens: '100000',
    taurus_incognito: false,
    taurus_allow_questions: true,  // Allow Claude to ask clarifying questions during execution
    taurus_bash_restrictions: false,  // Block dangerous bash commands
    bash_restrictions_enforced_by_org: false,  // Read-only flag from org settings
    // Promo codes (owner-managed)
    promo_codes: {}
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [activatingDatasource, setActivatingDatasource] = useState(null);
  const [refreshingStatsDatasource, setRefreshingStatsDatasource] = useState(null);
  const [notification, setNotification] = useState(null); // { type: 'success' | 'error', message: 'text' }
  const [isInitializing, setIsInitializing] = useState(false);
  const [showApiKeys, setShowApiKeys] = useState({
    // database_url removed for security
    openrouter_api_key: false,
    openai_api_key: false,
    groq_api_key: false,
    cerebras_api_key: false,
    taurus_openrouter_key: false,  // User's Taurus OpenRouter key
  });
  const settingsLoadingRef = useRef(false);

  // Agentic workflow states
  const [terminalConsoleOpen, setTerminalConsoleOpen] = useState(false);
  const [consoleOutput, setConsoleOutput] = useState('');
  const [isConsoleStreaming, setIsConsoleStreaming] = useState(false);
  const [viewingHistoricalConsole, setViewingHistoricalConsole] = useState(null); // For viewing past investigation threads
  const [agenticFinalCode, setAgenticFinalCode] = useState('');
  const [agenticFinalOutput, setAgenticFinalOutput] = useState('');
  const [agenticFinalNarrative, setAgenticFinalNarrative] = useState('');
  const [agenticChartData, setAgenticChartData] = useState([]);

  // Console output batching to prevent OOM from excessive re-renders
  const consoleBufferRef = useRef('');
  const consoleUpdateTimerRef = useRef(null);

  // Virgo abort controllers for stream cancellation (keyed by tabId)
  const virgoAbortControllersRef = useRef({});
  const virgoSessionIdsRef = useRef({}); // Track session_id by tabId for abort endpoint

  // Taurus abort controllers for stream cancellation (keyed by tabId)
  // Stores {controller: AbortController, requestId: string} for each active request
  const taurusAbortControllersRef = useRef({});

  // Review states (used by both Virgo and Taurus)
  const [reviewLoading, setReviewLoading] = useState({});  // Per tab
  const [reviewContent, setReviewContent] = useState({});  // Per tab
  const [pendingTaurusReview, setPendingTaurusReview] = useState(null);  // Trigger Taurus review after plots render
  const lastScrollTimeRef = useRef(0);  // Throttle scroll during streaming

  // Datasource management states
  const [addDatasourceModalOpen, setAddDatasourceModalOpen] = useState(false);
  const [editDatasourceModalOpen, setEditDatasourceModalOpen] = useState(false);
  const [editingDatasource, setEditingDatasource] = useState(null);
  const [dataNotesModalOpen, setDataNotesModalOpen] = useState(false);
  const [selectedDatasource, setSelectedDatasource] = useState(null);
  const [datasources, setDatasources] = useState([]);
  const [activeDatasource, setActiveDatasource] = useState(null);
  const [generatingDataNotes, setGeneratingDataNotes] = useState({}); // Track datasources with generating data notes {name: true}

  // Database Publishing states (Owner only)
  const [publishedDatasources, setPublishedDatasources] = useState([]);
  const [selectedPublishedDatasource, setSelectedPublishedDatasource] = useState(null);
  const [datasourcePermissions, setDatasourcePermissions] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [publishingLoading, setPublishingLoading] = useState(false);

  // Organizations Management states (Owner only)
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [organizationMembers, setOrganizationMembers] = useState([]);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showAddMembersModal, setShowAddMembersModal] = useState(false);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [orgBashEnforced, setOrgBashEnforced] = useState(false);  // Org-level bash restrictions enforcement
  const [orgBashLoading, setOrgBashLoading] = useState(false);

  // Promo Codes states (Owner only)
  const [showPromoCodeModal, setShowPromoCodeModal] = useState(false);
  const [editingPromoCode, setEditingPromoCode] = useState(null);
  const [promoCodeForm, setPromoCodeForm] = useState({ code: '', tier: 'pro', days: 30, expires: '' });

  // Owner settings reload state
  const [reloadingSettings, setReloadingSettings] = useState(false);

  // Prompt Libraries states (Admin only)
  const [promptLibraries, setPromptLibraries] = useState([]);
  const [selectedLibrary, setSelectedLibrary] = useState(null);
  const [libraryPermissions, setLibraryPermissions] = useState([]);
  const [librariesLoading, setLibrariesLoading] = useState(false);
  const [showCreateLibraryModal, setShowCreateLibraryModal] = useState(false);
  const [librariesOrgError, setLibrariesOrgError] = useState(false);

  // Snippet/Footnote states
  const [snippets, setSnippets] = useState([]);
  const [showSnippetModal, setShowSnippetModal] = useState(false);
  const [showEditFootnoteModal, setShowEditFootnoteModal] = useState(false);
  const [editingFootnote, setEditingFootnote] = useState(null); // null for create mode, object for edit mode
  const [showShareChatModal, setShowShareChatModal] = useState(false);

  // Changelog modal state
  const [showChangelogModal, setShowChangelogModal] = useState(false);

  // Taurus Question modal state (for AskUserQuestion tool)
  const [taurusQuestionModal, setTaurusQuestionModal] = useState({
    open: false,
    requestId: null,
    questions: [],
    tabId: null
  });

  // File Panel state (for Taurus mode)
  const [showFilePanel, setShowFilePanel] = useState(false);
  const [filePanelPath, setFilePanelPath] = useState(''); // Persisted path state

  // Data Sources Panel state (for Virgo mode - database and file context selection)
  const [showDataSourcesPanel, setShowDataSourcesPanel] = useState(false);
  // Parse stored project once — shared by both selectedFiles and activeProject initializers
  const _storedProject = (() => {
    try {
      const raw = localStorage.getItem('vg_active_project');
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const [selectedFiles, setSelectedFiles] = useState(_storedProject?.resolvedFiles || []);
  const [includeDatabase, setIncludeDatabase] = useState(() =>
    localStorage.getItem('vg_include_db') !== 'false'
  );
  // Virgo Projects
  const [activeProject, setActiveProject] = useState(_storedProject);

  // Projects Panel state (for Taurus Project Mode)
  const [showProjectsPanel, setShowProjectsPanel] = useState(false);
  const [projectEditorMode, setProjectEditorMode] = useState(null); // null or project being edited
  const [savedProjects, setSavedProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [runningProject, setRunningProject] = useState(null);

  // Project execution state for step-by-step execution
  const [projectExecution, setProjectExecution] = useState(null);
  // Shape: { project, projectName, steps, defaults, json, currentStepIndex, status: 'running'|'paused'|'completed'|'error', tabId }

  // Taurus database info (legacy - will be removed)
  const [taurusDatabaseInfo, setTaurusDatabaseInfo] = useState(null);

  // Taurus datasource selection (persisted in localStorage)
  const [taurusSelectedDatasource, setTaurusSelectedDatasource] = useState(null);

  // Taurus image attachments (for vision support)
  const [taurusAttachedImages, setTaurusAttachedImages] = useState([]); // [{data: base64, media_type: 'image/png', preview: blob_url}]

  // Refs to track current datasource values (avoids stale closure issues in callbacks)
  const taurusSelectedDatasourceRef = useRef(null);
  const activeDatasourceRef = useRef(null);

  // Unified datasource selection modal (for both Taurus and Virgo)
  const [showDatasourceModal, setShowDatasourceModal] = useState(false);
  const [datasourceModalMode, setDatasourceModalMode] = useState('taurus'); // 'taurus' or 'virgo'
  const [datasourceSearch, setDatasourceSearch] = useState('');

  // Upgrade modal state
  const [upgradePlansModalOpen, setUpgradePlansModalOpen] = useState(false);
  const [upgradePlansDefaultTab, setUpgradePlansDefaultTab] = useState('plans');

  const openAccountModal = (tab = 'plans') => {
    setUpgradePlansDefaultTab(tab);
    setUpgradePlansModalOpen(true);
  };
  const [billingPortalLoading, setBillingPortalLoading] = useState(false);

  const handleBillingPortal = async () => {
    setBillingPortalLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_ENDPOINTS.main}/stripe/billing-portal`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      sessionStorage.setItem('tierBeforeBillingPortal', user?.tier || '');
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Billing portal error:', err);
      showNotification('error', err.response?.data?.detail || 'Failed to open billing portal.');
      setBillingPortalLoading(false);
    }
  };
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [taurusInstallModalOpen, setTaurusInstallModalOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  // Guided tour state
  const [showGuidedTour, setShowGuidedTour] = useState(false);

  // Sample prompts carousel state
  const [samplePromptsPage, setSamplePromptsPage] = useState(0);

  // Handle responsive sidebar on window resize
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      if (isMobile && !sidebarCollapsed) {
        setSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarCollapsed]);

  // Sync chatMode when active tab changes (for tab switching)
  useEffect(() => {
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    if (activeTab && activeTab.mode && activeTab.mode !== chatMode) {
      setChatMode(activeTab.mode);
      localStorage.setItem('chatMode', activeTab.mode);
    }
  }, [activeTabId, tabs, chatMode]);

  // Sync taurusSelectedDatasource when active tab changes (per-tab datasource isolation)
  useEffect(() => {
    const activeTab = tabs.find(tab => tab.id === activeTabId);
    if (activeTab) {
      // Sync global state from tab's datasource (may be null)
      const tabDatasource = activeTab.taurusDatasource || null;
      setTaurusSelectedDatasource(tabDatasource);
      // Update localStorage to match
      if (tabDatasource) {
        localStorage.setItem('taurus_selected_datasource', tabDatasource.name);
      } else {
        localStorage.removeItem('taurus_selected_datasource');
      }
    }
  }, [activeTabId, tabs]);

  const messagesEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const isExecutingCodeRef = useRef(false);
  const userTriggeredCodeRef = useRef(false);

  // Scroll arrow visibility state
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showScrollBottom, setShowScrollBottom] = useState(false);

  // Answer Navigator state (A1, A2, A3 quick navigation)
  const [answerNavVisible, setAnswerNavVisible] = useState(null); // true or null
  const answerNavShowTimerRef = useRef(null);
  const answerNavHideTimerRef = useRef(null);

  // Cleanup answer navigator timers on unmount
  useEffect(() => {
    return () => {
      if (answerNavShowTimerRef.current) clearTimeout(answerNavShowTimerRef.current);
      if (answerNavHideTimerRef.current) clearTimeout(answerNavHideTimerRef.current);
    };
  }, []);

  // Get active tab
  const activeTab = tabs.find(tab => tab.id === activeTabId);

  // Notification helper - defined early so other functions can use it
  const showNotification = useCallback((type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 4000); // Hide after 4 seconds
  }, []);

  // Chat history utility functions
  const generateChatTitle = useCallback((firstUserMessage) => {
    if (!firstUserMessage) return 'New Chat';

    const cleaned = firstUserMessage.trim();
    if (cleaned.length <= 50) {
      return cleaned;
    }

    const words = cleaned.split(' ');
    let title = '';

    for (const word of words) {
      if ((title + ' ' + word).length <= 50) {
        title += (title ? ' ' : '') + word;
      } else {
        break;
      }
    }

    return title + (title.length < cleaned.length ? '...' : '');
  }, []);

  // Generate tab title based on first user message (shorter for tab display)
  const generateTabTitle = useCallback((messages, maxLength = 30) => {
    if (!messages || messages.length === 0) return 'New Chat';

    const firstUserMessage = messages.find(m => m.type === 'user');
    if (!firstUserMessage) return 'New Chat';

    const content = firstUserMessage.content.trim();
    if (content.length <= maxLength) {
      return content;
    }

    const words = content.split(' ');
    let title = '';

    for (const word of words) {
      if ((title + ' ' + word).length <= maxLength) {
        title += (title ? ' ' : '') + word;
      } else {
        break;
      }
    }

    return title + (title.length < content.length ? '...' : '');
  }, []);

  const groupBySession = useCallback((messages) => {
    const sessions = {};

    messages.forEach(message => {
      const sessionId = message.session_id;
      if (!sessions[sessionId]) {
        sessions[sessionId] = {
          sessionId,
          messages: [],
          lastMessage: '',
          firstUserMessage: '',
          timestamp: new Date(message.timestamp),
          messageCount: 0,
          title: '',
          mode: message.mode || 'virgo' // Track session mode from first message
        };
      }

      sessions[sessionId].messages.push(message);
      sessions[sessionId].messageCount++;

      // Track first user message for title generation
      if (message.message && !sessions[sessionId].firstUserMessage) {
        sessions[sessionId].firstUserMessage = message.message;
      }

      // Update last message and timestamp
      const messageTime = new Date(message.timestamp);
      if (messageTime > sessions[sessionId].timestamp) {
        sessions[sessionId].lastMessage = message.message;
        sessions[sessionId].timestamp = messageTime;
      }
    });

    // Generate titles and convert to array
    const sessionArray = Object.values(sessions).map(session => ({
      ...session,
      title: generateChatTitle(session.firstUserMessage)
    }));

    // Sort by timestamp
    return sessionArray.sort((a, b) => b.timestamp - a.timestamp);
  }, [generateChatTitle]);

  const fetchChatHistory = useCallback(async (reset = false) => {
    // Prevent duplicate concurrent calls
    if (chatHistoryLoadingRef.current) {
      return;
    }

    if (reset) {
      chatHistoryLoadingRef.current = true;
      setChatHistoryLoading(true);
      setChatSessions([]);
      setChatHistoryHasMore(false);  // New endpoint loads all at once
    } else {
      setChatHistoryLoadingMore(true);
    }

    try {
      const token = localStorage.getItem('token');
      const headers = {};

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Use new saved-conversations endpoint
      const response = await axios.get(`${API_ENDPOINTS.main}/saved-conversations`, {
        headers,
        params: {
          limit: 50  // Load up to 50 saved conversations
        }
      });

      const conversations = response.data.conversations || [];

      // Transform to session format expected by UI
      const sessions = conversations.map(conv => ({
        sessionId: conv.session_id,
        conversationId: conv.id,  // Backend ID for loading full conversation
        title: conv.title,
        lastMessage: '',  // Will be populated when loaded
        messageCount: 0,  // Will be populated when loaded
        timestamp: new Date(conv.updated_at || conv.created_at),
        mode: conv.mode,
        datasource: conv.datasource
      }));

      setChatSessions(sessions);
      setChatHistoryHasMore(false);  // New endpoint loads all at once

    } catch (error) {
      console.error('Failed to fetch saved conversations:', error);
      if (reset) {
        setChatSessions([]);
      }
    } finally {
      if (reset) {
        setChatHistoryLoading(false);
        chatHistoryLoadingRef.current = false;
      } else {
        setChatHistoryLoadingMore(false);
      }
    }
  }, [API_ENDPOINTS.main]);

  // Settings management functions - combined endpoint for efficiency
  const fetchUserSettings = useCallback(async () => {
    // Prevent duplicate concurrent calls
    if (settingsLoadingRef.current) {
      return;
    }

    settingsLoadingRef.current = true;
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };

      // Fetch settings
      const response = await axios.get(`${API_ENDPOINTS.main}/auth/settings`, { headers });

      // Extract settings from JSONB response
      const settingsData = response.data.settings || {};

      setSettings(prev => ({
        ...prev,
        openrouter_api_key: settingsData.openrouter_api_key || '',
        openai_api_key: settingsData.openai_api_key || '',
        groq_api_key: settingsData.groq_api_key || '',
        cerebras_api_key: settingsData.cerebras_api_key || '',
        openrouter_model: settingsData.openrouter_model || 'openai/gpt-4o',
        openrouter_multimodal: settingsData.openrouter_multimodal || 'qwen/qwen2.5-vl-32b-instruct:free',
        llm_provider: settingsData.llm_provider || 'openrouter',
        groq_model: settingsData.groq_model || 'llama-3.3-70b-versatile',
        cerebras_model: settingsData.cerebras_model || 'llama3.1-8b',
        // Virgo settings
        schema_model: settingsData.schema_model || 'anthropic/claude-haiku-4.5',
        schema_llm_provider: settingsData.schema_llm_provider || 'openrouter',
        schema_openrouter_model: settingsData.schema_openrouter_model || 'anthropic/claude-haiku-4.5',
        schema_groq_model: settingsData.schema_groq_model || 'llama-3.3-70b-versatile',
        schema_cerebras_model: settingsData.schema_cerebras_model || 'llama3.1-8b',
        smol_model: settingsData.smol_model || 'mistralai/mistral-small-3.2-24b-instruct',
        schema_include_similarity: settingsData.schema_include_similarity || 'yes',
        schema_threshold: settingsData.schema_threshold || '0.70',
        qa_history: settingsData.qa_history || '1',
        schema_cache_version: settingsData.schema_cache_version || '1',
        openai_embedding_model: settingsData.openai_embedding_model || 'text-embedding-ada-002',
        schema_db_reuse: settingsData.schema_db_reuse || 'yes',
        // Virgo Review settings
        use_virgo_review: settingsData.use_virgo_review || false,
        virgo_review_model: settingsData.virgo_review_model || 'anthropic/claude-haiku-4.5',
        // Taurus Review settings
        use_taurus_review: settingsData.use_taurus_review || false,
        taurus_review_model: settingsData.taurus_review_model || 'anthropic/claude-haiku-4.5',
        // Taurus settings
        taurus_llm_provider: settingsData.taurus_llm_provider || 'anthropic',
        taurus_openrouter_model: settingsData.taurus_openrouter_model || 'google/gemini-2.0-flash-001',
        taurus_openrouter_key: settingsData.taurus_openrouter_key || '',
        claude_model: settingsData.claude_model || 'claude-haiku-4-5-20251001',
        claude_max_output_tokens: settingsData.claude_max_output_tokens || '100000',
        taurus_incognito: settingsData.taurus_incognito ?? false,
        taurus_allow_questions: settingsData.taurus_allow_questions ?? true,  // Default true, preserve false
        taurus_bash_restrictions: settingsData.taurus_bash_restrictions ?? false,
        bash_restrictions_enforced_by_org: settingsData.bash_restrictions_enforced_by_org ?? false,
        // Promo codes
        promo_codes: settingsData.promo_codes || {}
      }));

      // Also update datasources from the same response
      setDatasources(response.data.datasources || []);
      const active = (response.data.datasources || []).find(ds => ds.is_active);
      setActiveDatasource(active);
    } catch (error) {
      console.error('Failed to fetch user settings:', error);
    } finally {
      setSettingsLoading(false);
      settingsLoadingRef.current = false;
    }
  }, [API]);

  // Fetch credit status (OPTIMIZED: lightweight, no DB write)
  const fetchCreditStatus = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/auth/credit-status`, { headers });
      setCreditStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch credit status:', error);
      // Don't show error to user - this is non-critical
    }
  }, [API]);

  // Fetch credit limit policies (Owner only)
  const fetchCreditPolicies = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || user?.role !== 'owner') return;

      setPoliciesLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/auth/credit-limit-policies`, { headers });
      setCreditPolicies(response.data);
    } catch (error) {
      console.error('Failed to fetch credit policies:', error);
      alert('Failed to load credit limit policies');
    } finally {
      setPoliciesLoading(false);
    }
  }, [API, user]);

  // Update credit limit policy (Owner only)
  const updateCreditPolicy = useCallback(async (tier, role, newLimit) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setPoliciesLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API_ENDPOINTS.main}/auth/update-credit-policy`,
        { tier, role, new_limit: newLimit },
        { headers }
      );

      // Refresh policies
      await fetchCreditPolicies();

      // Show success message
      setPoliciesSaved(true);
      setTimeout(() => setPoliciesSaved(false), 3000);
    } catch (error) {
      console.error('Failed to update credit policy:', error);
      alert('Failed to update credit policy');
    } finally {
      setPoliciesLoading(false);
    }
  }, [API, fetchCreditPolicies]);

  const fetchAvailableModels = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(
        `${API}/owner/available-models`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAvailableModels(response.data);
    } catch (error) {
      console.error('Failed to fetch available models:', error);
    }
  }, [API]);

  const fetchModelConfig = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(
        `${API}/owner/model-config`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = response.data;
      setModelConfig({
        virgo_models: withUids(data.virgo_models),
        review_models: withUids(data.review_models),
        max_virgo_retries: data.max_virgo_retries,
        max_review_retries: data.max_review_retries,
      });
    } catch (error) {
      console.error('Failed to fetch model config:', error);
    }
  }, [API]);

  const saveModelConfig = useCallback(async () => {
    if (!modelConfig) return;
    setModelConfigSaving(true);
    setModelConfigSaved(false);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/owner/model-config`,
        {
          virgo_models: stripUids(modelConfig.virgo_models),
          review_models: stripUids(modelConfig.review_models),
          max_virgo_retries: modelConfig.max_virgo_retries,
          max_review_retries: modelConfig.max_review_retries,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModelConfigSaved(true);
      // Refresh available-models dropdown data too
      fetchAvailableModels();
      setTimeout(() => setModelConfigSaved(false), 3000);
    } catch (error) {
      console.error('Failed to save model config:', error);
      alert('Failed to save model config: ' + (error.response?.data?.detail || error.message));
    } finally {
      setModelConfigSaving(false);
    }
  }, [API, modelConfig, fetchAvailableModels]);

  // Load credit policies and available models when Owner tab is opened
  useEffect(() => {
    if (settingsTab === 'owner' && user?.role === 'owner') {
      fetchCreditPolicies();
      fetchAvailableModels();
      fetchModelConfig();
    }
  }, [settingsTab, user, fetchCreditPolicies, fetchAvailableModels, fetchModelConfig]);

  // Database Publishing functions (Owner only)
  const fetchPublishedDatasources = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || user?.role !== 'owner') return;

      setPublishingLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/owner/published-datasources`, { headers });
      setPublishedDatasources(response.data);
    } catch (error) {
      console.error('Failed to fetch published datasources:', error);
      alert('Failed to load published datasources');
    } finally {
      setPublishingLoading(false);
    }
  }, [API, user]);

  const fetchOrganizations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || user?.role !== 'owner') return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/owner/organizations`, { headers });
      setOrganizations(response.data);
    } catch (error) {
      console.error('Failed to fetch organizations:', error);
      alert('Failed to load organizations');
    }
  }, [API, user]);

  const fetchDatasourcePermissions = useCallback(async (datasourceId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/owner/datasources/${datasourceId}/permissions`, { headers });
      setDatasourcePermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch datasource permissions:', error);
      alert('Failed to load datasource permissions');
    }
  }, [API]);

  const grantDatasourcePermission = useCallback(async (datasourceId, orgId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setPublishingLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API_ENDPOINTS.main}/owner/datasources/${datasourceId}/permissions`,
        { org_id: orgId },
        { headers }
      );

      // Refresh permissions and datasources
      await fetchDatasourcePermissions(datasourceId);
      await fetchPublishedDatasources();

      alert('Permission granted successfully');
    } catch (error) {
      console.error('Failed to grant permission:', error);
      alert('Failed to grant permission');
    } finally {
      setPublishingLoading(false);
    }
  }, [API, fetchDatasourcePermissions, fetchPublishedDatasources]);

  const revokeDatasourcePermission = useCallback(async (datasourceId, orgId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setPublishingLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const orgIdParam = orgId === null ? 'global' : orgId;
      await axios.delete(
        `${API_ENDPOINTS.main}/owner/datasources/${datasourceId}/permissions/${orgIdParam}`,
        { headers }
      );

      // Refresh permissions and datasources
      await fetchDatasourcePermissions(datasourceId);
      await fetchPublishedDatasources();

      alert('Permission revoked successfully');
    } catch (error) {
      console.error('Failed to revoke permission:', error);
      alert('Failed to revoke permission');
    } finally {
      setPublishingLoading(false);
    }
  }, [API, fetchDatasourcePermissions, fetchPublishedDatasources]);

  // Organization Management Functions (Owner only)
  const createOrganization = useCallback(async (name, emailDomain) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || user?.role !== 'owner') return;

      setOrgsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API_ENDPOINTS.main}/admin/organizations`,
        { name, email_domain: emailDomain },
        { headers }
      );

      await fetchOrganizations();
      showNotification('success', `Organization "${name}" created successfully`);
      return true;
    } catch (error) {
      console.error('Failed to create organization:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to create organization');
      return false;
    } finally {
      setOrgsLoading(false);
    }
  }, [API, user, fetchOrganizations, showNotification]);

  const fetchOrganizationMembers = useCallback(async (orgId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/admin/organizations/${orgId}/members`, { headers });
      setOrganizationMembers(response.data);
    } catch (error) {
      console.error('Failed to fetch organization members:', error);
      showNotification('error', 'Failed to load organization members');
    }
  }, [API, showNotification]);

  const updateUserRole = useCallback(async (userId, orgId, role) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setOrgsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API_ENDPOINTS.main}/admin/users/${userId}/organization`,
        { user_id: userId, org_id: orgId, role },
        { headers }
      );

      await fetchOrganizationMembers(orgId);
      showNotification('success', `User role updated to ${role}`);
    } catch (error) {
      console.error('Failed to update user role:', error);
      showNotification('error', 'Failed to update user role');
    } finally {
      setOrgsLoading(false);
    }
  }, [API, fetchOrganizationMembers, showNotification]);

  const deleteOrganization = useCallback(async (orgId, orgName) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || user?.role !== 'owner') return;

      setOrgsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_ENDPOINTS.main}/admin/organizations/${orgId}`, { headers });

      await fetchOrganizations();
      setSelectedOrganization(null);
      showNotification('success', `Organization "${orgName}" deleted successfully`);
      return true;
    } catch (error) {
      console.error('Failed to delete organization:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to delete organization');
      return false;
    } finally {
      setOrgsLoading(false);
    }
  }, [API, user, fetchOrganizations, showNotification]);

  const fetchAvailableUsers = useCallback(async (orgId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/admin/organizations/${orgId}/available-users`, { headers });
      setAvailableUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch available users:', error);
      showNotification('error', 'Failed to load available users');
    }
  }, [API, showNotification]);

  const bulkAddMembers = useCallback(async (orgId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || selectedUserIds.length === 0) return;

      setOrgsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API_ENDPOINTS.main}/admin/organizations/${orgId}/bulk-add-members`,
        { user_ids: selectedUserIds },
        { headers }
      );

      await fetchOrganizationMembers(orgId);
      setShowAddMembersModal(false);
      setSelectedUserIds([]);
      showNotification('success', response.data.message || `Added ${response.data.count} user(s) to organization`);
      return true;
    } catch (error) {
      console.error('Failed to add members:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to add members');
      return false;
    } finally {
      setOrgsLoading(false);
    }
  }, [API, selectedUserIds, fetchOrganizationMembers, showNotification]);

  const removeMemberFromOrg = useCallback(async (orgId, userId, userEmail) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setOrgsLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_ENDPOINTS.main}/admin/organizations/${orgId}/members/${userId}`, { headers });

      await fetchOrganizationMembers(orgId);
      showNotification('success', `Removed ${userEmail} from organization`);
      return true;
    } catch (error) {
      console.error('Failed to remove member:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to remove member');
      return false;
    } finally {
      setOrgsLoading(false);
    }
  }, [API, fetchOrganizationMembers, showNotification]);

  // Org Bash Restrictions Functions (Admin only)
  const fetchOrgBashRestrictions = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !user?.org_id || !(user?.role === 'admin' || user?.role === 'owner')) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/admin/organizations/${user.org_id}`, { headers });
      setOrgBashEnforced(response.data.enforce_taurus_bash_restrictions || false);
    } catch (error) {
      console.error('Failed to fetch org bash restrictions:', error);
    }
  }, [API, user]);

  const updateOrgBashRestrictions = useCallback(async (enforce) => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !user?.org_id) return;

      setOrgBashLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(
        `${API_ENDPOINTS.main}/admin/organizations/${user.org_id}/bash-restrictions`,
        { enforce },
        { headers }
      );
      setOrgBashEnforced(enforce);
      showNotification('success', `Taurus bash restrictions ${enforce ? 'enforced' : 'not enforced'} for organization`);
    } catch (error) {
      console.error('Failed to update org bash restrictions:', error);
      showNotification('error', error.response?.data?.detail || 'Failed to update setting');
    } finally {
      setOrgBashLoading(false);
    }
  }, [API, user, showNotification]);

  // Prompt Libraries API Functions (Admin only)
  const fetchPromptLibraries = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !(user?.role === 'admin' || user?.role === 'owner')) return;

      setLibrariesLoading(true);
      setLibrariesOrgError(false);
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/admin/prompt-libraries`, { headers });
      setPromptLibraries(response.data);
    } catch (error) {
      console.error('Failed to fetch Prompt Libraries:', error);
      // Check if it's the "must be part of an organization" error
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('organization')) {
        setLibrariesOrgError(true);
      } else {
        alert('Failed to load Prompt Libraries');
      }
    } finally {
      setLibrariesLoading(false);
    }
  }, [API, user]);

  const fetchLibraryPermissions = useCallback(async (libraryId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API_ENDPOINTS.main}/admin/prompt-libraries/${libraryId}/permissions`, { headers });
      setLibraryPermissions(response.data);
    } catch (error) {
      console.error('Failed to fetch library permissions:', error);
      alert('Failed to load library permissions');
    }
  }, [API]);

  const grantLibraryPermission = useCallback(async (libraryId, orgId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setLibrariesLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${API_ENDPOINTS.main}/admin/prompt-libraries/${libraryId}/permissions`,
        { org_id: orgId },
        { headers }
      );

      // Refresh permissions and libraries
      await fetchLibraryPermissions(libraryId);
      await fetchPromptLibraries();

      alert('Permission granted successfully');
    } catch (error) {
      console.error('Failed to grant permission:', error);
      alert('Failed to grant permission');
    } finally {
      setLibrariesLoading(false);
    }
  }, [API, fetchLibraryPermissions, fetchPromptLibraries]);

  const revokeLibraryPermission = useCallback(async (libraryId, orgId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setLibrariesLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(
        `${API_ENDPOINTS.main}/admin/prompt-libraries/${libraryId}/permissions/${orgId}`,
        { headers }
      );

      // Refresh permissions and libraries
      await fetchLibraryPermissions(libraryId);
      await fetchPromptLibraries();

      alert('Permission revoked successfully');
    } catch (error) {
      console.error('Failed to revoke permission:', error);
      alert('Failed to revoke permission');
    } finally {
      setLibrariesLoading(false);
    }
  }, [API, fetchLibraryPermissions, fetchPromptLibraries]);

  const deletePromptLibrary = useCallback(async (libraryId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setLibrariesLoading(true);
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_ENDPOINTS.main}/admin/prompt-libraries/${libraryId}`, { headers });

      // Refresh libraries
      await fetchPromptLibraries();
      setSelectedLibrary(null);

      alert('Prompt Library deleted successfully');
    } catch (error) {
      console.error('Failed to delete library:', error);
      alert('Failed to delete library');
    } finally {
      setLibrariesLoading(false);
    }
  }, [API, fetchPromptLibraries]);

  // Load published datasources and organizations when Database Publishing tab is opened
  useEffect(() => {
    if (settingsTab === 'owner' && ownerSubTab === 'database-publishing' && user?.role === 'owner') {
      fetchPublishedDatasources();
      fetchOrganizations();
    }
  }, [settingsTab, ownerSubTab, user, fetchPublishedDatasources, fetchOrganizations]);

  // Load organizations when Organizations tab is opened
  useEffect(() => {
    if (settingsTab === 'owner' && ownerSubTab === 'organizations' && user?.role === 'owner') {
      fetchOrganizations();
    }
  }, [settingsTab, ownerSubTab, user, fetchOrganizations]);

  // Load Prompt Libraries, organizations, and org settings when Admin tab is opened
  useEffect(() => {
    if (settingsTab === 'admin' && (user?.role === 'admin' || user?.role === 'owner')) {
      fetchPromptLibraries();
      fetchOrganizations();
      fetchOrgBashRestrictions();
    }
  }, [settingsTab, user, fetchPromptLibraries, fetchOrganizations, fetchOrgBashRestrictions]);

  // Fetch OpenRouter models with pricing when Taurus tab + OpenRouter provider is selected
  // Using ref to avoid re-fetching on every render while still checking current state
  const openRouterModelsFetchedRef = useRef(false);
  useEffect(() => {
    // Only fetch once per session when conditions are met
    if (settingsTab === 'taurus' &&
      settings.taurus_llm_provider === 'openrouter' &&
      !openRouterModelsFetchedRef.current) {
      openRouterModelsFetchedRef.current = true;
      fetchOpenRouterModels();
    }
  }, [settingsTab, settings.taurus_llm_provider]);

  const saveUserSettings = useCallback(async (overrides = {}) => {
    setSettingsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };

      // Merge current settings with any overrides (for immediate saves before state updates)
      const effectiveSettings = { ...settings, ...overrides };

      // Build settings object for JSONB
      const settingsPayload = {
        settings: {
          openrouter_api_key: effectiveSettings.openrouter_api_key || '',
          openai_api_key: settings.openai_api_key || '',
          groq_api_key: settings.groq_api_key || '',
          cerebras_api_key: settings.cerebras_api_key || '',
          openrouter_model: settings.openrouter_model || 'openai/gpt-4o',
          openrouter_multimodal: settings.openrouter_multimodal || 'qwen/qwen2.5-vl-32b-instruct:free',
          llm_provider: settings.llm_provider || 'openrouter',
          groq_model: settings.groq_model || 'llama-3.3-70b-versatile',
          cerebras_model: settings.cerebras_model || 'llama3.1-8b',
          // Virgo settings (only saved by owner)
          schema_model: settings.schema_model || 'anthropic/claude-haiku-4.5',
          schema_llm_provider: settings.schema_llm_provider || 'openrouter',
          schema_openrouter_model: settings.schema_openrouter_model || 'anthropic/claude-haiku-4.5',
          schema_groq_model: settings.schema_groq_model || 'llama-3.3-70b-versatile',
          schema_cerebras_model: settings.schema_cerebras_model || 'llama3.1-8b',
          smol_model: settings.smol_model || 'mistralai/mistral-small-3.2-24b-instruct',
          schema_include_similarity: settings.schema_include_similarity || 'yes',
          schema_threshold: settings.schema_threshold || '0.70',
          qa_history: settings.qa_history || '1',
          schema_cache_version: settings.schema_cache_version || '1',
          openai_embedding_model: settings.openai_embedding_model || 'text-embedding-ada-002',
          schema_db_reuse: settings.schema_db_reuse || 'yes',
          // Virgo Review settings
          use_virgo_review: settings.use_virgo_review || false,
          virgo_review_model: settings.virgo_review_model || 'anthropic/claude-haiku-4.5',
          // Taurus Review settings
          use_taurus_review: settings.use_taurus_review || false,
          taurus_review_model: settings.taurus_review_model || 'anthropic/claude-haiku-4.5',
          // Taurus settings (only saved by owner)
          taurus_llm_provider: effectiveSettings.taurus_llm_provider || 'anthropic',
          taurus_openrouter_model: effectiveSettings.taurus_openrouter_model || 'google/gemini-2.0-flash-001',
          taurus_openrouter_key: effectiveSettings.taurus_openrouter_key || '',
          claude_model: settings.claude_model || 'claude-haiku-4-5-20251001',
          claude_max_output_tokens: settings.claude_max_output_tokens || '100000',
          taurus_incognito: settings.taurus_incognito || false,
          taurus_allow_questions: settings.taurus_allow_questions !== false,  // Default true
          taurus_bash_restrictions: settings.taurus_bash_restrictions || false,
          // Promo codes (only saved by owner)
          promo_codes: settings.promo_codes || {}
        }
      };

      const response = await axios.put(`${API_ENDPOINTS.main}/auth/settings`, settingsPayload, { headers });

      // Update settings with the response (extract from JSONB)
      const settingsData = response.data.settings || {};
      setSettings(prev => ({
        ...prev,
        openrouter_api_key: settingsData.openrouter_api_key || prev.openrouter_api_key,
        openai_api_key: settingsData.openai_api_key || prev.openai_api_key,
        groq_api_key: settingsData.groq_api_key || prev.groq_api_key,
        cerebras_api_key: settingsData.cerebras_api_key || prev.cerebras_api_key,
        openrouter_model: settingsData.openrouter_model || prev.openrouter_model,
        openrouter_multimodal: settingsData.openrouter_multimodal || prev.openrouter_multimodal,
        llm_provider: settingsData.llm_provider || prev.llm_provider,
        groq_model: settingsData.groq_model || prev.groq_model,
        cerebras_model: settingsData.cerebras_model || prev.cerebras_model,
        // Virgo settings
        schema_model: settingsData.schema_model || prev.schema_model,
        schema_llm_provider: settingsData.schema_llm_provider || prev.schema_llm_provider,
        schema_openrouter_model: settingsData.schema_openrouter_model || prev.schema_openrouter_model,
        schema_groq_model: settingsData.schema_groq_model || prev.schema_groq_model,
        schema_cerebras_model: settingsData.schema_cerebras_model || prev.schema_cerebras_model,
        smol_model: settingsData.smol_model || prev.smol_model,
        schema_include_similarity: settingsData.schema_include_similarity || prev.schema_include_similarity,
        schema_threshold: settingsData.schema_threshold || prev.schema_threshold,
        qa_history: settingsData.qa_history || prev.qa_history,
        schema_cache_version: settingsData.schema_cache_version || prev.schema_cache_version,
        openai_embedding_model: settingsData.openai_embedding_model || prev.openai_embedding_model,
        schema_db_reuse: settingsData.schema_db_reuse || prev.schema_db_reuse,
        // Virgo Review settings
        use_virgo_review: settingsData.use_virgo_review || prev.use_virgo_review,
        virgo_review_model: settingsData.virgo_review_model || prev.virgo_review_model,
        // Taurus Review settings
        use_taurus_review: settingsData.use_taurus_review || prev.use_taurus_review,
        taurus_review_model: settingsData.taurus_review_model || prev.taurus_review_model,
        // Taurus settings
        taurus_llm_provider: settingsData.taurus_llm_provider || prev.taurus_llm_provider,
        taurus_openrouter_model: settingsData.taurus_openrouter_model || prev.taurus_openrouter_model,
        taurus_openrouter_key: settingsData.taurus_openrouter_key || prev.taurus_openrouter_key,
        claude_model: settingsData.claude_model || prev.claude_model,
        claude_max_output_tokens: settingsData.claude_max_output_tokens || prev.claude_max_output_tokens,
        taurus_incognito: settingsData.taurus_incognito ?? prev.taurus_incognito,
        taurus_allow_questions: settingsData.taurus_allow_questions ?? prev.taurus_allow_questions,
        taurus_bash_restrictions: settingsData.taurus_bash_restrictions ?? prev.taurus_bash_restrictions,
        // Promo codes
        promo_codes: settingsData.promo_codes || prev.promo_codes || {}
      }));

      // Show success message, toast, and clear test result (so "Save to persist" button disappears)
      setSettingsSaved(true);
      setTaurusProviderTest(null);
      showNotification('success', 'Settings saved');
      setTimeout(() => setSettingsSaved(false), 3000); // Hide after 3 seconds

      // Clear Taurus cache (fire-and-forget, don't block on errors)
      try {
        await fetch(`${API_ENDPOINTS.taurus}/api/clear-cache`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
        console.log('[Taurus] Cache cleared after settings update');
      } catch (err) {
        // Silently fail if Taurus server not running
        console.log('[Taurus] Failed to clear cache (server may not be running):', err.message);
      }
    } catch (error) {
      console.error('Failed to save user settings:', error);
      showNotification('error', 'Failed to save settings');
    } finally {
      setSettingsLoading(false);
    }
  }, [API, settings, showNotification]);

  // Reload owner settings from database (Owner only)
  const handleReloadSettings = useCallback(async () => {
    setReloadingSettings(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.post(
        `${API}/owner/reload-settings`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showNotification('success', 'Settings reloaded successfully');
      }
    } catch (error) {
      console.error('Failed to reload settings:', error);
      showNotification('error', 'Failed to reload settings. Please try again.');
    } finally {
      setReloadingSettings(false);
    }
  }, [API, showNotification]);

  // Fetch API balances (Owner only)
  const fetchApiBalances = useCallback(async () => {
    setLoadingBalances(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(
        `${API}/owner/api-balances`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setApiBalances(response.data);
    } catch (error) {
      console.error('Failed to fetch API balances:', error);
      showNotification('error', 'Failed to fetch API balances');
    } finally {
      setLoadingBalances(false);
    }
  }, [API, showNotification]);

  const fetchModelStats = useCallback(async () => {
    setLoadingModelStats(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(
        `${API}/owner/model-stats`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModelStats(response.data);
    } catch (error) {
      console.error('Failed to fetch model stats:', error);
    } finally {
      setLoadingModelStats(false);
    }
  }, [API]);

  // Auto-load model stats when Owner tab is opened (defined after fetchModelStats)
  useEffect(() => {
    if (settingsTab === 'owner' && user?.role === 'owner') {
      fetchModelStats();
    }
  }, [settingsTab, user, fetchModelStats]);

  // Poll for data notes generation status
  const pollDataNotesStatus = useCallback(async (datasourceName) => {
    const token = localStorage.getItem('token');
    if (!token) return;

    const headers = { Authorization: `Bearer ${token}` };
    let attempts = 0;
    const maxAttempts = 60; // Poll for up to 5 minutes (60 * 5 seconds)

    const poll = async () => {
      attempts++;
      try {
        const response = await axios.get(
          `${API_ENDPOINTS.main}/data-notes/${encodeURIComponent(datasourceName)}/status`,
          { headers }
        );

        const { status } = response.data;

        if (status === 'completed') {
          // Remove from generating state
          setGeneratingDataNotes(prev => {
            const newState = { ...prev };
            delete newState[datasourceName];
            return newState;
          });
          showNotification('success', `"${datasourceName}" is ready for activation!`);
          // Refresh datasources list to reflect the completed status
          await fetchUserSettings();
          return;
        } else if (status === 'failed') {
          // Remove from generating state
          setGeneratingDataNotes(prev => {
            const newState = { ...prev };
            delete newState[datasourceName];
            return newState;
          });
          showNotification('error', `Data notes generation failed for "${datasourceName}". You can still activate it.`);
          return;
        } else if (attempts < maxAttempts) {
          // Still generating, poll again (faster initially, then slower)
          const delay = attempts < 6 ? 2000 : 5000; // First 12 seconds: every 2s, then every 5s
          setTimeout(poll, delay);
        } else {
          // Max attempts reached, remove generating state
          setGeneratingDataNotes(prev => {
            const newState = { ...prev };
            delete newState[datasourceName];
            return newState;
          });
        }
      } catch (error) {
        console.error('Error polling data notes status:', error);
        // Stop polling on error, remove generating state
        setGeneratingDataNotes(prev => {
          const newState = { ...prev };
          delete newState[datasourceName];
          return newState;
        });
      }
    };

    // Start polling immediately (backend triggers task right away)
    poll();
  }, [API, showNotification, fetchUserSettings]);

  // Datasource management functions
  const handleDatasourceAdded = useCallback(async (newDatasource) => {
    // Mark this datasource as generating data notes
    setGeneratingDataNotes(prev => ({ ...prev, [newDatasource.name]: true }));

    // Open Settings tab with databases sub-tab FIRST so user sees it immediately
    setSettingsTab('databases');
    setTabs(prevTabs => {
      const existingSettingsTab = prevTabs.find(tab => tab.type === TAB_TYPES.SETTINGS);
      if (existingSettingsTab) {
        setActiveTabId(existingSettingsTab.id);
        return prevTabs;
      }
      const newTabId = `${TAB_TYPES.SETTINGS}-${Date.now()}`;
      const newTab = {
        id: newTabId,
        type: TAB_TYPES.SETTINGS,
        title: 'Settings',
        sessionId: null,
        messages: null,
        inputMessage: null,
        isLoading: null
      };
      setActiveTabId(newTabId);
      return [...prevTabs, newTab];
    });

    // Show toast - inform user about data notes generation
    showNotification('success', `Datasource "${newDatasource.name}" added! Generating data notes...`);

    // Reload settings to get updated datasources (DB is already created on backend)
    await fetchUserSettings();

    // Start polling for data notes completion
    pollDataNotesStatus(newDatasource.name);
  }, [fetchUserSettings, showNotification, pollDataNotesStatus]);

  const handleActivateDatasource = useCallback(async (datasourceName) => {
    try {
      setActivatingDatasource(datasourceName);
      showNotification('success', `Activating "${datasourceName}"...`);

      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API_ENDPOINTS.main}/datasources/${encodeURIComponent(datasourceName)}/activate`, {}, { headers });

      // OPTIMIZATION: Only fetch datasources instead of all settings
      const response = await axios.get(`${API_ENDPOINTS.main}/auth/settings`, { headers });
      setDatasources(response.data.datasources || []);
      const active = (response.data.datasources || []).find(ds => ds.is_active);
      setActiveDatasource(active);

      showNotification('success', `"${datasourceName}" activated successfully!`);
    } catch (error) {
      console.error('Failed to activate datasource:', error);
      showNotification('error', 'Failed to activate datasource');
    } finally {
      setActivatingDatasource(null);
    }
  }, [API, showNotification]);

  const handleRefreshStats = useCallback(async (datasourceName) => {
    try {
      setRefreshingStatsDatasource(datasourceName);
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(`${API_ENDPOINTS.main}/datasources/${encodeURIComponent(datasourceName)}/refresh-stats`, {}, { headers });

      if (response.data.success) {
        showNotification('success', `✓ Stats updated for ${response.data.table_count} tables`);
        setSchema(null); // Force schema reload on next view
      }
    } catch (error) {
      console.error('Failed to refresh stats:', error);
      const message = error.response?.data?.detail || 'Failed to update stats';
      showNotification('error', message);
    } finally {
      setRefreshingStatsDatasource(null);
    }
  }, [API, showNotification]);

  const handleDeleteDatasource = useCallback(async (datasourceName) => {
    if (!window.confirm('Are you sure you want to delete this datasource?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API_ENDPOINTS.main}/datasources/${encodeURIComponent(datasourceName)}`, { headers });

      // Clear taurusDatasource from all tabs that use the deleted datasource
      setTabs(prevTabs => prevTabs.map(tab => {
        if (tab.taurusDatasource?.name === datasourceName) {
          return { ...tab, taurusDatasource: null };
        }
        return tab;
      }));

      // Clear global state if it matches
      if (taurusSelectedDatasource?.name === datasourceName) {
        setTaurusSelectedDatasource(null);
        localStorage.removeItem('taurus_selected_datasource');
      }

      // Reload settings to get updated datasources
      await fetchUserSettings();

      // Show success notification
      showNotification('success', `Datasource "${datasourceName}" deleted successfully`);
    } catch (error) {
      console.error('Failed to delete datasource:', error);
      showNotification('error', 'Failed to delete datasource');
    }
  }, [API, fetchUserSettings, showNotification, taurusSelectedDatasource]);

  const handleEditDatasource = useCallback((datasource) => {
    setEditingDatasource(datasource);
    setEditDatasourceModalOpen(true);
  }, []);

  const handleDatasourceUpdated = useCallback(async () => {
    // Reload settings to get updated datasources
    await fetchUserSettings();
    setEditDatasourceModalOpen(false);
    setEditingDatasource(null);
  }, [fetchUserSettings]);

  // Taurus datasource selection management
  const handleSelectTaurusDatasource = useCallback(async (datasource) => {
    try {
      // Show activating toast
      showNotification('success', `Activating "${datasource.name}"...`);

      // Save to localStorage for Taurus
      localStorage.setItem('taurus_selected_datasource', datasource.name);
      setTaurusSelectedDatasource(datasource);

      // Store in active tab (per-tab datasource to prevent cross-tab contamination)
      setTabs(prevTabs => prevTabs.map(tab =>
        tab.id === activeTabId ? { ...tab, taurusDatasource: datasource } : tab
      ));

      // Activate the datasource globally (same as Settings activation)
      const token = localStorage.getItem('token');
      if (token) {
        const headers = { Authorization: `Bearer ${token}` };
        await axios.put(`${API_ENDPOINTS.main}/datasources/${encodeURIComponent(datasource.name)}/activate`, {}, { headers });

        // Refresh datasources and active datasource
        const response = await axios.get(`${API_ENDPOINTS.main}/auth/settings`, { headers });
        setDatasources(response.data.datasources || []);
        const active = (response.data.datasources || []).find(ds => ds.is_active);
        setActiveDatasource(active);

        // Show success toast
        showNotification('success', `"${datasource.name}" activated successfully!`);
      }

      setShowDatasourceModal(false);
      setDatasourceSearch('');
      console.log('[Taurus] Selected and activated datasource:', datasource.name);
    } catch (error) {
      console.error('Failed to activate datasource:', error);
      showNotification('error', 'Failed to activate datasource');
    }
  }, [showNotification, activeTabId]);

  const handleRemoveTaurusDatasource = useCallback((datasourceName) => {
    localStorage.removeItem('taurus_selected_datasource');
    setTaurusSelectedDatasource(null);

    // Clear from active tab as well
    setTabs(prevTabs => prevTabs.map(tab =>
      tab.id === activeTabId ? { ...tab, taurusDatasource: null } : tab
    ));

    console.log('[Taurus] Removed datasource selection');

    // Clear Taurus schema cache for the removed datasource (fire-and-forget)
    if (datasourceName) {
      fetch(`${API_ENDPOINTS.taurus}/api/clear-schema-cache?datasource_name=${encodeURIComponent(datasourceName)}`, {
        method: 'POST',
      }).then(() => {
        console.log(`[Taurus] Schema cache cleared for: ${datasourceName}`);
      }).catch(() => {
        console.log('[Taurus] Failed to clear schema cache (server may not be running)');
      });
    }
  }, [activeTabId]);

  // Ref to track previous datasource for system messages
  const prevActiveDatasourceRef = useRef(activeDatasource);
  // Keep ref in sync
  useEffect(() => {
    prevActiveDatasourceRef.current = activeDatasource;
  }, [activeDatasource]);

  // Keep datasource refs in sync with state (used by sendTaurusMessage to avoid stale closures)
  useEffect(() => {
    taurusSelectedDatasourceRef.current = taurusSelectedDatasource;
  }, [taurusSelectedDatasource]);

  useEffect(() => {
    activeDatasourceRef.current = activeDatasource;
  }, [activeDatasource]);

  const handleSelectVirgoDatasource = useCallback(async (datasource, closeModal = true) => {
    try {
      // Show activating toast
      showNotification('success', `Activating "${datasource.name}"...`);

      // Activate the datasource globally
      const token = localStorage.getItem('token');
      if (token) {
        const headers = { Authorization: `Bearer ${token}` };
        await axios.put(`${API_ENDPOINTS.main}/datasources/${encodeURIComponent(datasource.name)}/activate`, {}, { headers });

        // Refresh datasources and active datasource
        const response = await axios.get(`${API_ENDPOINTS.main}/auth/settings`, { headers });
        setDatasources(response.data.datasources || []);
        const active = (response.data.datasources || []).find(ds => ds.is_active);

        // Add system message if switching datasources mid-conversation
        const prevActive = prevActiveDatasourceRef.current;
        const isSwitching = prevActive && prevActive.name !== datasource.name;
        if (isSwitching) {
          // Format: "Switched database: old_db (PostgreSQL) → new_db (MySQL)"
          const formatDs = (ds) => {
            const dbType = ds.db_type ? ` (${ds.db_type})` : '';
            return `${ds.name}${dbType}`;
          };
          setTabs(currentTabs => currentTabs.map(tab => {
            if (tab.id === activeTabId && tab.mode === 'virgo' && tab.messages && tab.messages.length > 0) {
              return {
                ...tab,
                messages: [...tab.messages, {
                  id: uuidv4(),
                  type: 'system',
                  content: `Switched database: ${formatDs(prevActive)} → ${formatDs(datasource)}`,
                  timestamp: new Date()
                }]
              };
            }
            return tab;
          }));
        }

        // Update state
        setActiveDatasource(active);

        // Show success toast
        showNotification('success', `"${datasource.name}" activated successfully!`);
      }

      // Only close modal if called from DatasourceSelectionModal (not from DataSourcesPanel)
      if (closeModal) {
        setShowDatasourceModal(false);
        setDatasourceSearch('');
      }
      console.log('[Virgo] Selected and activated datasource:', datasource.name);
    } catch (error) {
      console.error('Failed to activate datasource:', error);
      showNotification('error', 'Failed to activate datasource');
      throw error; // Re-throw so DataSourcesPanel can handle it
    }
  }, [showNotification, activeTabId]);

  // Handler for file selection changes - adds system message when changing files mid-conversation
  // Uses ref to track previous files and avoid stale closures
  const prevSelectedFilesRef = useRef(selectedFiles);

  // Helper to format file names for system messages
  const formatFileNames = (files, maxShow = 3, maxNameLength = 30) => {
    if (files.length === 0) return '';
    const truncateName = (name) => name.length > maxNameLength ? name.slice(0, maxNameLength - 3) + '...' : name;
    const names = files.map(f => truncateName(f.original_filename || f.filename || f.name || 'file'));
    if (names.length <= maxShow) {
      return names.join(', ');
    }
    return `${names.slice(0, maxShow).join(', ')} + ${names.length - maxShow} more`;
  };

  const handleSelectFiles = useCallback((newFiles) => {
    const prevFiles = prevSelectedFilesRef.current;
    const prevIds = new Set(prevFiles.map(f => f.id));
    const newIds = new Set(newFiles.map(f => f.id));

    // Determine added and removed files
    const addedFiles = newFiles.filter(f => !prevIds.has(f.id));
    const removedFiles = prevFiles.filter(f => !newIds.has(f.id));

    // Update ref for next comparison
    prevSelectedFilesRef.current = newFiles;

    // Build change message
    let changeMessage = null;
    if (addedFiles.length > 0 && removedFiles.length === 0) {
      changeMessage = `Added to context: ${formatFileNames(addedFiles)}`;
    } else if (removedFiles.length > 0 && addedFiles.length === 0) {
      if (newFiles.length === 0) {
        changeMessage = 'Removed all files from context';
      } else {
        changeMessage = `Removed from context: ${formatFileNames(removedFiles)}`;
      }
    } else if (addedFiles.length > 0 && removedFiles.length > 0) {
      changeMessage = `Files changed: +${addedFiles.length}, -${removedFiles.length}`;
    }

    // Add system message if something changed
    if (changeMessage) {
      setTabs(currentTabs => currentTabs.map(tab => {
        if (tab.id === activeTabId && tab.mode === 'virgo' && tab.messages && tab.messages.length > 0) {
          return {
            ...tab,
            messages: [...tab.messages, {
              id: uuidv4(),
              type: 'system',
              content: changeMessage,
              timestamp: new Date()
            }]
          };
        }
        return tab;
      }));
    }

    // Update the actual state
    setSelectedFiles(newFiles);
  }, [activeTabId]);

  // Virgo Projects — activate a project (sets files, datasource, and context)
  const handleActivateProject = useCallback(async (project, resolvedFiles) => {
    const projectToStore = { ...project, resolvedFiles };
    localStorage.setItem('vg_active_project', JSON.stringify(projectToStore));
    setActiveProject(projectToStore);
    // Populate selected files with the project's files
    handleSelectFiles(resolvedFiles);
    // Project takes full precedence over the current DB state
    if (project.datasource_name) {
      const ds = datasources.find(d => d.name === project.datasource_name);
      if (ds) {
        try { await handleSelectVirgoDatasource(ds, false); } catch { /* datasource not found or error */ }
      }
      setIncludeDatabase(true);
      localStorage.setItem('vg_include_db', 'true');
    } else {
      // No DB in this project — exclude whatever was globally active
      setIncludeDatabase(false);
      localStorage.setItem('vg_include_db', 'false');
    }
    if (showNotification) showNotification('success', `Project "${project.name}" activated`);
  }, [handleSelectFiles, handleSelectVirgoDatasource, datasources, showNotification]);

  // Virgo Projects — exit project mode (keep selected files as-is)
  const handleDeactivateProject = useCallback(() => {
    localStorage.removeItem('vg_active_project');
    setActiveProject(null);
  }, []);

  // Taurus image attachment handlers
  const processTaurusImageFile = useCallback(async (file) => {
    // Validate file type
    if (!TAURUS_IMAGE_SUPPORTED_TYPES.includes(file.type)) {
      alert(`Unsupported image type: ${file.type}. Supported: PNG, JPEG, GIF, WebP`);
      return null;
    }
    // Validate file size
    if (file.size > TAURUS_IMAGE_MAX_SIZE) {
      alert(`Image too large: ${(file.size / 1024 / 1024).toFixed(1)}MB. Max: 5MB`);
      return null;
    }
    // Convert to base64
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Data = e.target.result.split(',')[1]; // Remove data:image/...;base64, prefix
        resolve({
          id: uuidv4(),
          data: base64Data,
          media_type: file.type,
          preview: URL.createObjectURL(file),
          name: file.name
        });
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }, []); // Constants are defined outside component

  const handleTaurusImagePaste = useCallback(async (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    const imageFiles = [];
    for (const item of items) {
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        if (file) imageFiles.push(file);
      }
    }

    if (imageFiles.length === 0) return;

    // Check max count
    const remainingSlots = TAURUS_IMAGE_MAX_COUNT - taurusAttachedImages.length;
    if (remainingSlots <= 0) {
      alert(`Maximum ${TAURUS_IMAGE_MAX_COUNT} images allowed`);
      return;
    }

    // Process images (up to remaining slots)
    const filesToProcess = imageFiles.slice(0, remainingSlots);
    const processed = await Promise.all(filesToProcess.map(processTaurusImageFile));
    const valid = processed.filter(Boolean);

    if (valid.length > 0) {
      setTaurusAttachedImages(prev => [...prev, ...valid]);
      e.preventDefault(); // Prevent default paste behavior for images
    }
  }, [taurusAttachedImages.length, processTaurusImageFile]);

  const handleTaurusImageDrop = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = Array.from(e.dataTransfer?.files || []);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));

    if (imageFiles.length === 0) return;

    // Check max count
    const remainingSlots = TAURUS_IMAGE_MAX_COUNT - taurusAttachedImages.length;
    if (remainingSlots <= 0) {
      alert(`Maximum ${TAURUS_IMAGE_MAX_COUNT} images allowed`);
      return;
    }

    // Process images (up to remaining slots)
    const filesToProcess = imageFiles.slice(0, remainingSlots);
    const processed = await Promise.all(filesToProcess.map(processTaurusImageFile));
    const valid = processed.filter(Boolean);

    if (valid.length > 0) {
      setTaurusAttachedImages(prev => [...prev, ...valid]);
    }
  }, [taurusAttachedImages.length, processTaurusImageFile]);

  const handleTaurusImageRemove = useCallback((imageId) => {
    setTaurusAttachedImages(prev => {
      const toRemove = prev.find(img => img.id === imageId);
      if (toRemove?.preview) {
        URL.revokeObjectURL(toRemove.preview); // Clean up blob URL
      }
      return prev.filter(img => img.id !== imageId);
    });
  }, []);

  const clearTaurusImages = useCallback(() => {
    setTaurusAttachedImages(currentImages => {
      currentImages.forEach(img => {
        if (img.preview) URL.revokeObjectURL(img.preview);
      });
      return [];
    });
  }, []);

  // Handler for database inclusion toggle - adds system message when toggling mid-conversation
  const handleIncludeDatabaseChange = useCallback((newValue) => {
    const currentDs = prevActiveDatasourceRef.current;
    const dsName = currentDs ? `${currentDs.name}${currentDs.db_type ? ` (${currentDs.db_type})` : ''}` : 'database';

    // Only add system message if there's an existing conversation in Virgo mode
    setTabs(currentTabs => currentTabs.map(tab => {
      if (tab.id === activeTabId && tab.mode === 'virgo' && tab.messages && tab.messages.length > 0) {
        const message = newValue
          ? `Added to context: ${dsName}`
          : `Removed from context: ${dsName}`;
        return {
          ...tab,
          messages: [...tab.messages, {
            id: uuidv4(),
            type: 'system',
            content: message,
            timestamp: new Date()
          }]
        };
      }
      return tab;
    }));

    localStorage.setItem('vg_include_db', String(newValue));
    setIncludeDatabase(newValue);
  }, [activeTabId]);

  // Project management functions (localStorage-based for now)
  const handleNewProject = useCallback(() => {
    setProjectEditorMode({
      id: null,
      name: 'New Project',
      json: EMPTY_PROJECT_TEMPLATE,
      isNew: true
    });
    setShowProjectsPanel(false);
  }, []);

  const handleEditProject = useCallback((project) => {
    setProjectEditorMode(project);
    setShowProjectsPanel(false);
  }, []);

  const handleSaveProject = useCallback(async (project) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('error', 'Please log in to save projects');
        return;
      }

      const payload = {
        name: project.name,
        description: project.description || project.json?.description || '',
        json_content: project.json,
        level: project.level || 'private'
      };

      if (project.id && !project.isNew) {
        // Update existing
        await axios.put(`${API_ENDPOINTS.main}/taurus-projects/${project.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Update local state
        setSavedProjects(prev => prev.map(p => p.id === project.id ? project : p));
      } else {
        // Create new
        const response = await axios.post(`${API_ENDPOINTS.main}/taurus-projects/create`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const newId = response.data.project_id;
        const newProject = { ...project, id: newId, isNew: false };
        // Update local state
        setSavedProjects(prev => [...prev, newProject]);
        // Update editor mode with the new ID
        setProjectEditorMode(newProject);
      }
      // Note: ProjectEditor shows its own toast (bottom-right) to avoid obscuring buttons
    } catch (err) {
      console.error('Error saving project:', err);
      // Re-throw so ProjectEditor can show its own error toast
      throw err;
    }
  }, [showNotification]);

  const handleDeleteProject = useCallback(async (projectId) => {
    if (!window.confirm('Delete this project?')) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('error', 'Please log in to delete projects');
        return;
      }

      await axios.delete(`${API_ENDPOINTS.main}/taurus-projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSavedProjects(prev => prev.filter(p => p.id !== projectId));
      // If deleting the currently edited project, go back
      if (projectEditorMode?.id === projectId) {
        setProjectEditorMode(null);
      }
      showNotification('success', 'Project deleted');
    } catch (err) {
      console.error('Error deleting project:', err);
      showNotification('error', err.response?.data?.detail || 'Failed to delete project');
    }
  }, [projectEditorMode, showNotification]);

  // Helper to determine if a step should be skipped
  const shouldSkipStep = useCallback((step, index, json) => {
    // Explicit skip on step
    if (step.skip === true) return true;

    // Global start_from (1-indexed)
    if (json.start_from && json.start_from > 1 && index < json.start_from - 1) return true;

    // Global start_from_id
    if (json.start_from_id) {
      const startIndex = json.steps.findIndex(s => s.id === json.start_from_id);
      if (startIndex > 0 && index < startIndex) return true;
    }

    return false;
  }, []);

  // Helper to find next non-skipped step index
  const findNextExecutableStep = useCallback((steps, fromIndex, json) => {
    for (let i = fromIndex; i < steps.length; i++) {
      if (!shouldSkipStep(steps[i], i, json)) {
        return i;
      }
    }
    return -1; // No more executable steps
  }, [shouldSkipStep]);

  const handleRunProject = useCallback((project) => {
    const json = project.json;
    const steps = json?.steps || [];
    const projectName = json?.project_name || project.name || 'Unnamed Project';
    const defaults = json?.defaults || {};

    if (steps.length === 0) {
      showNotification('error', 'Project has no steps to execute');
      return;
    }

    // Find first non-skipped step
    const firstExecutableIndex = findNextExecutableStep(steps, 0, json);
    if (firstExecutableIndex === -1) {
      showNotification('error', 'All steps are marked to skip');
      return;
    }

    // Close editor/panel
    setRunningProject(project);
    setProjectEditorMode(null);
    setShowProjectsPanel(false);

    // Increment run count via API (fire and forget)
    if (project.id && !project.isSample) {
      const token = localStorage.getItem('token');
      if (token) {
        axios.post(`${API_ENDPOINTS.main}/taurus-projects/${project.id}/run`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(err => console.error('Error recording project run:', err));
      }
    }

    // Ensure we're on a Taurus tab
    const currentTab = tabs.find(t => t.id === activeTabId);
    if (!currentTab || currentTab.mode !== 'taurus') {
      showNotification('error', 'Please switch to Taurus mode to run projects');
      setRunningProject(null);
      return;
    }

    // Initialize project execution state
    setProjectExecution({
      project,
      projectName,
      steps,
      defaults,
      json, // Store json for skip logic
      currentStepIndex: firstExecutableIndex,
      status: 'running',
      tabId: activeTabId
    });

    // Execute first executable step (with optional delay)
    const firstStep = steps[firstExecutableIndex];
    const firstStepDelay = firstStep.delay_ms ?? defaults.delay_ms ?? 0;

    const executeFirstStep = () => {
      const stepPrompt = buildStepPrompt(firstStep, firstExecutableIndex, steps.length, projectName, defaults, true);
      const contextMode = firstStep.context_mode || defaults.context_mode || 'continue';
      sendTaurusMessage(stepPrompt, activeTabId, { contextMode });
    };

    const skippedCount = firstExecutableIndex;
    const skippedMsg = skippedCount > 0 ? ` (skipped ${skippedCount})` : '';

    if (firstStepDelay > 0) {
      showNotification('info', `Starting project: ${projectName} (Step ${firstExecutableIndex + 1}/${steps.length}${skippedMsg} in ${firstStepDelay}ms)`);
      setTimeout(executeFirstStep, firstStepDelay);
    } else {
      showNotification('info', `Starting project: ${projectName} (Step ${firstExecutableIndex + 1}/${steps.length}${skippedMsg})`);
      executeFirstStep();
    }
  }, [showNotification, tabs, activeTabId, findNextExecutableStep]);

  // Helper to build a prompt for a single step
  const buildStepPrompt = (step, index, totalSteps, projectName, defaults = {}, isFirstExecuted = false) => {
    let prompt = `## 📋 Project: ${projectName} — Step ${index + 1}/${totalSteps}\n`;
    prompt += `### ${step.name || `Step ${index + 1}`}\n\n`;
    prompt += `${step.prompt}\n`;

    if (step.reference_files?.length > 0) {
      prompt += `\n**Reference files:** ${step.reference_files.join(', ')}\n`;
    }
    if (step.success_criteria) {
      prompt += `\n**Success criteria:** ${step.success_criteria}\n`;
    }

    // Note: review_notes are NOT added to prompt - they're only used for deep review after step completes

    // Add context hint if this isn't the first executed step and context continues
    const contextMode = step.context_mode || defaults.context_mode || 'continue';
    if (!isFirstExecuted && contextMode === 'continue') {
      prompt += `\n*Note: Variables from previous steps are available if needed.*\n`;
    }

    return prompt;
  };

  // Continue to next step (called after current step completes)
  const continueProjectExecution = useCallback(() => {
    if (!projectExecution || projectExecution.status !== 'paused') return;

    const { steps, currentStepIndex, projectName, tabId, defaults, json } = projectExecution;

    // Find next non-skipped step
    const nextIndex = findNextExecutableStep(steps, currentStepIndex + 1, json);

    if (nextIndex === -1) {
      // No more executable steps - project complete
      setProjectExecution(prev => ({ ...prev, status: 'completed' }));
      setRunningProject(null);
      showNotification('success', `Project completed: ${projectName}`);
      return;
    }

    // Execute next step (with optional delay for rate limiting)
    const nextStep = steps[nextIndex];
    const delayMs = nextStep.delay_ms ?? defaults?.delay_ms ?? 0;

    const executeNextStep = () => {
      setProjectExecution(prev => ({
        ...prev,
        currentStepIndex: nextIndex,
        status: 'running'
      }));

      const stepPrompt = buildStepPrompt(nextStep, nextIndex, steps.length, projectName, defaults);
      const contextMode = nextStep.context_mode || defaults?.context_mode || 'continue';
      sendTaurusMessage(stepPrompt, tabId, { contextMode });
    };

    if (delayMs > 0) {
      showNotification('info', `Continuing: Step ${nextIndex + 1}/${steps.length} in ${delayMs}ms`);
      setTimeout(executeNextStep, delayMs);
    } else {
      showNotification('info', `Continuing: Step ${nextIndex + 1}/${steps.length}`);
      executeNextStep();
    }
  }, [projectExecution, showNotification, findNextExecutableStep]);

  // Skip remaining steps and end project
  const cancelProjectExecution = useCallback(() => {
    if (!projectExecution) return;
    setProjectExecution(null);
    setRunningProject(null);
    showNotification('info', 'Project execution cancelled');
  }, [projectExecution, showNotification]);

  // Track previous loading state for step completion detection
  const prevTabLoadingRef = useRef(null);

  // Watch for step completion and handle transition to next step
  useEffect(() => {
    if (!projectExecution || projectExecution.status !== 'running') return;

    const { tabId, steps, currentStepIndex, defaults } = projectExecution;
    const currentTab = tabs.find(t => t.id === tabId);
    if (!currentTab) return;

    const wasLoading = prevTabLoadingRef.current;
    const isLoading = currentTab.isLoading;

    // Update ref for next comparison
    prevTabLoadingRef.current = isLoading;

    // Detect step completion: was loading, now not loading
    if (wasLoading === true && isLoading === false) {
      const currentStep = steps[currentStepIndex];
      const reviewMode = currentStep.review_mode || defaults.review_mode || 'user';
      const { json } = projectExecution;

      console.log(`[Project] Step ${currentStepIndex + 1} completed. Review mode: ${reviewMode}`);

      // Find next executable step
      const nextIndex = findNextExecutableStep(steps, currentStepIndex + 1, json);
      const isLastStep = nextIndex === -1;

      // Notify about skipped steps (between current and next, or all remaining if last)
      const skippedSteps = [];
      for (let i = currentStepIndex + 1; i < steps.length; i++) {
        if (shouldSkipStep(steps[i], i, json)) {
          skippedSteps.push(steps[i].name || `Step ${i + 1}`);
        } else if (!isLastStep && i >= nextIndex) {
          break; // Stop at next executable step
        }
      }
      if (skippedSteps.length > 0) {
        showNotification('info', `Skipping: ${skippedSteps.join(', ')}`);
      }

      // Handle auto-review first (applies to both last step and intermediate steps)
      if (reviewMode === 'auto') {
        const reviewNotes = currentStep.review_notes || defaults?.review_notes;
        const lastBotMessage = [...currentTab.messages].reverse().find(m => m.type === 'bot' && m.isTaurus);

        if (lastBotMessage && lastBotMessage.investigationThread) {
          console.log(`[Project] Auto-review starting for step ${currentStepIndex + 1} with notes: ${reviewNotes || '(none)'}`);

          // Set pending continuation - will be triggered when deep review completes
          // nextIndex of -1 means this is the last step
          setPendingAutoReviewContinue({ messageId: lastBotMessage.id, nextIndex, tabId, isLastStep });

          // Trigger deep review (will auto-continue or complete when done)
          callDeepReview(lastBotMessage, tabId, reviewNotes);
          showNotification('info', `Auto-reviewing step ${currentStepIndex + 1}...`);
          return; // Exit early - continuation handled by useEffect
        } else {
          console.log(`[Project] Step ${currentStepIndex + 1} has no investigation thread, skipping auto-review`);
          // Fall through to handle completion/continuation below
        }
      }

      // Handle step completion/continuation (after review or if no review needed)
      if (isLastStep) {
        // No more executable steps - project complete
        setProjectExecution(prev => ({ ...prev, status: 'completed' }));
        setRunningProject(null);
        showNotification('success', `Project completed! (${steps.length} steps)`);
      } else if (reviewMode === 'none' || reviewMode === 'auto') {
        // Auto-continue (auto falls through here if no investigation thread to review)
        const nextStep = steps[nextIndex];
        const delayMs = nextStep.delay_ms ?? defaults?.delay_ms ?? 1000;

        setTimeout(() => {
          setProjectExecution(prev => ({
            ...prev,
            currentStepIndex: nextIndex,
            status: 'running'
          }));

          const stepPrompt = buildStepPrompt(nextStep, nextIndex, steps.length, projectExecution.projectName, defaults);
          const contextMode = nextStep.context_mode || defaults?.context_mode || 'continue';
          sendTaurusMessage(stepPrompt, tabId, { contextMode });
          showNotification('info', `Auto-continuing: Step ${nextIndex + 1}/${steps.length}`);
        }, delayMs);
      } else {
        // review_mode === 'user' - pause for approval
        setProjectExecution(prev => ({ ...prev, status: 'paused' }));
        showNotification('info', `Step ${currentStepIndex + 1} complete. Click "Continue" to proceed.`);
      }
    }
  }, [projectExecution, tabs, showNotification, findNextExecutableStep]);

  // Track previous review loading state for detecting completion
  const prevAutoReviewLoadingRef = useRef(null);

  // Watch for deep review completion to auto-continue project execution
  useEffect(() => {
    if (!pendingAutoReviewContinue || !projectExecution) {
      prevAutoReviewLoadingRef.current = null;
      return;
    }

    const { messageId, nextIndex, tabId, isLastStep } = pendingAutoReviewContinue;
    const isReviewLoading = deepReviewLoading[messageId];
    const wasLoading = prevAutoReviewLoadingRef.current;

    // Update ref for next comparison
    prevAutoReviewLoadingRef.current = isReviewLoading;

    // Detect review completion: was loading (true), now not loading (false)
    if (wasLoading === true && isReviewLoading === false) {
      console.log(`[Project] Auto-review loading transition detected: ${wasLoading} -> ${isReviewLoading}`);

      // Clear pending state
      setPendingAutoReviewContinue(null);

      if (isLastStep) {
        // This was the last step - mark project complete
        console.log(`[Project] Auto-review completed for final step, project complete`);
        setProjectExecution(prev => ({ ...prev, status: 'completed' }));
        setRunningProject(null);
        showNotification('success', `Project completed! (${projectExecution.steps.length} steps)`);
      } else {
        // Continue to next step
        console.log(`[Project] Auto-review completed, continuing to step ${nextIndex + 1}`);

        const { steps, defaults } = projectExecution;
        const nextStep = steps[nextIndex];
        const delayMs = nextStep.delay_ms ?? defaults?.delay_ms ?? 1000;

        setTimeout(() => {
          setProjectExecution(prev => ({
            ...prev,
            currentStepIndex: nextIndex,
            status: 'running'
          }));

          const stepPrompt = buildStepPrompt(
            nextStep,
            nextIndex,
            steps.length,
            projectExecution.projectName,
            defaults
          );
          const contextMode = nextStep.context_mode || defaults?.context_mode || 'continue';
          sendTaurusMessage(stepPrompt, tabId, { contextMode });
          showNotification('info', `Auto-continuing: Step ${nextIndex + 1}/${steps.length}`);
        }, delayMs);
      }
    }
  }, [pendingAutoReviewContinue, deepReviewLoading, projectExecution, showNotification]);

  const handleBackFromEditor = useCallback(() => {
    setProjectEditorMode(null);
    setShowProjectsPanel(true);
  }, []);

  // Footnote (snippet) management functions
  const loadFootnotes = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token || !isAuthenticated) return;

      const response = await axios.get(`${API_ENDPOINTS.main}/footnotes/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200) {
        setSnippets(response.data.footnotes);
      }
    } catch (err) {
      console.error('Error loading footnotes:', err);
    }
  }, [API, isAuthenticated]);

  // Load Taurus projects from API
  const fetchProjects = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      setProjectsLoading(true);
      const response = await axios.get(`${API_ENDPOINTS.main}/taurus-projects/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200) {
        // Normalize JSON key order for consistent display
        const projects = (response.data.projects || []).map(p => ({
          ...p,
          json: normalizeProjectJson(p.json)
        }));
        setSavedProjects(projects);
      }
    } catch (err) {
      console.error('Error loading projects:', err);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  const handleCreateFootnote = useCallback(() => {
    // Create with default mode matching current chat mode
    setEditingFootnote({ mode: chatMode });
    setShowEditFootnoteModal(true);
    trackFeatureUse('footnote_create');
  }, [chatMode]);

  const handleEditFootnote = useCallback((footnote) => {
    setEditingFootnote(footnote);
    setShowEditFootnoteModal(true);
    trackFeatureUse('footnote_edit');
  }, []);

  const handleDeleteFootnote = useCallback(async (footnoteId) => {
    if (!window.confirm('Are you sure you want to delete this footnote?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_ENDPOINTS.main}/footnotes/${footnoteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.status === 200) {
        await loadFootnotes();
      } else {
        alert('Failed to delete footnote');
      }
    } catch (err) {
      console.error('Error deleting footnote:', err);
      alert('Failed to delete footnote');
    }
  }, [API, loadFootnotes]);

  const handleUseFootnote = useCallback(async (footnoteId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_ENDPOINTS.main}/footnotes/${footnoteId}/use`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Reload footnotes to update the sort order
      await loadFootnotes();
    } catch (err) {
      console.error('Error tracking footnote usage:', err);
      // Silently fail - don't interrupt user workflow
    }
  }, [API, loadFootnotes]);

  const handleSaveFootnote = useCallback(async (footnoteData) => {
    try {
      const token = localStorage.getItem('token');

      if (footnoteData.id) {
        // Update existing footnote
        const response = await axios.put(
          `${API_ENDPOINTS.main}/footnotes/${footnoteData.id}`,
          {
            title: footnoteData.title,
            content: footnoteData.content,
            tags: footnoteData.tags,
            mode: footnoteData.mode,
            level: footnoteData.level
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.status !== 200) {
          throw new Error('Failed to update footnote');
        }
      } else {
        // Create new footnote
        const response = await axios.post(
          `${API_ENDPOINTS.main}/footnotes/create`,
          {
            title: footnoteData.title,
            content: footnoteData.content,
            tags: footnoteData.tags,
            mode: footnoteData.mode,
            level: footnoteData.level
          },
          {
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.status !== 200) {
          throw new Error('Failed to create footnote');
        }
      }

      await loadFootnotes();
      setShowEditFootnoteModal(false);
    } catch (err) {
      console.error('Error saving footnote:', err);
      throw err;
    }
  }, [API, loadFootnotes]);

  const normalizeMobileViewportForChatSend = () => {
    const isMobileViewport = window.matchMedia('(max-width: 768px), (pointer: coarse)').matches;
    if (!isMobileViewport) return;

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && (activeElement.tagName === 'TEXTAREA' || activeElement.tagName === 'INPUT')) {
      activeElement.blur();
    }

    const syncViewport = () => {
      const viewportHeight = window.visualViewport?.height
        ? Math.round(window.visualViewport.height)
        : window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${viewportHeight}px`);
      window.scrollTo(0, 0);
    };

    requestAnimationFrame(() => {
      syncViewport();
      setTimeout(syncViewport, 250);
    });
  };

  const submitChatText = (text, mode, tabId) => {
    normalizeMobileViewportForChatSend();

    if (mode === 'virgo') return sendVirgoMessage(text, tabId);
    if (mode === 'taurus') return sendTaurusMessage(text, tabId);
    return sendMessage(text, tabId);
  };

  const handleSnippetModalSubmit = (text) => {
    return submitChatText(text, chatMode, activeTabId);
  };

  const handleOpenSnippetModal = useCallback(() => {
    setShowSnippetModal(true);
    trackFeatureUse('snippet_modal_opened');
  }, []);

  const formatRelativeTime = useCallback((date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString();
  }, []);

  const handleSelectChat = useCallback(async (session) => {
    // Fetch the full conversation from the new saved-conversations endpoint
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('Cannot load conversation: not authenticated');
      return;
    }

    // Set loading state for this specific conversation
    setLoadingConversationId(session.conversationId);

    try {
      // Use conversationId (backend ID) to fetch full conversation
      const response = await axios.get(`${API_ENDPOINTS.main}/saved-conversations/${session.conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const conversation = response.data;
      const loadedMessages = conversation.messages || [];

      // Create a new chat tab with the loaded messages
      const newTabId = uuidv4();

      // Transform saved messages back to UI format
      const uiMessages = loadedMessages.map(msg => {
        // Handle charts - convert back to plots format
        let plots = [];
        if (msg.charts && msg.charts.length > 0) {
          plots = msg.charts.map(chart => {
            if (chart.type === 'matplotlib' && chart.data) {
              // Convert base64 to blob URL for display
              return {
                url: base64ToBlobUrl(chart.data),
                data: chart.data
              };
            }
            return chart;
          }).filter(p => p);
        }

        // Re-add plots if they existed
        const result = { ...msg };
        if (plots.length > 0) {
          result.plots = plots;
        }
        // Remove the charts field (we converted to plots)
        delete result.charts;

        return result;
      });

      const newTab = {
        id: newTabId,
        type: TAB_TYPES.CHAT,
        title: conversation.title || session.title,
        sessionId: conversation.session_id,
        savedConversationId: conversation.id,  // Track the backend ID
        inputMessage: '',
        mode: conversation.mode || 'virgo',
        messages: uiMessages,
        isLoading: false,
        isSaved: true,  // This is a loaded conversation, so it's "saved"
        taurusDatasource: null  // Per-tab Taurus datasource (user must reselect for loaded conversations)
      };

      // Add the new tab and switch to it
      setTabs(prev => [...prev, newTab]);
      setActiveTabId(newTab.id);

    } catch (error) {
      console.error('Failed to load saved conversation:', error);
      // Could show a toast notification here
    } finally {
      setLoadingConversationId(null);
    }
  }, [setTabs, setActiveTabId, API_ENDPOINTS.main]);

  // Feedback and copy functionality
  const handleFeedback = useCallback(async (dbMessageId, feedbackType, uiMessageId) => {
    // Update UI state immediately using the UI message ID
    setFeedback(prev => ({
      ...prev,
      [uiMessageId]: feedbackType
    }));

    // Send feedback to backend using database message ID
    try {
      const feedbackValue = feedbackType === 'positive' ? 'up' : 'down';
      await axios.post(`${API_ENDPOINTS.main}/feedback`, {
        message_id: dbMessageId,
        feedback: feedbackValue
      });
      console.log(`Feedback sent to backend: ${dbMessageId} -> ${feedbackValue}`);
    } catch (error) {
      console.error('Failed to send feedback:', error);
      // Keep the UI feedback even if backend call fails
    }
  }, [API]);

  const copyToClipboard = useCallback(async (text, messageId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedMessageId(messageId);
      setTimeout(() => setCopiedMessageId(null), 2000); // Clear after 2 seconds
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }, []);

  // Download analysis report as markdown file (works for both Virgo and Taurus)
  const downloadAnalysisReport = useCallback((message, tabId) => {
    try {
      // Find the user's question (previous message in the array)
      const tab = tabs.find(t => t.id === tabId);
      if (!tab) return;

      const messageIndex = tab.messages.findIndex(m => m.id === message.id);
      const userQuestion = messageIndex > 0 && tab.messages[messageIndex - 1].type === 'user'
        ? tab.messages[messageIndex - 1].content
        : 'N/A';

      // Get review content if available
      const reviewSummary = reviewContent[tabId] || '';

      // Determine mode
      const mode = message.isTaurus ? 'Taurus' : 'Virgo';
      const modeSlug = message.isTaurus ? 'taurus' : 'virgo';

      // Format timestamp
      const timestamp = new Date(message.timestamp).toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short'
      });

      // Get active datasource info
      const datasourceInfo = activeDatasource
        ? `**Datasource:** ${activeDatasource.name} (${formatDbType(activeDatasource.db_type)})`
        : '**Datasource:** Not specified';

      // Build markdown content
      let markdown = `# VerbaGPT Analysis Report\n\n`;
      markdown += `**Generated:** ${timestamp}\n\n`;
      markdown += `${datasourceInfo}\n\n`;
      markdown += `**Mode:** ${mode}\n\n`;
      markdown += `---\n\n`;
      markdown += `## User Question\n\n${userQuestion}\n\n`;
      markdown += `---\n\n`;

      // Investigation thread (different field names for Taurus vs Virgo)
      const investigationContent = message.investigationThread || message.execution_output || message.consoleOutput || '';
      if (investigationContent) {
        markdown += `## Investigation Thread\n\n`;
        markdown += `\`\`\`\n${investigationContent}\n\`\`\`\n\n`;
        markdown += `---\n\n`;
      }

      // Add plot information if available
      if (message.plots && message.plots.length > 0) {
        markdown += `## Visualizations\n\n`;
        markdown += `${message.plots.length} visualization(s) were generated during this analysis.\n\n`;
        markdown += `_Note: Visualizations are not embedded in this report. Please refer to the original session for visual outputs._\n\n`;
        markdown += `---\n\n`;
      }

      // Add review/summary if available
      if (reviewSummary) {
        markdown += `## Summary (AI Review)\n\n`;
        markdown += `${reviewSummary}\n\n`;
        markdown += `---\n\n`;
      }

      markdown += `_This report was generated by VerbaGPT ${mode} mode. For verification or further analysis, please consult with your data analytics team._\n`;

      // Create blob and download
      const blob = new Blob([markdown], { type: 'text/markdown' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      // Generate filename with timestamp
      const filename = `verbagpt-${modeSlug}-report-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.md`;
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      console.log('Analysis report downloaded:', filename);
    } catch (err) {
      console.error('Failed to download analysis report:', err);
    }
  }, [tabs, reviewContent, activeDatasource]);

  const copyCodeToClipboard = useCallback(async (code, messageId) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopiedCodeId(messageId);
      setTimeout(() => setCopiedCodeId(null), 2000); // Clear after 2 seconds
    } catch (err) {
      console.error('Failed to copy code: ', err);
    }
  }, []);

  // Schema functionality
  const fetchTablePreview = useCallback(async (tableName) => {
    setLoadingTablePreview(true);
    try {
      const response = await axios.get(`${API_ENDPOINTS.main}/table-preview/${tableName}`);
      setTablePreviewData(response.data);
      setTablePreviewModalOpen(true);
    } catch (error) {
      console.error('Error fetching table preview:', error);
    } finally {
      setLoadingTablePreview(false);
    }
  }, [API]);

  // Fuzzy search scoring function
  const calculateSearchScore = useCallback((searchTerm, column) => {
    if (!searchTerm) return { score: 1, matches: true };

    const term = searchTerm.toLowerCase();
    const name = column.name.toLowerCase();
    const note = (column.note || '').toLowerCase();
    const value = (column.value || '').toLowerCase();

    let score = 0;
    let matches = false;

    // Exact match in column name (highest priority)
    if (name === term) {
      score += 100;
      matches = true;
    }
    // Column name starts with search term
    else if (name.startsWith(term)) {
      score += 80;
      matches = true;
    }
    // Column name contains search term
    else if (name.includes(term)) {
      score += 60;
      matches = true;
    }
    // Fuzzy matching for column name (word boundaries)
    else {
      const nameWords = name.split(/[\s_-]+/);
      const termWords = term.split(/[\s_-]+/);

      for (const termWord of termWords) {
        for (const nameWord of nameWords) {
          if (nameWord.startsWith(termWord) && termWord.length >= 2) {
            score += 40;
            matches = true;
          } else if (nameWord.includes(termWord) && termWord.length >= 3) {
            score += 20;
            matches = true;
          }
        }
      }
    }

    // Search in descriptions (lower priority than column names)
    if (note.includes(term)) {
      score += 30;
      matches = true;
    }

    // Fuzzy matching for descriptions
    if (note && !note.includes(term)) {
      const noteWords = note.split(/\s+/);
      const termWords = term.split(/[\s_-]+/);

      for (const termWord of termWords) {
        for (const noteWord of noteWords) {
          if (noteWord.startsWith(termWord) && termWord.length >= 3) {
            score += 15;
            matches = true;
          } else if (noteWord.includes(termWord) && termWord.length >= 4) {
            score += 10;
            matches = true;
          }
        }
      }
    }

    // Search in values (lowest priority)
    if (value.includes(term)) {
      score += 5;
      matches = true;
    }

    return { score, matches };
  }, []);

  const filteredColumns = useCallback((table) => {
    // Safety check: return empty array if schema or table is not available
    if (!schema || !schema[table] || !schema[table].columns) return [];

    if (!schemaSearch.trim()) return schema[table].columns;

    const searchTerm = schemaSearch.trim();
    const columnsWithScores = schema[table].columns.map(col => {
      const { score, matches } = calculateSearchScore(searchTerm, col);
      return { ...col, searchScore: score, searchMatches: matches };
    });

    // Filter out non-matching columns and sort by score (descending)
    return columnsWithScores
      .filter(col => col.searchMatches)
      .sort((a, b) => b.searchScore - a.searchScore);
  }, [schema, schemaSearch, calculateSearchScore]);

  // Scroll to bottom helper
  // instant: use immediate scroll (no animation) - good for streaming
  // throttleMs: minimum time between scrolls (0 = no throttle)
  const scrollToBottom = (instant = false, throttleMs = 0) => {
    if (!isExecutingCodeRef.current && !userTriggeredCodeRef.current) {
      const now = Date.now();
      if (throttleMs > 0 && now - lastScrollTimeRef.current < throttleMs) {
        return; // Skip scroll if within throttle window
      }
      lastScrollTimeRef.current = now;
      messagesEndRef.current?.scrollIntoView({ behavior: instant ? "auto" : "smooth" });
    }
  };

  // Scroll to top helper
  const scrollToTop = () => {
    chatScrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle chat scroll to show/hide scroll arrows (throttled for performance)
  const lastScrollCheckRef = useRef(0);
  const handleChatScroll = useCallback((e) => {
    const now = Date.now();
    if (now - lastScrollCheckRef.current < 100) return; // Throttle to 100ms
    lastScrollCheckRef.current = now;

    const el = e.target;
    const scrollTop = el.scrollTop;
    const scrollHeight = el.scrollHeight;
    const clientHeight = el.clientHeight;

    // Show arrows only if content is tall enough to warrant scrolling (>500px overflow)
    const hasEnoughContent = scrollHeight - clientHeight > 500;

    // Show top arrow if scrolled down more than 300px
    setShowScrollTop(hasEnoughContent && scrollTop > 300);

    // Show bottom arrow if not near bottom (>300px from bottom)
    setShowScrollBottom(hasEnoughContent && scrollHeight - scrollTop - clientHeight > 300);
  }, []);

  // Show auth modal on app load if user is not authenticated (auth-first approach)
  useEffect(() => {
    // Wait for auth loading to complete before checking
    if (!authLoading && !isAuthenticated && !demoMode) {
      setAuthModalOpen(true);
    }
  }, [authLoading, isAuthenticated, demoMode]); // Check when auth loading completes

  // Fetch chat history when user is authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Show loading overlay and parallelize API calls for faster loading
      const initializeUserData = async () => {
        setIsInitializing(true);
        try {
          // Run all API calls in parallel for optimal performance
          await Promise.all([
            // fetchChatHistory() - Removed: Only load when user opens History tab to avoid slow initial load
            fetchUserSettings(), // Also fetches datasources in one call
            loadFootnotes(), // Load user's footnotes/snippets
            fetchCreditStatus() // Load credit limit status (lightweight)
          ]);
        } catch (error) {
          console.error('Error initializing user data:', error);
        } finally {
          setIsInitializing(false);

          // Check if user wants to see the tour (user-specific)
          const userId = user?.id;
          if (userId) {
            const tourCompleted = localStorage.getItem(`guided_tour_completed_${userId}`);
            const tourDismissed = localStorage.getItem(`guided_tour_dismissed_${userId}`);

            // Show tour if not completed AND not dismissed
            if (!tourCompleted && !tourDismissed) {
              // Delay tour start to let UI settle
              setTimeout(() => {
                setShowGuidedTour(true);
              }, 1000);
            }
          }
        }
      };

      // Add a small delay to ensure auth context is fully updated
      const timeoutId = setTimeout(initializeUserData, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated, fetchUserSettings, loadFootnotes, fetchCreditStatus]);

  // Fetch Taurus projects when Projects panel is opened (always refresh to get latest sort order)
  const projectsPanelOpenRef = useRef(false);
  useEffect(() => {
    // Only fetch when panel transitions from closed to open
    if (showProjectsPanel && isAuthenticated && !projectsPanelOpenRef.current) {
      fetchProjects();
    }
    projectsPanelOpenRef.current = showProjectsPanel;
  }, [showProjectsPanel, isAuthenticated, fetchProjects]);

  // Fetch schema when schema tab is active and schema not loaded
  useEffect(() => {
    const activeSchemaTab = tabs.find(tab => tab.id === activeTabId && tab.type === TAB_TYPES.SCHEMA);
    if (activeSchemaTab && !schema) {
      axios.get(`${API_ENDPOINTS.main}/verbagpt-schema`).then(res => setSchema(res.data)).catch(err => {
        console.error('Error fetching schema:', err);
      });
    }
  }, [activeTabId, tabs, schema, API]);

  // Fetch sample questions when app loads or datasources change
  const sampleQuestionsAbortRef = useRef(null);
  const fetchSampleQuestions = useCallback(async () => {
    // Cancel any in-flight request
    if (sampleQuestionsAbortRef.current) {
      sampleQuestionsAbortRef.current.abort();
    }

    // Create new abort controller for this request
    sampleQuestionsAbortRef.current = new AbortController();

    try {
      // Pass datasource name to avoid race condition with config updates
      const params = activeDatasource?.name
        ? { datasource: activeDatasource.name }
        : {};

      const response = await axios.get(`${API_ENDPOINTS.main}/sample-questions`, {
        params,
        timeout: 5000, // 5 second timeout
        signal: sampleQuestionsAbortRef.current.signal
      });

      if (response.data.questions && response.data.questions.length > 0) {
        setSamplePrompts(response.data.questions);
        setSamplePromptsDescription(response.data.description || '');
      }
    } catch (err) {
      // Ignore abort errors (expected when switching datasources quickly)
      if (axios.isCancel(err) || err.name === 'AbortError') {
        return;
      }
      console.error('Error fetching sample questions:', err);
      // Keep default prompts on error - user can still use the app
    }
  }, [API_ENDPOINTS.main, activeDatasource?.name]);

  useEffect(() => {
    // Reset to defaults immediately when datasource changes to prevent stale data
    setSamplePrompts(DEFAULT_SAMPLE_PROMPTS);
    setSamplePromptsDescription('');
    setSamplePromptsPage(0);

    fetchSampleQuestions();

    // Cleanup: abort request if component unmounts or datasource changes
    return () => {
      if (sampleQuestionsAbortRef.current) {
        sampleQuestionsAbortRef.current.abort();
      }
    };
  }, [activeDatasource?.name, fetchSampleQuestions]); // Re-fetch when active datasource name changes

  // Invalidate schema when active datasource changes
  useEffect(() => {
    if (activeDatasource) {
      setSchema(null); // Force schema refresh on next view
      setSelectedTable(null); // Clear selected table to avoid accessing undefined schema
    }
  }, [activeDatasource]);

  // Save theme preference to localStorage
  useEffect(() => {
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    scrollToBottom();
    isExecutingCodeRef.current = false;
    userTriggeredCodeRef.current = false;
  }, [activeTab?.messages]);

  // Autoscroll when Investigation Thread (execution_output/plots) updates
  // NOTE: For plots, actual scroll happens in onLoad handler of images (after they're fully loaded)
  useEffect(() => {
    if (activeTab?.messages && activeTab.messages.length > 0) {
      const lastMessage = activeTab.messages[activeTab.messages.length - 1];
      // Only handle execution_output without plots here (plots handled by image onLoad)
      if (lastMessage.execution_output && !lastMessage.plots) {
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      }
    }
  }, [activeTab?.messages?.length, activeTab?.messages?.[activeTab?.messages?.length - 1]?.execution_output, activeTab?.messages?.[activeTab?.messages?.length - 1]?.plots]);

  // Autoscroll for Taurus streaming content
  useEffect(() => {
    if (activeTab?.taurusStreamingContent) {
      scrollToBottom();
    }
  }, [activeTab?.taurusStreamingContent]);

  // Autoscroll for review streaming content (both Virgo and Taurus)
  // Uses instant scroll with 100ms throttle during streaming for smooth UX
  // Falls back to smooth scroll when streaming completes
  useEffect(() => {
    if (reviewContent[activeTabId]) {
      const isStreaming = reviewLoading[activeTabId];
      if (isStreaming) {
        // During streaming: instant scroll, throttled to 100ms
        scrollToBottom(true, 100);
      } else {
        // Streaming complete: one final smooth scroll
        scrollToBottom(false, 0);
      }
    }
  }, [reviewContent, activeTabId, reviewLoading]);


  // Keyboard shortcut for sidebar toggle (Ctrl+/)
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.ctrlKey && event.key === '/') {
        event.preventDefault();
        setSidebarCollapsed(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // ESC key to interrupt Taurus execution (like Claude Code CLI)
  // After interrupt, user can immediately type a redirect prompt
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        const tab = tabs.find(t => t.id === activeTabId);
        // Only interrupt if Taurus is loading in current tab
        if (tab?.isLoading && tab?.taurusStreamingContent !== undefined) {
          event.preventDefault();
          cancelTaurusRequest(activeTabId);
          // Auto-focus input after interrupt for redirect prompt
          setTimeout(() => {
            const input = document.querySelector('[data-tour="chat-input"]');
            if (input) input.focus();
          }, 100);
        }
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [tabs, activeTabId]);

  // Load and validate Taurus datasource selection from localStorage
  useEffect(() => {
    if (chatMode === 'taurus' && datasources.length > 0) {
      const savedDatasourceName = localStorage.getItem('taurus_selected_datasource');
      if (savedDatasourceName) {
        // Validate that the saved datasource still exists and is owned by user (is_editable = true)
        const datasource = datasources.find(ds => ds.name === savedDatasourceName && ds.is_editable);
        if (datasource) {
          // Check if saved datasource matches the currently active datasource
          if (activeDatasource && datasource.name !== activeDatasource.name) {
            // Mismatch detected - clear Taurus selection to avoid confusion
            localStorage.removeItem('taurus_selected_datasource');
            setTaurusSelectedDatasource(null);
            // Clear Taurus schema cache for the mismatched datasource
            fetch(`${API_ENDPOINTS.taurus}/api/clear-schema-cache?datasource_name=${encodeURIComponent(savedDatasourceName)}`, {
              method: 'POST',
            }).catch(() => { });
          } else {
            setTaurusSelectedDatasource(datasource);
          }
        } else {
          // Datasource no longer exists or is now shared - clear localStorage
          localStorage.removeItem('taurus_selected_datasource');
          setTaurusSelectedDatasource(null);
          // Clear Taurus schema cache for the removed datasource
          fetch(`${API_ENDPOINTS.taurus}/api/clear-schema-cache?datasource_name=${encodeURIComponent(savedDatasourceName)}`, {
            method: 'POST',
          }).catch(() => { });
        }
      }
    }
  }, [chatMode, datasources, activeDatasource]);

  // Fetch Taurus database info when in Taurus mode or when datasource changes (LEGACY - will be removed)
  useEffect(() => {
    if (chatMode === 'taurus') {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token && isAuthenticated) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      // Pass backend URL to Taurus server
      headers['X-Backend-URL'] = API_ENDPOINTS.main;

      fetch(`${API_ENDPOINTS.taurus}/api/database-info`, { headers })
        .then(res => res.json())
        .then(data => setTaurusDatabaseInfo(data))
        .catch(err => {
          console.error('Failed to fetch Taurus database info:', err);
          setTaurusDatabaseInfo(null);
        });
    }
  }, [chatMode, activeDatasource, isAuthenticated]); // Also refresh when datasource changes

  // Tab management functions
  const createNewChatTab = () => {
    const newTabId = `chat-${Date.now()}`;
    const newTab = {
      id: newTabId,
      type: TAB_TYPES.CHAT,
      title: 'New Chat',
      sessionId: uuidv4(),
      messages: [],
      inputMessage: '',
      isLoading: false,
      mode: chatMode, // Tag new tab with current mode
      isSaved: true,  // New tabs start as "saved" (nothing to save yet)
      savedConversationId: null,
      taurusDatasource: null  // Per-tab Taurus datasource (starts empty)
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);
  };

  // Save conversation to backend
  const saveConversation = async (tabId = activeTabId) => {
    const tab = tabs.find(t => t.id === tabId);
    if (!tab || tab.type !== TAB_TYPES.CHAT || !tab.messages.length) return;

    const token = localStorage.getItem('token');
    if (!token || !isAuthenticated) {
      console.warn('Cannot save conversation: not authenticated');
      return;
    }

    // Set saving state
    setSavingConversation(prev => ({ ...prev, [tabId]: true }));

    try {
      // Generate title from first user message
      const firstUserMsg = tab.messages.find(m => m.type === 'user');
      const title = firstUserMsg?.content?.substring(0, 100) || 'Untitled Chat';

      // Helper: Convert blob URL to base64
      const blobToBase64 = async (blobUrl) => {
        try {
          const response = await fetch(blobUrl);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              const base64 = reader.result.split(',')[1];
              resolve(base64);
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
          });
        } catch (err) {
          console.error('Failed to convert blob to base64:', err);
          return null;
        }
      };

      // Convert messages for storage (convert plots to base64)
      const messagesForSave = await Promise.all(tab.messages.map(async (msg) => {
        const msgCopy = { ...msg };

        // Convert plots to base64 for storage (matching ShareChatModal logic)
        if (msg.plots && msg.plots.length > 0) {
          const processedCharts = await Promise.all(msg.plots.map(async (plot) => {
            // Case 1: Blob URL string - fetch and convert
            if (typeof plot === 'string' && plot.startsWith('blob:')) {
              const base64Data = await blobToBase64(plot);
              return base64Data ? { type: 'matplotlib', data: base64Data } : null;
            }
            // Case 2: Data URL string - extract base64
            if (typeof plot === 'string' && plot.startsWith('data:')) {
              const base64Data = plot.split(',')[1];
              return { type: 'matplotlib', data: base64Data };
            }
            // Case 3: Taurus file object - fetch from server
            if (typeof plot === 'object' && plot !== null && plot.type === 'file' && plot.path) {
              try {
                const fileUrl = `${API_ENDPOINTS.taurus}/api/files/view?path=${encodeURIComponent(plot.path)}`;
                const response = await fetch(fileUrl);
                if (response.ok) {
                  const blob = await response.blob();
                  const base64Data = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result.split(',')[1]);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                  });
                  return { type: 'matplotlib', data: base64Data };
                }
              } catch (e) {
                console.warn('Failed to convert Taurus plot to base64:', e);
              }
              return null;
            }
            // Case 4: Object with data field (Virgo stores {url: blobUrl, data: base64})
            if (typeof plot === 'object' && plot !== null && plot.data) {
              // If data is already base64 string, use it
              if (typeof plot.data === 'string' && !plot.data.startsWith('blob:')) {
                return { type: 'matplotlib', data: plot.data };
              }
            }
            // Case 5: Object with url field but no data - fetch from blob URL
            if (typeof plot === 'object' && plot !== null && plot.url && plot.url.startsWith('blob:')) {
              const base64Data = await blobToBase64(plot.url);
              return base64Data ? { type: 'matplotlib', data: base64Data } : null;
            }
            // Fallback: if it's an object with type matplotlib, keep it
            if (typeof plot === 'object' && plot !== null && plot.type === 'matplotlib') {
              return plot;
            }
            console.warn('Unknown plot format:', plot);
            return null;
          }));
          msgCopy.charts = processedCharts.filter(c => c !== null);
          delete msgCopy.plots;
        }

        return msgCopy;
      }));

      const response = await fetch(`${API_ENDPOINTS.main}/saved-conversations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          session_id: tab.sessionId,
          title: title,
          mode: tab.mode || 'virgo',
          messages: messagesForSave,
          datasource: activeDatasource?.name || selectedDatasource || null
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Update tab to mark as saved
        setTabs(prev => prev.map(t =>
          t.id === tabId
            ? { ...t, isSaved: true, savedConversationId: data.conversation?.id }
            : t
        ));
        console.log('Conversation saved:', data.conversation?.id);
      } else {
        console.error('Failed to save conversation:', await response.text());
      }
    } catch (error) {
      console.error('Error saving conversation:', error);
    } finally {
      // Clear saving state
      setSavingConversation(prev => ({ ...prev, [tabId]: false }));
    }
  };

  // Mark conversation as unsaved when messages change
  const markConversationUnsaved = (tabId) => {
    setTabs(prev => prev.map(t =>
      t.id === tabId && t.messages.length > 0
        ? { ...t, isSaved: false }
        : t
    ));
  };

  const closeTab = (tabId) => {
    // Find the tab being closed to clean up its blob URLs
    const closingTab = tabs.find(tab => tab.id === tabId);

    // Revoke all blob URLs in the tab's messages to free memory
    if (closingTab?.messages) {
      closingTab.messages.forEach(msg => {
        if (msg.plots) {
          msg.plots.forEach(plot => {
            // Check if plot is a blob URL string
            if (typeof plot === 'string' && plot.startsWith('blob:')) {
              URL.revokeObjectURL(plot);
            }
            // Check if plot is an object with blob URL
            else if (plot && typeof plot === 'object') {
              // New format: {url: 'blob:...', data: 'base64'}
              if (plot.url && typeof plot.url === 'string' && plot.url.startsWith('blob:')) {
                URL.revokeObjectURL(plot.url);
              }
              // Legacy format: {data: 'blob:...'}
              const plotData = plot.data || plot.base64 || plot.image || plot;
              if (typeof plotData === 'string' && plotData.startsWith('blob:')) {
                URL.revokeObjectURL(plotData);
              }
            }
          });
        }
      });
    }

    // Clean up Taurus visuals folders for the closed tab
    if (closingTab?.mode === 'taurus' && closingTab?.messages?.length > 0) {
      const requestIdsToCleanup = [];
      closingTab.messages.forEach(msg => {
        if (msg.taurusRequestId && !requestIdsToCleanup.includes(msg.taurusRequestId)) {
          requestIdsToCleanup.push(msg.taurusRequestId);
        }
      });
      if (requestIdsToCleanup.length > 0) {
        // Fire and forget - don't block tab close on cleanup
        fetch(`${API_ENDPOINTS.taurus}/api/cleanup-visuals`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ request_ids: requestIdsToCleanup })
        }).catch(() => { }); // Ignore errors silently
      }
    }

    setTabs(prev => {
      const newTabs = prev.filter(tab => tab.id !== tabId);
      if (tabId === activeTabId && newTabs.length > 0) {
        // Switching to first remaining tab
        const newActiveTab = newTabs[0];
        setActiveTabId(newActiveTab.id);
        // Sync chatMode to match the new active tab's mode
        if (newActiveTab.mode) {
          setChatMode(newActiveTab.mode);
          localStorage.setItem('chatMode', newActiveTab.mode);
        }
      } else if (tabId === activeTabId) {
        setActiveTabId(null);
      }
      return newTabs;
    });

    // Clean up review states for closed tab to prevent memory leaks
    setReviewContent(prev => {
      const newState = { ...prev };
      delete newState[tabId];
      return newState;
    });
    setReviewLoading(prev => {
      const newState = { ...prev };
      delete newState[tabId];
      return newState;
    });
  };

  const openTab = (type, title = '') => {
    const existingTab = tabs.find(tab => tab.type === type);
    if (existingTab) {
      setActiveTabId(existingTab.id);
      // Refresh chat history when switching to existing History tab
      if (type === TAB_TYPES.HISTORY && isAuthenticated) {
        fetchChatHistory(true);
      }
      return;
    }

    const newTabId = `${type}-${Date.now()}`;
    const newTab = {
      id: newTabId,
      type: type,
      title: title || type.charAt(0).toUpperCase() + type.slice(1),
      sessionId: type === TAB_TYPES.CHAT ? uuidv4() : null,
      messages: type === TAB_TYPES.CHAT ? [] : null,
      inputMessage: type === TAB_TYPES.CHAT ? '' : null,
      isLoading: type === TAB_TYPES.CHAT ? false : null,
      isSaved: type === TAB_TYPES.CHAT ? true : null,  // New tabs start as "saved"
      savedConversationId: type === TAB_TYPES.CHAT ? null : null
    };
    setTabs(prev => [...prev, newTab]);
    setActiveTabId(newTabId);

    // Fetch chat history when opening new History tab
    if (type === TAB_TYPES.HISTORY && isAuthenticated) {
      fetchChatHistory(true);
    }
  };

  const updateTabData = (tabId, updates) => {
    setTabs(prev => prev.map(tab =>
      tab.id === tabId ? { ...tab, ...updates } : tab
    ));
  };

  const buildPriorVirgoQaContext = (messages = []) => {
    const MAX_CHART_BASE64_CHARS = 1_200_000; // Keep below backend 1.5M hard cap
    const MAX_TOTAL_CONTEXT_CHARS = 1_800_000; // Keep below backend ~2MB payload cap
    const context = [];
    let latestConsoleCaptured = false;
    let totalChars = 0;

    for (let i = messages.length - 1; i >= 0 && context.length < 3; i--) {
      const msg = messages[i];
      if (!msg || msg.type !== 'bot' || !msg.isVirgo) continue;

      let question = '';
      for (let j = i - 1; j >= 0; j--) {
        const prev = messages[j];
        if (prev?.type === 'user') {
          question = prev.content || '';
          break;
        }
      }

      const charts = (msg.plots || [])
        .map(plot => {
          if (!plot) return null;
          if (typeof plot === 'string') {
            if (plot.startsWith('data:')) return plot.split(',')[1] || null;
            return null;
          }
          const raw = plot.data || plot.base64 || plot.image || null;
          if (!raw || typeof raw !== 'string') return null;
          return raw.startsWith('data:') ? (raw.split(',')[1] || null) : raw;
        })
        .filter(raw => raw && raw.length <= MAX_CHART_BASE64_CHARS)
        .filter(Boolean);

      // Include console output only from the most recent Virgo QA.
      let consoleOutput = '';
      if (!latestConsoleCaptured) {
        consoleOutput = (msg.consoleOutput || msg.execution_output || '').trim();
        if (consoleOutput) latestConsoleCaptured = true;
      }

      // Skip entries that contribute neither console (latest only) nor charts.
      if (!consoleOutput && charts.length === 0) continue;

      const entryChars = (question?.length || 0) + (consoleOutput?.length || 0) +
        charts.reduce((sum, c) => sum + (c?.length || 0), 0);
      if (totalChars + entryChars > MAX_TOTAL_CONTEXT_CHARS) continue;
      totalChars += entryChars;

      context.unshift({
        question,
        console_output: consoleOutput,
        charts
      });
    }
    return context;
  };

  // Send message with Virgo (smolagents) workflow
  const sendVirgoMessage = async (text, tabId = activeTabId) => {
    if (!text.trim()) return;

    const tab = tabs.find(t => t.id === tabId);
    if (!tab || tab.type !== TAB_TYPES.CHAT) return;

    if (!isAuthenticated && !demoMode) {
      setAuthPromptModalOpen(true);
      return;
    }

    const userStartTime = Date.now();  // Track response time
    const userMessage = { id: uuidv4(), type: 'user', content: text, timestamp: new Date(), isVirgo: true };
    const updatedMessages = [...tab.messages, userMessage];
    const updates = { messages: updatedMessages, isLoading: true, inputMessage: '', isSaved: false };
    if (tab.messages.length === 0 || tab.title === 'New Chat') {
      updates.title = generateTabTitle(updatedMessages);
    }
    updateTabData(tabId, updates);

    // Open console modal and reset states
    setConsoleOutput('');
    consoleBufferRef.current = '';  // Reset console buffer
    if (consoleUpdateTimerRef.current) {
      clearTimeout(consoleUpdateTimerRef.current);
      consoleUpdateTimerRef.current = null;
    }
    setAgenticFinalCode('');
    setAgenticFinalOutput('');
    setAgenticFinalNarrative('');
    setAgenticChartData([]);
    setTerminalConsoleOpen(true);
    setIsConsoleStreaming(true);

    // Create AbortController for this stream
    const abortController = new AbortController();
    virgoAbortControllersRef.current[tabId] = abortController;
    virgoSessionIdsRef.current[tabId] = tab.sessionId;

    try {
      let useVirgoReview = false;  // Flag to track if review is enabled
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const priorQaContext = buildPriorVirgoQaContext(tab.messages);

      const response = await fetch(`${API_ENDPOINTS.main}/virgo-stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          session_id: tab.sessionId,
          message: text,
          model: settings.baseModel,
          is_demo: demoMode,
          // File context for Virgo
          file_ids: selectedFiles.map(f => f.id),
          include_database: includeDatabase,
          prior_qa_context: priorQaContext,
          // Project context — persistent instructions from active project
          project_context: activeProject?.context_text || null
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        // Handle error responses (429, 401, etc.)
        let errorMessage = `Error ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // If response isn't JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullConsoleOutput = '';
      let promptData = null;
      let finalPlots = [];
      let finalResponse = '';
      let dbMessageId = null;
      let queryCost = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = JSON.parse(line.slice(6));
          switch (data.type) {
            case 'console':
              // Batch console updates to prevent OOM from excessive re-renders
              fullConsoleOutput += data.data;
              consoleBufferRef.current = fullConsoleOutput;
              scheduleBatchedConsoleUpdate(consoleBufferRef, consoleUpdateTimerRef, setConsoleOutput);
              break;
            case 'chart_data':
              try {
                const newImgs = (data.data || []).map(d => d.data).filter(Boolean);
                if (newImgs.length > 0) {
                  console.log('Virgo chart_data received:', newImgs.length, 'plots');
                  // Convert base64 to blob URLs immediately to free memory
                  // This prevents OOM errors when streaming multiple large plots
                  // Store both blob URL (for display) and original base64 (for review)
                  const plotsWithData = newImgs.map(base64 => ({
                    url: base64ToBlobUrl(base64),
                    data: base64  // Keep original base64 for review
                  }));
                  console.log('Converted to blob URLs:', plotsWithData.length);
                  finalPlots.push(...plotsWithData);
                }
              } catch (e) {
                console.error('Error processing chart_data:', e);
              }
              break;
            case 'success':
              // Backend may send a synthesized success payload (final event)
              promptData = data.prompt_data || null;
              if (Array.isArray(data.plots) && data.plots.length > 0) {
                // Only use success event plots if we haven't received any chart_data events
                // This prevents overwriting plots that were already streamed
                if (finalPlots.length === 0) {
                  // Extract base64 from plot objects and store both blob URL and original data
                  const plotsArray = data.plots.map(plot => {
                    const base64 = typeof plot === 'string' ? plot : plot.data;
                    return {
                      url: base64ToBlobUrl(base64),
                      data: base64  // Keep original base64 for review
                    };
                  });
                  finalPlots = plotsArray;
                  console.log('Using plots from success event:', finalPlots.length);
                } else {
                  console.log('Ignoring success event plots (already have', finalPlots.length, 'from chart_data events)');
                }
              }
              finalResponse = data.response || '';
              dbMessageId = data.id || null;
              useVirgoReview = data.use_virgo_review || false;
              if (data.estimated_cost_usd != null) queryCost = data.estimated_cost_usd;
              break;
            case 'heartbeat':
              // SSE keepalive - ignore (prevents browser timeout during long operations)
              break;
            case 'error':
              console.error('Virgo error:', data.data);
              flushConsoleBuffer(consoleBufferRef, consoleUpdateTimerRef, setConsoleOutput);
              setIsConsoleStreaming(false);
              setTerminalConsoleOpen(false);
              // Attach whatever console output arrived so the investigation toggle still renders
              const partialConsole = stripAnsi(fullConsoleOutput);
              const errorMessage = {
                id: uuidv4(), type: 'bot',
                content: '_Analysis interrupted — see investigation details below._',
                execution_output: partialConsole,
                consoleOutput: partialConsole,
                plots: finalPlots,
                isVirgo: true,
                finalResponse: true,
                timestamp: new Date(),
                error: data.data
              };
              setTabs(currentTabs => currentTabs.map(t => t.id === tabId ? { ...t, messages: [...t.messages, errorMessage], isLoading: false } : t));
              return;
          }
        }
      }

      // Flush any pending console output before closing
      flushConsoleBuffer(consoleBufferRef, consoleUpdateTimerRef, setConsoleOutput);

      // Close console modal
      setIsConsoleStreaming(false);
      setTimeout(() => {
        setTerminalConsoleOpen(false);

        // Check if Virgo review is enabled (from backend flag)
        if (useVirgoReview) {
          // First, show the investigation thread and plots immediately
          const initialBotMessage = {
            id: uuidv4(),
            type: 'bot',
            content: '',  // No content yet - will be filled by review
            timestamp: new Date(),
            generated_code: '',
            execution_output: stripAnsi(fullConsoleOutput),
            plots: finalPlots,
            consoleOutput: stripAnsi(fullConsoleOutput),
            isVirgo: true,
            finalResponse: true,
            responseTime: Date.now() - userStartTime,
            ...(queryCost != null ? { queryCost } : {}),
            ...(dbMessageId ? { dbMessageId } : {}),
            ...(promptData ? { prompt_data: promptData } : {})
          };

          // Add message to chat immediately
          setTabs(currentTabs => currentTabs.map(t =>
            t.id === tabId ? { ...t, messages: [...t.messages, initialBotMessage], isLoading: false } : t
          ));

          // Now trigger review call (will show loading spinner and stream result)
          callVirgoReview(
            stripAnsi(fullConsoleOutput),  // Investigation thread
            tabId,
            finalPlots,
            stripAnsi(fullConsoleOutput),
            dbMessageId,
            promptData
          );
        } else {
          // Original behavior: show final response directly
          const botMessage = {
            id: uuidv4(),
            type: 'bot',
            content: finalResponse || 'Analysis complete',
            timestamp: new Date(),
            generated_code: '',
            execution_output: stripAnsi(fullConsoleOutput),
            plots: finalPlots,
            consoleOutput: stripAnsi(fullConsoleOutput),
            isVirgo: true,
            finalResponse: true,
            responseTime: Date.now() - userStartTime,
            ...(queryCost != null ? { queryCost } : {}),
            ...(dbMessageId ? { dbMessageId } : {}),
            ...(promptData ? { prompt_data: promptData } : {})
          };
          setTabs(currentTabs => currentTabs.map(t => t.id === tabId ? { ...t, messages: [...t.messages, botMessage], isLoading: false } : t));
        }
      }, 200);  // Reduced from 800ms for faster perceived response
    } catch (error) {
      console.error('Virgo workflow error:', error);
      // Flush console buffer before closing on error
      flushConsoleBuffer(consoleBufferRef, consoleUpdateTimerRef, setConsoleOutput);
      setIsConsoleStreaming(false);
      setTerminalConsoleOpen(false);

      // Check if this was an abort (user-initiated stop)
      if (error.name === 'AbortError') {
        console.log('Virgo stream aborted by user');
        // Don't add error message - stop function handles the message
        setTabs(currentTabs => currentTabs.map(t =>
          t.id === tabId ? { ...t, isLoading: false } : t
        ));
      } else {
        // Add error message to chat for real errors
        const errorMessage = {
          id: uuidv4(),
          type: 'bot',
          content: error.message || 'An error occurred while processing your request.',
          timestamp: new Date(),
          error: error.message,
          isVirgo: true
        };
        setTabs(currentTabs => currentTabs.map(t =>
          t.id === tabId ? { ...t, messages: [...t.messages, errorMessage], isLoading: false } : t
        ));
      }
    } finally {
      // Clean up abort controller and session tracking
      delete virgoAbortControllersRef.current[tabId];
      delete virgoSessionIdsRef.current[tabId];

      // Refresh credit status after query is sent (counted against limit)
      if (isAuthenticated) {
        fetchCreditStatus();
      }
    }
  };

  // Stop Virgo stream (user-initiated cancellation)
  const stopVirgoStream = useCallback(async () => {
    const tabId = activeTabId;
    const abortController = virgoAbortControllersRef.current[tabId];
    const sessionId = virgoSessionIdsRef.current[tabId];

    if (!abortController) {
      console.warn('No active Virgo stream to stop');
      return;
    }

    try {
      // Abort the frontend stream
      abortController.abort();

      // Call backend to terminate the process
      if (sessionId) {
        const token = localStorage.getItem('token');
        const headers = { 'Content-Type': 'application/json' };
        if (token) headers.Authorization = `Bearer ${token}`;

        // Fire and forget - don't wait for response
        fetch(`${API_ENDPOINTS.main}/virgo-abort`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ session_id: sessionId })
        }).catch(err => console.error('Failed to abort backend process:', err));
      }

      // Close modal
      setTerminalConsoleOpen(false);
      setIsConsoleStreaming(false);

      // Add stopped message to chat
      const stoppedMessage = {
        id: uuidv4(),
        type: 'bot',
        content: '⚠️ **Analysis stopped by user**\n\nThe operation was cancelled and resources have been released. You can start a new query at any time.',
        timestamp: new Date(),
        isVirgo: true,
        finalResponse: true
      };

      setTabs(currentTabs => currentTabs.map(t =>
        t.id === tabId ? { ...t, messages: [...t.messages, stoppedMessage], isLoading: false } : t
      ));

      // Clean up refs
      delete virgoAbortControllersRef.current[tabId];
      delete virgoSessionIdsRef.current[tabId];
    } catch (error) {
      console.error('Error stopping Virgo stream:', error);
    }
  }, [activeTabId]);

  // Call Virgo review API and stream polished response
  const callVirgoReview = async (investigationThread, tabId, plots = [], consoleOutput = '', dbMessageId = null, promptData = null) => {
    try {
      setReviewLoading(prev => ({ ...prev, [tabId]: true }));
      setReviewContent(prev => ({ ...prev, [tabId]: '' }));

      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token && isAuthenticated) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Extract base64 chart data from plots array
      // Plots can be:
      // - Objects with {url: 'blob:...', data: 'base64'} (new format)
      // - Strings (blob URLs - old format, no base64 available)
      // - Objects with {data: 'base64'} (legacy format)
      const chartData = plots && plots.length > 0
        ? plots.map(plot => {
          if (typeof plot === 'object' && plot.data) {
            return plot.data;  // Extract base64 from object
          }
          return null;  // Can't extract base64 from blob URL string
        }).filter(Boolean)
        : null;

      // Simple debug log
      if (chartData && chartData.length > 0) {
        console.log(`[Virgo Review] Sending ${chartData.length} chart(s) for vision analysis`);
      } else {
        console.log('[Virgo Review] No charts provided - text-only mode');
      }

      const response = await fetch(`${API_ENDPOINTS.main}/virgo-review`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          investigation_thread: investigationThread,
          model: null,  // Use virgo_review_model from settings
          charts: chartData  // Include charts for vision analysis
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim();

            // Skip empty data or [DONE] marker
            if (!dataStr || dataStr === '[DONE]') continue;

            let reviewError = null;
            try {
              const data = JSON.parse(dataStr);
              if (data.type === 'content') {
                accumulatedContent += data.data;
                // Update review content in real-time
                setReviewContent(prev => ({ ...prev, [tabId]: accumulatedContent }));
              } else if (data.type === 'done') {
                // Review complete - save to database and turn off loading spinner
                setReviewLoading(prev => ({ ...prev, [tabId]: false }));
                const reviewCost = data.review_cost_usd ?? null;

                // Persist review content to the message object
                // This prevents re-rendering/reset when follow-up questions trigger new reviews
                // Target by dbMessageId if available, otherwise last message with isVirgo flag
                setTabs(currentTabs => currentTabs.map(t => {
                  if (t.id !== tabId) return t;

                  // Find target message index once (not per iteration)
                  let targetIdx = -1;
                  if (dbMessageId) {
                    targetIdx = t.messages.findIndex(m => m.dbMessageId === dbMessageId);
                  } else {
                    // Fallback: find last Virgo message
                    for (let i = t.messages.length - 1; i >= 0; i--) {
                      if (t.messages[i].isVirgo) { targetIdx = i; break; }
                    }
                  }

                  if (targetIdx === -1) return t;

                  return {
                    ...t,
                    messages: t.messages.map((m, idx) => {
                      if (idx !== targetIdx) return m;
                      const updatedCost = reviewCost != null
                        ? (m.queryCost ?? 0) + reviewCost
                        : m.queryCost;
                      return { ...m, reviewContent: accumulatedContent, queryCost: updatedCost };
                    })
                  };
                }));

                // Save virgo summary to database if we have a message ID
                if (dbMessageId && accumulatedContent && isAuthenticated) {
                  try {
                    await fetch(`${API_ENDPOINTS.main}/save-review-summary`, {
                      method: 'POST',
                      headers: headers,
                      body: JSON.stringify({
                        message_id: dbMessageId,
                        review_summary: accumulatedContent
                      })
                    });
                  } catch (saveError) {
                    console.error('Failed to save virgo summary:', saveError);
                    // Don't throw - summary is already visible to user
                  }
                }
              } else if (data.type === 'heartbeat') {
                // SSE keepalive - ignore
                continue;
              } else if (data.type === 'error') {
                reviewError = data.data;
              }
            } catch (e) {
              // Only log if it's not just an empty/whitespace line
              if (dataStr.length > 0) {
                console.error('Error parsing review stream:', e.message, 'Data:', dataStr.substring(0, 100));
              }
              // Don't throw - continue processing other chunks
            }
            if (reviewError) throw new Error(reviewError);
          }
        }
      }
    } catch (error) {
      console.error('Virgo review error:', error);
      setReviewLoading(prev => ({ ...prev, [tabId]: false }));

      // Update the initial placeholder message (which already has consoleOutput attached)
      // with fallback content rather than adding a separate orphaned error message.
      // This keeps the investigation console toggle visible so analysis isn't lost.
      setTabs(currentTabs => currentTabs.map(t => {
        if (t.id !== tabId) return t;
        const lastIdx = t.messages.length - 1;
        const lastMsg = t.messages[lastIdx];
        // Only update if last message is our empty isVirgo placeholder
        if (!lastMsg?.isVirgo || lastMsg.content?.trim()) {
          // Unexpected state — fall back to adding error message
          return { ...t, isLoading: false, messages: [...t.messages, {
            id: uuidv4(), type: 'bot',
            content: `Review failed: ${error.message}`,
            timestamp: new Date(), error: error.message
          }]};
        }
        return {
          ...t, isLoading: false,
          messages: t.messages.map((m, i) => i !== lastIdx ? m : {
            ...m,
            content: '_Review could not be generated — see investigation details below._',
            reviewError: true
          })
        };
      }));
    }
  };

  // Call Taurus Review endpoint - mirror of callVirgoReview for Taurus mode
  const callTaurusReview = async (investigationThread, tabId, plots = [], consoleOutput = '', dbMessageId = null, promptData = null) => {
    try {
      setReviewLoading(prev => ({ ...prev, [tabId]: true }));
      setReviewContent(prev => ({ ...prev, [tabId]: '' }));

      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token && isAuthenticated) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_ENDPOINTS.main}/taurus-review`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          investigation_thread: investigationThread,
          model: null  // Use taurus_review_model from settings
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const dataStr = line.slice(6).trim();

            // Skip empty data or [DONE] marker
            if (!dataStr || dataStr === '[DONE]') continue;

            let reviewError = null;
            try {
              const data = JSON.parse(dataStr);
              if (data.type === 'content') {
                accumulatedContent += data.data;
                // Update review content in real-time
                setReviewContent(prev => ({ ...prev, [tabId]: accumulatedContent }));
              } else if (data.type === 'done') {
                // Review complete - save to database and turn off loading spinner
                setReviewLoading(prev => ({ ...prev, [tabId]: false }));

                // Persist review content to the message object
                // This prevents re-rendering/reset when follow-up questions trigger new reviews
                // Target by dbMessageId if available, otherwise last message with isTaurus flag
                setTabs(currentTabs => currentTabs.map(t => {
                  if (t.id !== tabId) return t;

                  // Find target message index once (not per iteration)
                  let targetIdx = -1;
                  if (dbMessageId) {
                    targetIdx = t.messages.findIndex(m => m.dbMessageId === dbMessageId);
                  } else {
                    // Fallback: find last Taurus message
                    for (let i = t.messages.length - 1; i >= 0; i--) {
                      if (t.messages[i].isTaurus) { targetIdx = i; break; }
                    }
                  }

                  if (targetIdx === -1) return t;

                  return {
                    ...t,
                    messages: t.messages.map((m, idx) =>
                      idx === targetIdx ? { ...m, reviewContent: accumulatedContent } : m
                    )
                  };
                }));

                // Save taurus summary to database if we have a message ID
                if (dbMessageId && accumulatedContent && isAuthenticated) {
                  try {
                    await fetch(`${API_ENDPOINTS.main}/save-review-summary`, {
                      method: 'POST',
                      headers: headers,
                      body: JSON.stringify({
                        message_id: dbMessageId,
                        review_summary: accumulatedContent
                      })
                    });
                  } catch (saveError) {
                    console.error('Failed to save taurus summary:', saveError);
                    // Don't throw - summary is already visible to user
                  }
                }
              } else if (data.type === 'heartbeat') {
                // SSE keepalive - ignore
                continue;
              } else if (data.type === 'error') {
                reviewError = data.data;
              }
            } catch (e) {
              // Only log if it's not just an empty/whitespace line
              if (dataStr.length > 0) {
                console.error('Error parsing review stream:', e.message, 'Data:', dataStr.substring(0, 100));
              }
              // Don't throw - continue processing other chunks
            }
            if (reviewError) throw new Error(reviewError);
          }
        }
      }
    } catch (error) {
      console.error('Taurus review error:', error);
      setReviewLoading(prev => ({ ...prev, [tabId]: false }));

      // Add error message
      const errorMessage = {
        id: uuidv4(),
        type: 'bot',
        content: `Failed to generate review: ${error.message}`,
        timestamp: new Date(),
        error: error.message
      };
      setTabs(currentTabs => currentTabs.map(t =>
        t.id === tabId ? { ...t, messages: [...t.messages, errorMessage], isLoading: false } : t
      ));
    }
  };

  // Deep Review - Verifies accuracy and fixes issues in-place (supports both Virgo and Taurus)
  const callDeepReview = async (message, tabId, reviewNotes = null) => {
    const messageId = message.id;
    const isVirgo = !message.isTaurus;

    try {
      setDeepReviewLoading(prev => ({ ...prev, [messageId]: true }));
      setDeepReviewFindings(prev => ({ ...prev, [messageId]: '' }));

      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Backend-URL': API_ENDPOINTS.main
      };

      // Find the original user question that preceded this bot message
      const tab = tabs.find(t => t.id === tabId);
      let originalQuestion = '';
      if (tab) {
        const msgIndex = tab.messages.findIndex(m => m.id === messageId);
        if (msgIndex > 0) {
          for (let i = msgIndex - 1; i >= 0; i--) {
            if (tab.messages[i].type === 'user') {
              originalQuestion = tab.messages[i].content;
              break;
            }
          }
        }
      }

      // Virgo vs Taurus: different endpoints and data sources
      let endpoint, requestBody;
      if (isVirgo) {
        // Virgo: use consoleOutput as investigation thread, charts are base64
        const investigationThread = message.consoleOutput || message.execution_output || '';
        const charts = message.plots?.filter(p => p.data).map(p => p.data) || [];
        endpoint = `${API_ENDPOINTS.main}/virgo-deep-review`;
        requestBody = {
          investigation_thread: investigationThread,
          original_question: originalQuestion,
          charts: charts,  // base64 array
          review_notes: reviewNotes
        };
      } else {
        // Taurus: use investigationThread, plots are file paths
        endpoint = `${API_ENDPOINTS.taurus}/api/deep-review`;
        requestBody = {
          investigation_thread: message.investigationThread,
          original_question: originalQuestion,
          plots: message.plots,
          review_notes: reviewNotes,
          request_id: message.taurusRequestId  // Links to original request's visuals folder
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulatedFindings = '';
      let hasCorrections = false;
      let regeneratedPlots = []; // Only charts generated during review (replacements)

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const dataStr = line.slice(6).trim();
          if (!dataStr || dataStr === '[DONE]') continue;

          try {
            const data = JSON.parse(dataStr);

            switch (data.type) {
              case 'status':
                // Status updates - could show in UI if desired
                console.log('Deep review status:', data.content);
                break;
              case 'console':
                // Stream console output during verification phase (Virgo only)
                // Prefix with marker so we know it's console output
                setDeepReviewFindings(prev => ({
                  ...prev,
                  [messageId]: (prev[messageId] || '') + data.data
                }));
                break;
              case 'progress':
                // Legacy - accumulate progress
                accumulatedFindings += data.content;
                break;
              case 'finding_stream':
                // Streaming summary from OpenRouter - clear console and show findings
                if (!accumulatedFindings) {
                  // First chunk - clear the console output, start fresh with findings
                  setDeepReviewFindings(prev => ({ ...prev, [messageId]: data.content }));
                } else {
                  // Append to findings
                  setDeepReviewFindings(prev => ({
                    ...prev,
                    [messageId]: (prev[messageId] || '') + data.content
                  }));
                }
                accumulatedFindings += data.content;
                break;
              case 'image':
                // Regenerated chart - handle both Virgo (base64) and Taurus (file path)
                if (isVirgo) {
                  regeneratedPlots.push({ type: 'base64', data: data.data, timestamp: Date.now() });
                } else {
                  regeneratedPlots.push({ type: 'file', path: data.path, timestamp: Date.now() });
                }
                break;
              case 'finding':
                // Final complete findings - replace everything
                accumulatedFindings = data.content;
                setDeepReviewFindings(prev => ({ ...prev, [messageId]: accumulatedFindings }));
                break;
              case 'done':
                // Extract has_corrections from summary
                hasCorrections = data.summary?.has_corrections || false;

                // Compute updated thread for both state update and DB save
                // Virgo uses consoleOutput, Taurus uses investigationThread
                let threadToSave = isVirgo
                  ? (message.consoleOutput || message.execution_output || '')
                  : (message.investigationThread || '');
                if (hasCorrections && threadToSave) {
                  threadToSave = threadToSave + '\n\n---\n📋 **Deep Review found corrections** — see findings below';
                }

                // Update message in-place with findings and any regenerated charts
                setTabs(currentTabs => currentTabs.map(t => {
                  if (t.id !== tabId) return t;
                  return {
                    ...t,
                    messages: t.messages.map(m => {
                      if (m.id !== messageId) return m;

                      // Smart merge: for Taurus replace plots by filename, for Virgo just append
                      let updatedPlots = m.plots ? [...m.plots] : [];
                      if (regeneratedPlots.length > 0) {
                        if (isVirgo) {
                          // Virgo: base64 plots, just append (no filename-based replacement)
                          updatedPlots = [...updatedPlots, ...regeneratedPlots];
                        } else {
                          // Taurus: file-based, replace by filename
                          for (const newPlot of regeneratedPlots) {
                            const newFilename = newPlot.path?.split(/[/\\]/).pop();
                            const existingIndex = updatedPlots.findIndex(p =>
                              p.path && p.path.split(/[/\\]/).pop() === newFilename
                            );
                            if (existingIndex >= 0) {
                              updatedPlots[existingIndex] = newPlot;
                            } else {
                              updatedPlots.push(newPlot);
                            }
                          }
                        }
                      }

                      // Update the appropriate thread field based on mode
                      const threadUpdate = hasCorrections
                        ? (isVirgo ? { consoleOutput: threadToSave } : { investigationThread: threadToSave })
                        : {};

                      return {
                        ...m,
                        ...threadUpdate,
                        ...(regeneratedPlots.length > 0 ? { plots: updatedPlots } : {}),
                        deepReviewFindings: accumulatedFindings
                      };
                    })
                  };
                }));

                // Save deep review results to database
                if (message.dbMessageId && accumulatedFindings) {
                  try {
                    await fetch(`${API_ENDPOINTS.main}/save-deep-review`, {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                      },
                      body: JSON.stringify({
                        message_id: message.dbMessageId,
                        response: threadToSave,
                        deep_review_findings: accumulatedFindings
                      })
                    });
                  } catch (saveError) {
                    console.error('Failed to save deep review to database:', saveError);
                  }
                }
                break;
              case 'error':
                throw new Error(data.message);
              default:
                break;
            }
          } catch (e) {
            if (dataStr.length > 0 && !dataStr.includes('"type":"status"')) {
              console.error('Deep review parse error:', e.message);
            }
          }
        }
      }
    } catch (error) {
      console.error('Deep review error:', error);
      setDeepReviewFindings(prev => ({
        ...prev,
        [messageId]: `**Error**: ${error.message}\n\nPlease try again.`
      }));
    } finally {
      setDeepReviewLoading(prev => ({ ...prev, [messageId]: false }));
      setDeepReviewExpanded(prev => ({ ...prev, [messageId]: true }));
    }
  };

  // Helper to extract plots from Taurus content (base64 markers)
  const extractTaurusPlots = (content) => {
    const plots = [];
    const plotRegex = /__TAURUS_PLOT__([A-Za-z0-9+/=]+)/g;
    let match;

    while ((match = plotRegex.exec(content)) !== null) {
      plots.push({
        type: 'base64',
        data: match[1]  // base64 encoded image
      });
    }

    // Remove plot markers from content
    const cleanContent = content.replace(plotRegex, '').trim();

    return { plots, cleanContent };
  };

  // Send Taurus message (Claude CLI integration via standalone server) - streams directly to chat
  // options: { contextMode?: 'reset' | 'continue' | 'summary' } - for project step execution
  const sendTaurusMessage = async (text, tabId = activeTabId, options = {}) => {
    if (!text.trim()) return;

    const tab = tabs.find(t => t.id === tabId);
    if (!tab || tab.type !== TAB_TYPES.CHAT) return;

    const userStartTime = Date.now();  // Track response time

    // Capture image metadata before clearing (for chat history display)
    const attachedImagesMeta = taurusAttachedImages.length > 0 ? {
      count: taurusAttachedImages.length,
      names: taurusAttachedImages.map(img => img.name || 'image')
    } : null;

    // Capture image data for the request (before clearing)
    const imagesToSend = taurusAttachedImages.length > 0
      ? taurusAttachedImages.map(img => ({ data: img.data, media_type: img.media_type }))
      : null;

    // Clear images immediately for instant UI feedback
    if (taurusAttachedImages.length > 0) {
      clearTaurusImages();
    }

    const userMessage = {
      id: uuidv4(),
      type: 'user',
      content: text,
      timestamp: new Date(),
      isTaurus: true,
      attachedImages: attachedImagesMeta  // Store metadata only (not base64)
    };
    const updatedMessages = [...tab.messages, userMessage];
    // Reuse existing requestId for follow-up messages (enables REPL variable persistence)
    // Generate new one only for first message OR when context_mode is 'reset' (true isolation)
    const shouldResetRepl = options.contextMode === 'reset';
    const requestId = shouldResetRepl ? uuidv4() : (tab.taurusRequestId || uuidv4());
    console.log('[Taurus] RequestId:', requestId.slice(0, 8), shouldResetRepl ? '(reset)' : (tab.taurusRequestId ? '(reused)' : '(new)'));
    const updates = { messages: updatedMessages, isLoading: true, inputMessage: '', taurusStreamingContent: '', taurusRequestId: requestId, isSaved: false };
    if (tab.messages.length === 0 || tab.title === 'New Chat') {
      updates.title = generateTabTitle(updatedMessages);
    }
    updateTabData(tabId, updates);

    // Declare outside try block so catch can access partial content on error
    let accumulatedResponse = '';
    let imagePaths = [];

    // Create AbortController for this stream (allows cancellation on clear/stop)
    const abortController = new AbortController();
    taurusAbortControllersRef.current[tabId] = { controller: abortController, requestId };

    try {
      // Check if this is a continuation after user interrupted (for backend instruction injection)
      const lastMessage = tab.messages[tab.messages.length - 1];
      const afterInterrupt = lastMessage?.content?.includes('stopped by user') || false;

      // Claude SDK session resumption:
      // - If tab has taurusSessionId, we're continuing a conversation (resume)
      // - If no taurusSessionId, this is a new conversation (fresh session)
      // - If context_mode is 'reset' (batch mode), start fresh session for true isolation
      // - After interrupt: still resume for conversation context, backend adds instruction to avoid stale tasks
      const hasExistingSession = tab.taurusSessionId && tab.messages.length > 0;
      const shouldResumeSession = hasExistingSession && options.contextMode !== 'reset';

      console.log('[Taurus] Sending message. Session:', tab.taurusSessionId || 'new', 'Messages:', tab.messages.length, 'Resume:', shouldResumeSession, 'AfterInterrupt:', afterInterrupt);

      // Get auth token for datasource access
      // Note: Just check for token, not isAuthenticated - after refresh, auth context may not be ready yet
      const token = localStorage.getItem('token');
      const headers = { 'Content-Type': 'application/json' };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      // Pass backend URL to Taurus server (enables auto-switching between local/production)
      // Taurus will use this URL to fetch user config, data_notes, etc.
      headers['X-Backend-URL'] = API_ENDPOINTS.main;

      // Build request body
      const requestBody = {
        question: text,
        request_id: requestId,
        session_id: shouldResumeSession ? tab.taurusSessionId : null,  // Resume or start new (batch mode always starts fresh, also fresh after interrupt)
        after_interrupt: afterInterrupt
      };

      // Include selected datasource if available (optional - Taurus can work without it)
      // Read from tab's taurusDatasource (per-tab isolation prevents cross-tab contamination)
      const datasourceToUse = tab.taurusDatasource || null;
      if (datasourceToUse) {
        requestBody.datasource_name = datasourceToUse.name;
      }

      // Include context_mode for project step execution (reset/continue/summary)
      if (options.contextMode) {
        requestBody.context_mode = options.contextMode;
      }

      // Include allow_questions setting (default true - allows Claude to ask clarifying questions)
      requestBody.allow_questions = settings.taurus_allow_questions ?? true;

      // Include attached images for vision support (using captured data from before clear)
      if (imagesToSend) {
        requestBody.images = imagesToSend;
      }

      // Connect to standalone Taurus server on port 48002
      const response = await fetch(`${API_ENDPOINTS.taurus}/api/ask`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(requestBody),
        signal: abortController.signal
      });

      if (!response.ok) {
        // Handle error responses (429, 401, etc.)
        let errorMessage = `Error ${response.status}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (e) {
          // If response isn't JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = JSON.parse(line.slice(6));

          switch (data.type) {
            case 'metadata':
              // Initial metadata
              break;
            case 'session_id':
              // Commit session_id immediately - SDK saves session from the start
              // This allows resuming even if the request is interrupted
              console.log('[Taurus] Received session_id:', data.session_id);
              updateTabData(tabId, { taurusSessionId: data.session_id });
              break;
            case 'model':
              accumulatedResponse += `\n✅ Model: ${data.model} (max tokens: ${data.max_tokens})\n\n`;
              updateTabData(tabId, { taurusStreamingContent: accumulatedResponse });
              break;
            case 'system':
              accumulatedResponse += `✅ ${data.message}\n`;
              updateTabData(tabId, { taurusStreamingContent: accumulatedResponse });
              break;
            case 'status':
              accumulatedResponse += `${data.message}\n`;
              updateTabData(tabId, { taurusStreamingContent: accumulatedResponse });
              break;
            case 'text':
              // Character-by-character streaming
              accumulatedResponse += data.content || '';
              updateTabData(tabId, { taurusStreamingContent: accumulatedResponse });
              break;
            case 'tool':
              // Branded display for custom MCP tools
              let toolName = data.tool;
              let toolIcon = '🔧';

              // Check for execute_python (our safe Python REPL)
              if (toolName.includes('__execute_python')) {
                toolIcon = '🐍';
                toolName = 'Python';
              } else if (toolName.includes('__sql_query')) {
                toolIcon = '🗄️';
                toolName = 'SQL Query';
              } else if (toolName.includes('__schema')) {
                toolIcon = '📊';
                toolName = 'Schema Inspector';
              }

              // For Python/SQL with inline_code, skip tool indicator (collapsible IS the indicator)
              if (data.inline_code) {
                accumulatedResponse += data.inline_code;
              } else {
                // Standard tool indicator for other tools
                let toolMsg = `${toolIcon} Tool: ${toolName}\n`;

                // MCP tool preview
                if (data.preview) {
                  toolMsg += `   ${data.preview}\n`;
                }
                // Standard tool details
                else if (data.command) {
                  toolMsg += `   Command: ${data.command}\n`;
                } else if (data.file_path) {
                  toolMsg += `   File: ${data.file_path}\n`;
                  if (data.content_length) {
                    toolMsg += `   Length: ${data.content_length} characters\n`;
                  }
                } else if (data.pattern) {
                  toolMsg += `   Pattern: ${data.pattern}\n`;
                  if (data.path) {
                    toolMsg += `   Path: ${data.path}\n`;
                  }
                }
                toolMsg += '\n';
                accumulatedResponse += toolMsg;
              }

              updateTabData(tabId, { taurusStreamingContent: accumulatedResponse });
              break;
            case 'result':
              let resultMsg = `\n${'='.repeat(70)}\n✅ ${data.message || 'Task completed!'}\n`;
              if (data.model) resultMsg += `Model: ${data.model}\n`;
              if (data.duration) resultMsg += `Duration: ${data.duration}\n`;
              if (data.cost) resultMsg += `Cost: ${data.cost} 💰\n`;
              if (data.turns) resultMsg += `Turns: ${data.turns}\n`;
              resultMsg += '='.repeat(70) + '\n';
              accumulatedResponse += resultMsg;
              updateTabData(tabId, { taurusStreamingContent: accumulatedResponse });
              break;
            case 'image':
              // Collect image paths
              if (data.path) {
                imagePaths.push(data.path);
              }
              break;
            case 'question':
              // Claude is asking the user a clarifying question
              console.log('[Taurus] Question from Claude:', data.questions);
              accumulatedResponse += '\n❓ **Claude needs clarification:**\n';
              setTaurusQuestionModal({
                open: true,
                requestId: data.request_id,
                questions: data.questions || [],
                tabId: tabId
              });
              updateTabData(tabId, { taurusStreamingContent: accumulatedResponse });

              // Notify user if tab is not focused
              if (document.hidden) {
                // Change title to get attention
                document.title = '❓ Question - VerbaGPT';

                // Show browser notification if permitted
                if (Notification.permission === 'granted') {
                  new Notification('VerbaGPT needs your input', {
                    body: 'Claude has a clarifying question',
                    icon: '/favicon.ico',
                    tag: 'taurus-question' // Prevents duplicate notifications
                  });
                } else if (Notification.permission === 'default') {
                  // Request permission for next time (non-blocking)
                  Notification.requestPermission();
                }
              }
              break;
            case 'done':
              // Stream complete - exit loop to process results after stream closes
              break;
            case 'error':
              console.error('Taurus error:', data.message);
              // Close question modal if open
              setTaurusQuestionModal({ open: false, requestId: null, questions: [], tabId: null });
              // Preserve any partial content that was streamed before the error
              const partialContent = accumulatedResponse || '';
              let errorContent;
              if (partialContent) {
                errorContent = partialContent + '\n\n---\n❌ Taurus failed: ' + data.message;
              } else {
                errorContent = `❌ Taurus failed: ${data.message}`;
              }
              const errorMessage = {
                id: uuidv4(),
                type: 'bot',
                content: errorContent,
                investigationThread: partialContent || null,
                isTaurus: true, // Required for UI to show Investigation Thread section
                timestamp: new Date(),
                error: data.message,
                isPartialResult: !!partialContent
              };
              setTabs(currentTabs => currentTabs.map(t => {
                if (t.id !== tabId) return t;
                return {
                  ...t,
                  messages: [...t.messages, errorMessage],
                  isLoading: false,
                  taurusStreamingContent: ''
                  // Note: taurusRequestId preserved for retry with same REPL
                };
              }));
              return;
            case 'cancelled':
              console.log('Taurus cancelled:', data.message);
              // Close question modal if open
              setTaurusQuestionModal({ open: false, requestId: null, questions: [], tabId: null });
              // Don't add a message here - the cancelTaurusRequest function handles it
              // Just clean up state
              setTabs(currentTabs => currentTabs.map(t => {
                if (t.id !== tabId) return t;
                return {
                  ...t,
                  isLoading: false,
                  taurusStreamingContent: ''
                  // Note: taurusRequestId preserved for retry with same REPL
                };
              }));
              return;
          }
        }
      }

      // Stream complete - process results (mirrors Virgo behavior)
      // Extract plots from accumulated response (for backwards compatibility with old base64 format)
      const { plots: base64Plots, cleanContent } = extractTaurusPlots(accumulatedResponse);

      // Convert file paths to plot objects with timestamp for cache busting
      const plotTimestamp = Date.now();
      const filePlots = imagePaths.map(path => ({ type: 'file', path, timestamp: plotTimestamp }));

      // Combine base64 plots (if any) with file plots
      const allPlots = [...base64Plots, ...filePlots];

      // Auto-save removed - user saves intentionally via save button
      // dbMessageId is no longer set here (feedback/deep-review will need saved_conversations update)
      const dbMessageId = null;

      // Add completed message to chat with finalResponse flag for display
      const botMessage = {
        id: uuidv4(),
        type: 'bot',
        content: '',  // Empty - Investigation Thread shows full output, Taurus Review (if enabled) will populate this
        timestamp: new Date(),
        finalResponse: true,  // Required for message to display
        isTaurus: true,
        dbMessageId: dbMessageId,  // For feedback buttons
        plots: allPlots.length > 0 ? allPlots : undefined,  // Add plots if any found
        investigationThread: accumulatedResponse,  // Full streaming content (includes inline tool code blocks)
        responseTime: Date.now() - userStartTime,  // Track response time (ms)
        taurusRequestId: requestId  // Preserved for deep review to access same visuals folder
      };
      setTabs(currentTabs => currentTabs.map(t => {
        if (t.id !== tabId) return t;
        return {
          ...t,
          messages: [...t.messages, botMessage],
          isLoading: false,
          taurusStreamingContent: ''
          // Note: taurusRequestId is preserved for REPL variable persistence across follow-up messages
          // Note: taurusSessionId is committed immediately when received (not here)
        };
      }));

      // Check if Taurus Review is enabled
      if (settings.use_taurus_review) {
        // If there are plots, set pending state and wait for images to load
        // Otherwise, trigger review immediately
        if (allPlots.length > 0) {
          setPendingTaurusReview({
            investigationThread: accumulatedResponse,
            tabId: tabId,
            plots: allPlots,
            consoleOutput: accumulatedResponse,
            dbMessageId: dbMessageId
          });
        } else {
          // No plots - trigger review immediately
          callTaurusReview(
            accumulatedResponse,
            tabId,
            [],
            accumulatedResponse,
            dbMessageId,
            null
          );
        }
      }

    } catch (error) {
      // Close question modal if open
      setTaurusQuestionModal({ open: false, requestId: null, questions: [], tabId: null });

      // If request was aborted (user cleared conversation or clicked stop), silently exit
      if (error.name === 'AbortError') {
        console.log('[Taurus] Request aborted - fetch terminated cleanly');
        return;
      }

      console.error('Taurus workflow error:', error);

      // Check if this is a network interruption (sleep/suspend)
      const isNetworkError = error.message?.toLowerCase().includes('network error') ||
        error.message?.toLowerCase().includes('failed to fetch') ||
        error.name === 'TypeError';

      // Use accumulatedResponse directly (declared before try block)
      // Don't read from tabs state - it's stale due to React closure
      const partialContent = accumulatedResponse || '';

      // Build appropriate error message and user-friendly display text
      let errorContent;
      let errorDisplayText; // Shown in the red error box
      if (isNetworkError && partialContent) {
        // Network error with partial results - show friendly message
        errorContent = '\n\n---\n⚠️ Connection interrupted. Partial results shown above.';
        errorDisplayText = 'Connection interrupted (computer may have gone to sleep). Your partial results are preserved above.';
      } else if (isNetworkError) {
        // Network error with no content
        errorContent = '⚠️ Connection interrupted. Please check your network and retry.';
        errorDisplayText = 'Connection interrupted. Please check your network connection and try again.';
      } else if (error.message?.includes('Daily credit limit') || error.message?.includes('Error 429')) {
        // Credit limit error
        errorContent = `❌ ${error.message}`;
        errorDisplayText = error.message;
      } else {
        // Other errors - show server hint
        errorContent = `❌ Error: ${error.message}\n\nMake sure the Taurus server is running on port 48002.`;
        errorDisplayText = `${error.message}. Make sure the Taurus server is running on port 48002.`;
      }

      // If we have partial content, preserve it and append error
      const finalContent = partialContent ? partialContent + errorContent : errorContent;

      const errorMessage = {
        id: uuidv4(),
        type: 'bot',
        content: finalContent,
        investigationThread: partialContent || null, // Preserve for display
        isTaurus: true, // Required for UI to show Investigation Thread section
        timestamp: new Date(),
        error: errorDisplayText, // User-friendly error text for red box
        isPartialResult: !!partialContent
      };

      setTabs(currentTabs => currentTabs.map(t => {
        if (t.id !== tabId) return t;
        return {
          ...t,
          messages: [...t.messages, errorMessage],
          isLoading: false,
          taurusStreamingContent: ''
          // Note: taurusRequestId preserved for retry with same REPL
        };
      }));
    } finally {
      // Clean up abort controller
      delete taurusAbortControllersRef.current[tabId];

      // Refresh credit status after query is sent (counted against limit)
      if (isAuthenticated) {
        fetchCreditStatus();
      }
    }
  };

  // Submit user answers to Claude's clarifying questions
  const submitTaurusAnswer = async (requestId, answers) => {
    try {
      const response = await fetch(`${API_ENDPOINTS.taurus}/api/answer/${requestId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers })
      });

      if (!response.ok) {
        throw new Error(`Failed to submit answer: ${response.status}`);
      }

      console.log('[Taurus] Answer submitted successfully');

      // Close the modal and restore title
      setTaurusQuestionModal({ open: false, requestId: null, questions: [], tabId: null });
      document.title = 'VerbaGPT';

      return true;
    } catch (error) {
      console.error('[Taurus] Error submitting answer:', error);
      showNotification('error', 'Failed to submit answer: ' + error.message);
      return false;
    }
  };

  // Cancel Taurus request (Stop button)
  const cancelTaurusRequest = async (tabId = activeTabId) => {
    // Use ref for immediate abort (ref is always current, unlike state)
    const taurusRef = taurusAbortControllersRef.current[tabId];
    const tab = tabs.find(t => t.id === tabId);

    // Need either ref or tab state to proceed
    if (!taurusRef && (!tab || !tab.taurusRequestId)) return;

    try {
      // Abort fetch immediately
      if (taurusRef) {
        taurusRef.controller.abort();
      }

      // Also tell backend to stop (use ref's requestId if available, fall back to tab state)
      const requestId = taurusRef?.requestId || tab?.taurusRequestId;
      if (requestId) {
        fetch(`${API_ENDPOINTS.taurus}/api/cancel/${requestId}`, { method: 'POST' }).catch(() => { });
      }

      console.log('Taurus request cancelled by user');

      // Extract plots from accumulated response before adding cancellation message
      const accumulatedContent = tab.taurusStreamingContent || '';
      const { plots } = extractTaurusPlots(accumulatedContent);

      // Minimal content display, Investigation Thread shows full streaming content (no duplication)
      const cancelledMessage = {
        id: uuidv4(),
        type: 'bot',
        content: '⏸️ **Processing stopped by user**',
        timestamp: new Date(),
        finalResponse: true,
        isTaurus: true,
        plots: plots.length > 0 ? plots : undefined,
        investigationThread: accumulatedContent
      };

      // Update tab state
      setTabs(currentTabs => currentTabs.map(t => {
        if (t.id !== tabId) return t;
        return {
          ...t,
          messages: [...t.messages, cancelledMessage],
          isLoading: false,
          taurusStreamingContent: ''
          // Note: taurusRequestId preserved for retry with same REPL
        };
      }));
    } catch (error) {
      console.error('Failed to cancel Taurus request:', error);
    }
  };

  // Test Taurus LLM provider configuration (OpenRouter integration test)
  const testTaurusProvider = async () => {
    setTaurusProviderTest({ status: 'testing', message: 'Testing provider...' });

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setTaurusProviderTest({ status: 'error', message: 'Not authenticated' });
        return;
      }

      // Send current UI selections (not saved settings) so user can test before saving
      const response = await fetch(`${API_ENDPOINTS.taurus}/api/test-provider`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Backend-URL': API_ENDPOINTS.main,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          provider: settings.taurus_llm_provider || 'anthropic',
          model: settings.taurus_openrouter_model || 'google/gemini-2.0-flash-001',
          api_key: settings.taurus_openrouter_key || ''
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        setTaurusProviderTest({
          status: 'success',
          message: data.latency_ms > 0 ? `Connected (${data.latency_ms}ms)` : 'Configured'
        });
      } else if (data.status === 'warning') {
        setTaurusProviderTest({
          status: 'warning',
          message: data.message || 'Model may not support tool calling'
        });
      } else {
        setTaurusProviderTest({
          status: 'error',
          message: data.error || 'Provider test failed'
        });
      }
    } catch (error) {
      setTaurusProviderTest({
        status: 'error',
        message: `Connection failed: ${error.message}`
      });
    }
  };

  // Fetch OpenRouter models with pricing (for Taurus settings dropdown)
  const fetchOpenRouterModels = async (forceRefresh = false) => {
    setLoadingOpenRouterModels(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('[OpenRouter] Not authenticated, skipping model fetch');
        return;
      }

      const url = forceRefresh
        ? `${API_ENDPOINTS.taurus}/api/openrouter-models?refresh=true`
        : `${API_ENDPOINTS.taurus}/api/openrouter-models`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Backend-URL': API_ENDPOINTS.main
        }
      });

      const data = await response.json();
      if (data.status === 'ok' && data.models) {
        setOpenRouterModels(data.models);
        console.log(`[OpenRouter] Loaded ${data.models.length} models with pricing`);
      }
    } catch (error) {
      console.error('[OpenRouter] Failed to fetch models:', error);
    } finally {
      setLoadingOpenRouterModels(false);
    }
  };

  // Send message function
  const sendMessage = async (text, tabId = activeTabId) => {
    if (!text.trim()) return;

    const tab = tabs.find(t => t.id === tabId);
    if (!tab || tab.type !== TAB_TYPES.CHAT) return;

    // Check authentication requirements
    if (!isAuthenticated && !demoMode) {
      setAuthModalOpen(true);
      return;
    }

    const userMessage = {
      id: uuidv4(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    // Update tab with new message, loading state, and title (if first message)
    const updatedMessages = [...tab.messages, userMessage];
    const updates = {
      messages: updatedMessages,
      isLoading: true,
      inputMessage: ''
    };

    // Update title if this is the first user message
    if (tab.messages.length === 0 || tab.title === 'New Chat') {
      updates.title = generateTabTitle(updatedMessages);
    }

    updateTabData(tabId, updates);

    // Track query submission
    trackQuerySubmit(tab.mode || chatMode);

    // Add temporary bot message
    const tempBotId = uuidv4();
    const tempBotMessage = {
      id: tempBotId,
      type: 'bot',
      content: 'Starting analysis...',
      timestamp: new Date(),
      isTemporary: true
    };

    updateTabData(tabId, {
      messages: [...tab.messages, userMessage, tempBotMessage]
    });

    try {
      const token = localStorage.getItem('token');
      const headers = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(`${API_ENDPOINTS.main}/chat-stream`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          session_id: tab.sessionId,
          message: text,
          model: settings.model,
          base_model: settings.baseModel,
          multimodal_model: settings.openrouter_multimodal,
          is_demo: demoMode
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let currentBotMessage = {
        id: uuidv4(),
        type: 'bot',
        content: '',
        timestamp: new Date(),
        generated_code: '',
        execution_output: '',
        plots: [],
        error: '',
        status: '',
        finalResponse: false,
        prompt_data: null
      };

      // Replace temp message with real bot message
      setTabs(currentTabs => {
        const updatedTabs = currentTabs.map(tab => {
          if (tab.id === tabId) {
            const messagesWithoutTemp = tab.messages.filter(msg => msg.id !== tempBotId);
            return { ...tab, messages: [...messagesWithoutTemp, currentBotMessage] };
          }
          return tab;
        });
        return updatedTabs;
      });

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('SSE Data received:', data);
              console.log('Data type:', data.type);

              if (data.type === 'status' && data.message) {
                console.log('Status received:', data.message);
                currentBotMessage.status = data.message;
                // Don't overwrite final response with status
                if (!currentBotMessage.finalResponse) {
                  currentBotMessage.content = data.message;
                }
              } else if (data.type === 'final_error') {
                console.log('Final error received:', data.error);
                currentBotMessage.content = `I apologize, but I encountered an issue after multiple attempts: ${data.error}. Please try your query again in a moment.`;
                currentBotMessage.error = data.error;
                currentBotMessage.finalResponse = true; // Mark as final to stop spinner
                delete currentBotMessage.status; // Clear any status messages
              } else if (data.type === 'openrouter_error') {
                console.log('OpenRouter error received:', data.error);
                // For non-final attempts, show as status. Final attempts are handled by final_error
                if (data.message) {
                  currentBotMessage.status = data.message;
                  if (!currentBotMessage.finalResponse) {
                    currentBotMessage.content = data.message;
                  }
                }
              } else if (data.type === 'llm_error') {
                console.log('LLM error received:', data.error);
                // For non-final attempts, show as status. Final attempts are handled by error type
                currentBotMessage.status = `Error occurred during attempt: ${data.error}`;
                if (!currentBotMessage.finalResponse) {
                  currentBotMessage.content = `Error occurred during attempt: ${data.error}`;
                }
              } else if (data.type === 'error') {
                console.log('General error received:', data.error);
                currentBotMessage.content = `I apologize, but I encountered an error: ${data.error}. Please try your query again.`;
                currentBotMessage.error = data.error;
                currentBotMessage.finalResponse = true; // Mark as final to stop spinner
                delete currentBotMessage.status; // Clear any status messages
              } else if (data.type === 'failure') {
                console.log('Failure after retries:', data);
                currentBotMessage.content = data.response || 'Query failed after multiple attempts.';
                currentBotMessage.generated_code = data.generated_code || '';
                currentBotMessage.execution_output = data.execution_result?.output || '';
                currentBotMessage.error = data.execution_result?.error || 'Execution failed';
                currentBotMessage.finalResponse = true; // Mark as final to stop spinner
                delete currentBotMessage.status; // Clear any status messages
              } else if (data.status) {
                console.log('Legacy status received:', data.status);
                currentBotMessage.status = data.status;
                // Don't overwrite final response with status
                if (!currentBotMessage.finalResponse) {
                  currentBotMessage.content = data.status;
                }
              }
              if (data.response) {
                console.log('Final response received:', data.response);
                currentBotMessage.content = data.response;
                currentBotMessage.finalResponse = true;
                delete currentBotMessage.status; // Clear status once we have final response
                delete currentBotMessage.error; // Clear any previous error since we got a successful response
              }
              if (data.generated_code) {
                currentBotMessage.generated_code = data.generated_code;
              }
              if (data.execution_output) {
                currentBotMessage.execution_output = data.execution_output;
                // Auto-expand and select Output tab when output is available
                setExpandedCodeOutput(prev => ({
                  ...prev,
                  [currentBotMessage.id]: true
                }));
                setActiveCodeTab(prev => ({
                  ...prev,
                  [currentBotMessage.id]: 'output'
                }));
              }
              if (data.plots) {
                currentBotMessage.plots = data.plots;
                // Auto-expand and select Output tab when plots are available
                setExpandedCodeOutput(prev => ({
                  ...prev,
                  [currentBotMessage.id]: true
                }));
                setActiveCodeTab(prev => ({
                  ...prev,
                  [currentBotMessage.id]: 'output'
                }));
              }
              if (data.prompt_data) {
                console.log('Prompt data received:', data.prompt_data);
                currentBotMessage.prompt_data = data.prompt_data;
              }
              if (data.id) {
                console.log('Database message ID received:', data.id);
                currentBotMessage.dbMessageId = data.id; // Store the database ID
              }
              if (data.error) {
                currentBotMessage.error = data.error;
              }

              // Update the tab with the current message
              setTabs(currentTabs => {
                const updatedTabs = currentTabs.map(tab => {
                  if (tab.id === tabId) {
                    const updatedMessages = tab.messages.map(msg =>
                      msg.id === currentBotMessage.id ? { ...currentBotMessage } : msg
                    );
                    return { ...tab, messages: updatedMessages };
                  }
                  return tab;
                });
                return updatedTabs;
              });
            } catch (e) {
              console.error('Error parsing SSE data:', e);
            }
          }
        }
      }

    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: uuidv4(),
        type: 'bot',
        content: `Error: ${error.message}`,
        timestamp: new Date(),
        error: error.message
      };

      setTabs(currentTabs => {
        const updatedTabs = currentTabs.map(tab => {
          if (tab.id === tabId) {
            const messagesWithoutTemp = tab.messages.filter(msg => msg.id !== tempBotId);
            return { ...tab, messages: [...messagesWithoutTemp, errorMessage] };
          }
          return tab;
        });
        return updatedTabs;
      });
    } finally {
      updateTabData(tabId, { isLoading: false });
    }
  };

  // Get model label helper
  const getModelLabel = (modelValue) => {
    if (!modelValue) return 'Default';
    if (modelValue.includes('claude')) return 'Claude';
    if (modelValue.includes('gpt')) return 'GPT';
    if (modelValue.includes('qwen')) return 'Qwen';
    return modelValue.split('/').pop() || 'Unknown';
  };

  // Execute code function
  const executeCode = async (messageId, code) => {
    console.log('ðŸ”§ executeCode called:', { messageId, codeLength: code?.length });
    userTriggeredCodeRef.current = true;
    try {
      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }

      console.log('ðŸš€ Sending code execution request to:', `${API_ENDPOINTS.main}/execute-code`);
      const response = await axios.post(`${API_ENDPOINTS.main}/execute-code`, {
        code: code,
        session_id: activeTab?.sessionId || 'default'
      }, { headers });

      console.log('âœ… Code execution response:', response.data);
      if (response.data.success) {
        // Update the message with execution results
        setTabs(currentTabs => {
          const updatedTabs = currentTabs.map(tab => {
            if (tab.id === activeTabId) {
              const updatedMessages = tab.messages.map(msg => {
                if (msg.id === messageId) {
                  return {
                    ...msg,
                    generated_code: code, // Update with the executed code (edited or original)
                    execution_output: response.data.output,
                    plots: response.data.plots || [],
                    error: response.data.error || ''
                  };
                }
                return msg;
              });
              return { ...tab, messages: updatedMessages };
            }
            return tab;
          });
          return updatedTabs;
        });

        // Auto-expand and select Output tab when output or plots are available
        if (response.data.output || (response.data.plots && response.data.plots.length > 0)) {
          setExpandedCodeOutput(prev => ({
            ...prev,
            [messageId]: true
          }));
          setActiveCodeTab(prev => ({
            ...prev,
            [messageId]: 'output'
          }));
        }

        // Exit edit mode
        setEditingCode(prev => ({
          ...prev,
          [messageId]: false
        }));
      }
    } catch (error) {
      console.error('Code execution error:', error);
      // Show error to user
      setTabs(currentTabs => {
        const updatedTabs = currentTabs.map(tab => {
          if (tab.id === activeTabId) {
            const updatedMessages = tab.messages.map(msg => {
              if (msg.id === messageId) {
                return {
                  ...msg,
                  error: error.response?.data?.detail || 'Execution failed'
                };
              }
              return msg;
            });
            return { ...tab, messages: updatedMessages };
          }
          return tab;
        });
        return updatedTabs;
      });
    }
  };

  // Helper function to download plot image
  const downloadPlot = async (plotSrc, index) => {
    try {
      // Check if it's a cross-origin URL (e.g., Taurus server)
      const isCrossOrigin = plotSrc.startsWith('http') && !plotSrc.startsWith(window.location.origin);

      if (isCrossOrigin) {
        // Fetch the image and convert to blob for cross-origin downloads
        const response = await fetch(plotSrc);
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = `visualization_${index + 1}_${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up blob URL
        setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
      } else {
        // Same-origin or data URL - use direct download
        const link = document.createElement('a');
        link.href = plotSrc;
        link.download = `visualization_${index + 1}_${Date.now()}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error('Error downloading plot:', error);
      // Fallback: open in new tab
      window.open(plotSrc, '_blank');
    }
  };

  // Helper function to download all plots from a message
  const downloadAllPlots = async (message) => {
    if (!message.plots || message.plots.length === 0) return;

    for (let i = 0; i < message.plots.length; i++) {
      const plot = message.plots[i];
      let plotSrc = '';

      if (typeof plot === 'string') {
        if (plot.startsWith('blob:') || plot.startsWith('data:') || plot.startsWith('http')) {
          plotSrc = plot;
        } else {
          plotSrc = `data:image/png;base64,${plot}`;
        }
      } else if (plot && typeof plot === 'object') {
        if (plot.type === 'file' && plot.path) {
          // Taurus: File path from visuals folder
          // Add timestamp for cache busting
          const cacheBuster = plot.timestamp || Date.now();
          plotSrc = `${API_ENDPOINTS.taurus}/api/files/view?path=${encodeURIComponent(plot.path)}&t=${cacheBuster}`;
        } else if (plot.url) {
          plotSrc = plot.url;
        } else if (plot.data) {
          plotSrc = plot.data.startsWith('data:') ? plot.data : `data:image/png;base64,${plot.data}`;
        }
      }

      if (plotSrc) {
        await downloadPlot(plotSrc, i);
        // Small delay between downloads to avoid browser blocking
        if (i < message.plots.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 300));
        }
      }
    }
  };

  // Render chat tab content
  // Helper function to render plot images
  const renderPlotImage = (plot, index, message = null, isLastPlot = false) => {
    let plotSrc = '';

    if (typeof plot === 'string') {
      // Handle blob URLs, data URLs, or raw base64
      if (plot.startsWith('blob:') || plot.startsWith('data:') || plot.startsWith('http')) {
        plotSrc = plot;
      } else {
        // Raw base64 string
        plotSrc = `data:image/png;base64,${plot}`;
      }
    } else if (plot && typeof plot === 'object') {
      if (plot.type === 'file' && plot.path) {
        // Taurus: File path from visuals folder
        // Add timestamp for cache busting (file may be overwritten in follow-up questions)
        const cacheBuster = plot.timestamp || Date.now();
        plotSrc = `${API_ENDPOINTS.taurus}/api/files/view?path=${encodeURIComponent(plot.path)}&t=${cacheBuster}`;
      } else if (plot.url) {
        // New format: Object with url (for display) and data (for review)
        plotSrc = plot.url;
      } else {
        // Legacy Virgo: Object with base64 data property
        const plotData = plot.data || plot.base64 || plot.image || plot;
        if (typeof plotData === 'string') {
          if (plotData.startsWith('blob:') || plotData.startsWith('data:') || plotData.startsWith('http')) {
            plotSrc = plotData;
          } else {
            plotSrc = `data:image/png;base64,${plotData}`;
          }
        }
      }
    }

    // Handler for when image finishes loading
    const handleImageLoad = () => {
      // Only scroll and trigger review for the last plot in a message
      if (isLastPlot) {
        scrollToBottom();

        // If this is a Taurus message with pending review, trigger it now
        if (message && message.isTaurus && pendingTaurusReview) {
          callTaurusReview(
            pendingTaurusReview.investigationThread,
            pendingTaurusReview.tabId,
            pendingTaurusReview.plots,
            pendingTaurusReview.consoleOutput,
            pendingTaurusReview.dbMessageId,
            null
          );
          setPendingTaurusReview(null);
        }
      }
    };

    return plotSrc ? (
      <div className="relative group">
        <img
          src={plotSrc}
          alt={`Plot ${index + 1}`}
          className="max-w-full h-auto"
          onLoad={handleImageLoad}
          onError={(e) => {
            console.error('Image failed to load');
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <button
          onClick={() => downloadPlot(plotSrc, index)}
          className="absolute top-2 right-2 bg-white/90 hover:bg-white text-gray-700 p-2 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
          title="Download visualization"
        >
          <Download className="h-4 w-4" />
        </button>
      </div>
    ) : (
      <div className="text-red-400 text-sm">No valid plot data found</div>
    );
  };

  const renderChatTab = (tab) => (
    <div
      className="bg-white dark:bg-gray-900 relative flex flex-col"
      style={{ height: '100%', minHeight: 0 }}
    >
      {/* Chat Messages */}
      <div
        ref={chatScrollRef}
        onScroll={handleChatScroll}
        className="overflow-y-auto p-4 sm:p-6 space-y-4"
        style={{ flex: '1 1 0', minHeight: 0 }}
      >
        {tab.messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            {tab.mode === 'taurus' ? (
              // Taurus mode welcome message
              <>
                {!taurusAvailable ? (
                  // Taurus not running - show installation instructions
                  <>
                    <Mountain className="h-16 w-16 text-gray-400 mb-4" />
                    <h3 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">Taurus Mode</h3>
                    <div className="bg-amber-50 dark:bg-amber-900/20 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-6 max-w-2xl">
                      <p className="text-gray-700 dark:text-gray-300 mb-4 text-lg">
                        <span className="font-semibold">Taurus Server Not Running</span>
                      </p>
                      <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Taurus mode provides an advanced AI assistant powered by Claude Code SDK with multi-step reasoning, Python execution, and direct database access.
                      </p>
                      <p className="text-gray-600 dark:text-gray-400 mb-6">
                        To use Taurus mode, install the lightweight Taurus server on your local machine.
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3">
                        <button
                          onClick={() => setVideoModalOpen(true)}
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          Watch Install Guide (5 min)
                        </button>
                        <button
                          onClick={() => setTaurusInstallModalOpen(true)}
                          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg transition-colors"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                          Get Started - Download Taurus
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // Taurus running - ready to use
                  <>
                    <Mountain className="h-16 w-16 text-amber-600 mb-4" />
                    <h3 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">Welcome to Taurus</h3>

                    {/* Incognito Mode Indicator */}
                    {settings.taurus_incognito && (
                      <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 text-xs mb-3" title="Conversations won't be saved to chat history">
                        <EyeOff className="h-3.5 w-3.5" />
                        <span>Incognito</span>
                      </div>
                    )}

                    {/* Datasource Selector */}
                    <div className="mb-3">
                      {taurusSelectedDatasource ? (
                        // Datasource selected - show with change/remove options
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-2">
                          <div className="flex items-center justify-between">
                            <div className="text-sm">
                              <span className="text-gray-600 dark:text-gray-400">Active Database: </span>
                              <span className="font-semibold text-gray-800 dark:text-gray-200">{taurusSelectedDatasource.name}</span>
                              <span className="text-gray-500 dark:text-gray-500 ml-2">({formatDbType(taurusSelectedDatasource.db_type)})</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setDatasourceModalMode('taurus');
                                  setShowDatasourceModal(true);
                                }}
                                className="text-xs px-2 py-1 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded transition-colors"
                              >
                                Change
                              </button>
                              <button
                                onClick={() => handleRemoveTaurusDatasource(taurusSelectedDatasource?.name)}
                                className="text-xs px-2 py-1 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        // No datasource selected - show add button
                        <button
                          onClick={() => {
                            setDatasourceModalMode('taurus');
                            setShowDatasourceModal(true);
                          }}
                          className="w-full px-4 py-3 bg-blue-50 dark:bg-blue-900/20 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors font-medium flex items-center justify-center gap-2"
                        >
                          <Database className="h-4 w-4" />
                          Add your saved datasource
                        </button>
                      )}
                    </div>

                    {/* File Management Buttons */}
                    <div className="mb-3">
                      <div className="grid grid-cols-2 gap-2">
                        {/* Open Files Folder in Explorer */}
                        <button
                          onClick={async () => {
                            try {
                              const response = await fetch(`${API_ENDPOINTS.taurus}/api/files/open-folder`, {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ path: '' }) // Defaults to files directory
                              });

                              if (response.ok) {
                                showNotification('success', 'Files folder opened in Explorer');
                              } else {
                                showNotification('error', 'Failed to open folder. Is Taurus running?');
                              }
                            } catch (error) {
                              console.error('Failed to open files folder:', error);
                              showNotification('error', 'Could not open folder. Make sure Taurus server is running.');
                            }
                          }}
                          className="px-3 py-3 bg-green-50 dark:bg-green-900/20 border-2 border-dashed border-green-300 dark:border-green-700 rounded-lg text-green-700 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <FolderOpen className="h-4 w-4" />
                          Open Folder
                        </button>

                        {/* Browse Files in Panel */}
                        <button
                          onClick={() => {
                            setFilePanelPath(''); // Reset to files directory
                            setShowFilePanel(true);
                          }}
                          className="px-3 py-3 bg-blue-50 dark:bg-blue-900/20 border-2 border-dashed border-blue-300 dark:border-blue-700 rounded-lg text-blue-700 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                        >
                          <FolderTree className="h-4 w-4" />
                          Browse Files
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1 text-center">
                        Add data files for Taurus to analyze
                      </p>
                    </div>

                    <p className="text-gray-600 dark:text-gray-400 mb-3 max-w-2xl">
                      Powered by Claude Code, Taurus is a free-form AI assistant with access to your local filesystem, Python execution, and database tools.
                    </p>

                    <details className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 max-w-2xl text-left mb-4">
                      <summary className="font-medium text-gray-800 dark:text-gray-200 cursor-pointer flex items-center gap-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                        <Info className="h-4 w-4 text-blue-600" />
                        Getting Started with Taurus
                      </summary>
                      <ul className="text-sm text-gray-700 dark:text-gray-300 space-y-2 ml-6 list-disc mt-3">
                        <li>
                          <span className="font-medium">Sign in to Claude Code:</span> You must be authenticated in Claude Code.{' '}
                          <a href="https://docs.claude.com/en/docs/claude-code/setup" target="_blank" rel="noopener noreferrer" className="text-blue-600 dark:text-blue-400 hover:underline">
                            Installation guide
                          </a>
                        </li>
                        <li>
                          <span className="font-medium">Configure database:</span> Add your datasource connections in Settings (uses your active datasource automatically).
                        </li>
                        <li>
                          <span className="font-medium">In your prompts:</span> Reference your database by name (e.g., "Query my MEPS database...") and Claude will use your configured connection.
                        </li>
                      </ul>
                    </details>

                    <p className="text-sm text-gray-500 dark:text-gray-500 max-w-2xl">
                      Ask Claude about your data, run Python code, analyze files, or any programming task.
                    </p>
                  </>
                )}
              </>
            ) : (
              // Virgo mode welcome message
              <>
                <Sparkles className="h-16 w-16 text-purple-600 mb-4" />
                <h3 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-3">Welcome to Virgo</h3>
                {/* Query Context Summary */}
                <div data-tour="active-datasource" className="mb-3">
                  <button
                    onClick={() => setShowDataSourcesPanel(true)}
                    className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg px-4 py-2 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors w-full max-w-md"
                  >
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 text-sm flex-wrap">
                        <span className="text-gray-600 dark:text-gray-400">Query context:</span>
                        <div className="flex items-center gap-2">
                          {includeDatabase && activeDatasource && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300 rounded text-xs font-medium">
                              <Database className="w-3 h-3" />
                              {activeDatasource.name}
                            </span>
                          )}
                          {selectedFiles.length > 0 && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-800/50 text-green-700 dark:text-green-300 rounded text-xs font-medium">
                              <FileText className="w-3 h-3" />
                              {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''}
                            </span>
                          )}
                          {!includeDatabase && selectedFiles.length === 0 && !activeDatasource && (
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              No data sources selected
                            </span>
                          )}
                          {!includeDatabase && !activeDatasource && selectedFiles.length === 0 && (
                            <span className="text-gray-500 dark:text-gray-400 text-xs">
                              Click to configure
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs px-2 py-1 text-purple-600 dark:text-purple-400">
                        Configure
                      </span>
                    </div>
                  </button>
                </div>
                {/* Description and sample prompts - varies based on context */}
                {(() => {
                  const hasDatabase = includeDatabase && activeDatasource;
                  const hasFiles = selectedFiles.length > 0;

                  // Determine description based on context
                  let description;
                  if (hasDatabase && hasFiles) {
                    description = "Ask questions about your database and files. I'll analyze them together.";
                  } else if (hasDatabase) {
                    description = samplePromptsDescription || "Ask questions about your data and I'll help you analyze it.";
                  } else if (hasFiles) {
                    // Use demo file description if available
                    const fileConfig = getFilePromptsForSelection(selectedFiles);
                    description = fileConfig.description || `Ask questions about your ${selectedFiles.length} selected file${selectedFiles.length !== 1 ? 's' : ''}.`;
                  } else {
                    description = "Configure your data sources to get started.";
                  }

                  // Get file-specific prompts (demo files have custom prompts)
                  const filePromptsConfig = getFilePromptsForSelection(selectedFiles);

                  // Determine which prompts to show
                  const promptsToShow = hasDatabase ? samplePrompts : (hasFiles ? filePromptsConfig.prompts : []);
                  const showSamplePrompts = promptsToShow.length > 0;

                  return (
                    <>
                      <p className="text-gray-600 dark:text-gray-500 mb-2">{description}</p>
                      {showSamplePrompts && (
                        <p className="text-sm text-gray-500 dark:text-gray-600 mb-6">
                          Try one of these sample questions:
                        </p>
                      )}
                    </>
                  );
                })()}
                {/* Sample prompts carousel */}
                {(() => {
                  const hasDatabase = includeDatabase && activeDatasource;
                  const hasFiles = selectedFiles.length > 0;

                  // Get file-specific prompts (demo files have custom prompts)
                  const filePromptsConfig = getFilePromptsForSelection(selectedFiles);

                  // Determine which prompts to show
                  const promptsToShow = hasDatabase ? samplePrompts : (hasFiles ? filePromptsConfig.prompts : []);

                  if (promptsToShow.length === 0) {
                    return null;
                  }

                  const PROMPTS_PER_PAGE = window.innerWidth < 768 ? 3 : 6;
                  const totalPages = Math.ceil(promptsToShow.length / PROMPTS_PER_PAGE);
                  const startIndex = samplePromptsPage * PROMPTS_PER_PAGE;
                  const currentPrompts = promptsToShow.slice(startIndex, startIndex + PROMPTS_PER_PAGE);

                  return (
                    <div className="max-w-2xl w-full mt-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {currentPrompts.map((prompt, index) => (
                          <button
                            key={startIndex + index}
                            onClick={() => {
                              updateTabData(tab.id, { inputMessage: prompt });
                              if (chatMode === 'virgo') {
                                sendVirgoMessage(prompt, tab.id);
                              } else {
                                sendMessage(prompt, tab.id);
                              }
                            }}
                            className="p-3 text-left text-sm text-gray-700 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-700 hover:border-gray-400 dark:hover:border-gray-600 transition-colors"
                          >
                            <span className="line-clamp-2 md:line-clamp-none">{prompt}</span>
                          </button>
                        ))}
                      </div>

                      {/* Carousel controls - only show if more than 6 prompts */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-4 mt-4">
                          <button
                            onClick={() => setSamplePromptsPage(prev => Math.max(0, prev - 1))}
                            disabled={samplePromptsPage === 0}
                            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Previous"
                          >
                            <ChevronLeft className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                          </button>

                          {/* Page indicators (dots) */}
                          <div className="flex gap-2">
                            {Array.from({ length: totalPages }).map((_, index) => (
                              <button
                                key={index}
                                onClick={() => setSamplePromptsPage(index)}
                                className={`w-2 h-2 rounded-full transition-all ${index === samplePromptsPage
                                    ? 'bg-blue-600 dark:bg-blue-400 w-6'
                                    : 'bg-gray-300 dark:bg-gray-600 hover:bg-gray-400 dark:hover:bg-gray-500'
                                  }`}
                                title={`Page ${index + 1}`}
                              />
                            ))}
                          </div>

                          <button
                            onClick={() => setSamplePromptsPage(prev => Math.min(totalPages - 1, prev + 1))}
                            disabled={samplePromptsPage === totalPages - 1}
                            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Next"
                          >
                            <ChevronRight className="h-5 w-5 text-gray-700 dark:text-gray-300" />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </>
            )}
          </div>
        ) : (
          <>
            {/* Pre-compute answer indices for navigator (O(n) once vs O(n²) inline) */}
            {(() => {
              const answerIndexMap = new Map();
              let answerCount = 0;
              tab.messages.forEach((msg, idx) => {
                if (msg.type !== 'user' && msg.type !== 'system') {
                  answerIndexMap.set(idx, answerCount++);
                }
              });
              return tab.messages.map((message, index) => (
                message.type === 'system' ? (
                  // System message (datasource change notification)
                  <div key={message.id} className="flex justify-center my-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-full">
                      {message.content}
                    </div>
                  </div>
                ) : (
                  <div
                    key={message.id}
                    data-answer-index={message.type !== 'user' ? answerIndexMap.get(index) : undefined}
                    className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-4xl ${message.type === 'user' ? 'ml-12' : 'mr-12'}`}>
                      {message.type === 'user' ? (
                        <div className="flex items-start gap-2">
                          <button
                            onClick={() => copyToClipboard(message.content, message.id)}
                            className={`mt-1 p-1 transition-colors ${copiedMessageId === message.id
                                ? 'text-green-400'
                                : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
                              }`}
                            title={copiedMessageId === message.id ? 'Copied!' : 'Copy message'}
                          >
                            {copiedMessageId === message.id ? (
                              <Check className="h-4 w-4" />
                            ) : (
                              <Copy className="h-4 w-4" />
                            )}
                          </button>
                          <div className="bg-blue-600 text-white p-4 rounded-lg whitespace-pre-wrap">
                            {message.content}
                            {/* Attachment badge for images */}
                            {message.attachedImages && (
                              <div className="mt-2 pt-2 border-t border-blue-500 flex items-center gap-1.5 text-blue-200 text-xs">
                                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                </svg>
                                <span>
                                  {message.attachedImages.count} image{message.attachedImages.count > 1 ? 's' : ''} attached
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-4">

                          {/* Investigation Thread for Taurus messages */}
                          {message.isTaurus && message.investigationThread && (
                            <div className="mt-4 w-full">
                              <div className="w-full bg-gray-50 dark:bg-gray-900 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-700">
                                <div
                                  className="w-full px-4 py-2 bg-gray-200 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700 flex items-center justify-between cursor-pointer"
                                  onClick={() => setExpandedCodeOutput(prev => ({
                                    ...prev,
                                    [message.id]: prev[message.id] === undefined ? false : !prev[message.id]
                                  }))}
                                >
                                  <div className="flex items-center gap-2">
                                    <Code className="h-4 w-4" />
                                    <span className="text-sm font-medium">Investigation Thread</span>
                                    <span className="text-xs text-gray-600 dark:text-gray-400">({message.investigationThread.length} characters)</span>
                                  </div>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation(); setExpandedCodeOutput(prev => ({
                                        ...prev,
                                        [message.id]: prev[message.id] === undefined ? false : !prev[message.id]
                                      }));
                                    }}
                                    className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white transition-colors"
                                  >
                                    {expandedCodeOutput[message.id] !== false ? (
                                      <ChevronDown className="h-4 w-4" />
                                    ) : (
                                      <ChevronRight className="h-4 w-4" />
                                    )}
                                  </button>
                                </div>

                                {(expandedCodeOutput[message.id] !== false) && (
                                  <div className="relative p-4 text-sm text-gray-800 dark:text-gray-300 overflow-x-auto border-b border-gray-300 dark:border-gray-700">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        try {
                                          navigator.clipboard.writeText(message.investigationThread);
                                          setCopiedMessageId(message.id);
                                          setTimeout(() => setCopiedMessageId(null), 2000);
                                        } catch (err) {
                                          console.error('Failed to copy investigation thread:', err);
                                        }
                                      }}
                                      className="absolute top-2 right-2 p-1 rounded bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                                      title={copiedMessageId === message.id ? 'Copied!' : 'Copy investigation thread'}
                                    >
                                      {copiedMessageId === message.id ? (
                                        <Check className="h-4 w-4" />
                                      ) : (
                                        <Copy className="h-4 w-4" />
                                      )}
                                    </button>
                                    <div className="text-sm font-mono pr-10 text-gray-800 dark:text-gray-100">
                                      <TaurusContentRenderer
                                        content={message.investigationThread.replace(
                                          /__TAURUS_PLOT__([A-Za-z0-9+/=]+)/g,
                                          (match, base64) => `__TAURUS_PLOT__${base64.substring(0, 10)}... [visualization data truncated]`
                                        )}
                                        enableMarkdown={true}
                                      />
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Investigation Thread removed per preference; use Output only */}

                          {/* Investigation Thread - Clickable button that opens console modal */}
                          {message.consoleOutput && (
                            <div className="mt-4 w-full">
                              <button
                                onClick={() => {
                                  // Find the user message that came just before this bot message
                                  const messageIndex = activeTab.messages.findIndex(m => m.id === message.id);
                                  const userMessage = messageIndex > 0 ? activeTab.messages.slice(0, messageIndex).reverse().find(m => m.type === 'user') : null;

                                  setViewingHistoricalConsole({
                                    consoleOutput: message.consoleOutput,
                                    userQuestion: userMessage?.content || 'Previous Investigation'
                                  });
                                  setTerminalConsoleOpen(true);
                                }}
                                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg border border-gray-300 dark:border-gray-700 flex items-center justify-between transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <Terminal className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">Investigation Thread</span>
                                  <span className="text-xs text-gray-600 dark:text-gray-400">({message.consoleOutput.length} characters)</span>
                                </div>
                                <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                              </button>
                            </div>
                          )}

                          {/* Virgo Visualizations - separate panel, always visible */}
                          {message.isVirgo && message.plots && message.plots.length > 0 && (
                            <div className="mt-4 space-y-4">
                              {message.plots.map((plot, index) => (
                                <div key={index} className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                                  <div className="px-4 py-2 bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Visualization {index + 1}</span>
                                  </div>
                                  <div className="p-4 bg-white dark:bg-gray-900">
                                    {renderPlotImage(plot, index, message, index === message.plots.length - 1)}
                                    <div className="text-red-400 text-sm" style={{ display: 'none' }}>Image failed to load</div>
                                  </div>
                                </div>
                              ))}
                              {/* Chart storage notice */}
                              <div className="px-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                Charts are not stored. Please download them for reference later.
                              </div>
                            </div>
                          )}

                          {/* Error */}
                          {message.error && (
                            <div className="mt-4 p-3 bg-red-900/20 border border-red-700 rounded-lg">
                              <p className="text-red-400 text-sm">{message.error}</p>
                            </div>
                          )}

                          {/* Status Messages - shows during processing */}
                          {message.status && !message.finalResponse && (
                            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg">
                              <div className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 dark:border-blue-400 border-t-transparent"></div>
                                <span className="text-blue-800 dark:text-blue-200 text-sm">{message.status}</span>
                              </div>
                            </div>
                          )}

                          {/* Taurus plots - appear after Investigation Thread, before final summary */}
                          {message.isTaurus && message.plots && message.plots.length > 0 && (
                            <div className="mt-4 space-y-4">
                              {message.plots.map((plot, index) => (
                                <div key={index} className="bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                                  <div className="px-4 py-2 bg-gray-200 dark:bg-gray-700 border-b border-gray-300 dark:border-gray-600">
                                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Visualization {index + 1}</span>
                                  </div>
                                  <div className="p-4 bg-white dark:bg-gray-900">
                                    {renderPlotImage(plot, index, message, index === message.plots.length - 1)}
                                    <div className="text-red-400 text-sm" style={{ display: 'none' }}>Image failed to load</div>
                                  </div>
                                </div>
                              ))}
                              {/* Chart storage notice */}
                              <div className="px-2 text-xs text-gray-500 dark:text-gray-400 italic">
                                Charts will be overwritten for the next question. Please save or download them for reference.
                              </div>
                            </div>
                          )}

                          {/* Virgo Review Loading State - only for last message */}
                          {index === tab.messages.length - 1 && reviewLoading[activeTabId] && message.isVirgo && message.finalResponse && (
                            <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-300 dark:border-blue-700 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-600 dark:border-blue-400 border-t-transparent"></div>
                                <span className="text-blue-800 dark:text-blue-200 text-sm font-medium">
                                  Working on summarizing Virgo response...
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Virgo Review Streaming Content */}
                          {/* Show persisted reviewContent for older messages, or tab-level streaming for last message */}
                          {(message.reviewContent || (index === tab.messages.length - 1 && reviewContent[activeTabId])) && message.isVirgo && (
                            <div className="mt-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-5 rounded-lg border border-blue-200 dark:border-blue-700 shadow-sm">
                              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-blue-200 dark:border-blue-700">
                                <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                                  Virgo Summary
                                </span>
                              </div>
                              <div className="prose dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-800 dark:prose-p:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:text-gray-900 dark:prose-code:text-gray-100 prose-ul:text-gray-800 dark:prose-ul:text-gray-100 prose-ol:text-gray-800 dark:prose-ol:text-gray-100 prose-li:text-gray-800 dark:prose-li:text-gray-100 prose-table:text-gray-800 dark:prose-table:text-gray-100 text-gray-800 dark:text-gray-100">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    p: ({ children }) => <p className="text-gray-800 dark:text-gray-100 mb-3 leading-relaxed">{children}</p>,
                                    h1: ({ children }) => <h1 className="text-gray-900 dark:text-white text-2xl font-bold mb-4 mt-6">{children}</h1>,
                                    h2: ({ children }) => <h2 className="text-gray-900 dark:text-white text-xl font-bold mb-3 mt-5">{children}</h2>,
                                    h3: ({ children }) => <h3 className="text-gray-900 dark:text-white text-lg font-semibold mb-2 mt-4">{children}</h3>,
                                    h4: ({ children }) => <h4 className="text-gray-900 dark:text-white text-base font-semibold mb-2 mt-3">{children}</h4>,
                                    strong: ({ children }) => <strong className="text-gray-900 dark:text-white font-semibold">{children}</strong>,
                                    em: ({ children }) => <em className="text-gray-700 dark:text-gray-200">{children}</em>,
                                    ul: ({ children }) => <ul className="text-gray-800 dark:text-gray-100 list-disc list-inside mb-3 space-y-1">{children}</ul>,
                                    ol: ({ children }) => <ol className="text-gray-800 dark:text-gray-100 list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                                    li: ({ children }) => <li className="text-gray-800 dark:text-gray-100 mb-1">{children}</li>,
                                    table: ({ children }) => (
                                      <div className="overflow-x-auto my-4">
                                        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 border border-gray-300 dark:border-gray-600">
                                          {children}
                                        </table>
                                      </div>
                                    ),
                                    thead: ({ children }) => <thead className="bg-gray-100 dark:bg-gray-800">{children}</thead>,
                                    tbody: ({ children }) => <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">{children}</tbody>,
                                    tr: ({ children }) => <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">{children}</tr>,
                                    th: ({ children }) => <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider border-r border-gray-300 dark:border-gray-600 last:border-r-0">{children}</th>,
                                    td: ({ children }) => <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-700 last:border-r-0">{children}</td>,
                                    code: ({ node, inline, className, children, ...props }) => {
                                      const match = /language-(\w+)/.exec(className || '');
                                      const codeString = String(children).replace(/\n$/, '');

                                      return !inline && match ? (
                                        <div className="relative group my-3">
                                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                              onClick={() => {
                                                navigator.clipboard.writeText(codeString);
                                                setCopiedCodeId(codeString);
                                                setTimeout(() => setCopiedCodeId(null), 2000);
                                              }}
                                              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs flex items-center gap-1"
                                            >
                                              {copiedCodeId === codeString ? (
                                                <><Check className="h-3 w-3" /> Copied</>
                                              ) : (
                                                <><Copy className="h-3 w-3" /> Copy</>
                                              )}
                                            </button>
                                          </div>
                                          <SyntaxHighlighter
                                            style={vscDarkPlus}
                                            language={match[1]}
                                            PreTag="div"
                                            className="rounded-lg text-sm"
                                            {...props}
                                          >
                                            {codeString}
                                          </SyntaxHighlighter>
                                        </div>
                                      ) : (
                                        <code className="text-gray-900 dark:text-gray-100 bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                                          {children}
                                        </code>
                                      );
                                    }
                                  }}
                                >
                                  {message.reviewContent || reviewContent[activeTabId]}
                                </ReactMarkdown>
                              </div>
                              {/* Streaming spinner at bottom of review box - only for last message */}
                              {index === tab.messages.length - 1 && reviewLoading[activeTabId] && (
                                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-blue-200 dark:border-blue-700 text-xs text-blue-600 dark:text-blue-400">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  <span>Streaming...</span>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Taurus Review Loading State - only for last message */}
                          {index === tab.messages.length - 1 && reviewLoading[activeTabId] && message.isTaurus && message.finalResponse && (
                            <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
                              <div className="flex items-center gap-3">
                                <div className="animate-spin rounded-full h-5 w-5 border-2 border-amber-600 dark:border-amber-400 border-t-transparent"></div>
                                <span className="text-amber-800 dark:text-amber-200 text-sm font-medium">
                                  Working on summarizing Taurus response...
                                </span>
                              </div>
                            </div>
                          )}

                          {/* Taurus Review Streaming Content */}
                          {/* Show persisted reviewContent for older messages, or tab-level streaming for last message */}
                          {(message.reviewContent || (index === tab.messages.length - 1 && reviewContent[activeTabId])) && message.isTaurus && (
                            <div className="mt-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-5 rounded-lg border border-amber-200 dark:border-amber-700 shadow-sm">
                              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-amber-200 dark:border-amber-700">
                                <svg className="h-5 w-5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <span className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                                  Taurus Summary
                                </span>
                              </div>
                              <div className="prose dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-800 dark:prose-p:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:text-gray-900 dark:prose-code:text-gray-100 prose-ul:text-gray-800 dark:prose-ul:text-gray-100 prose-ol:text-gray-800 dark:prose-ol:text-gray-100 prose-li:text-gray-800 dark:prose-li:text-gray-100 prose-table:text-gray-800 dark:prose-table:text-gray-100 text-gray-800 dark:text-gray-100">
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  components={{
                                    p: ({ children }) => <p className="text-gray-800 dark:text-gray-100 mb-3 leading-relaxed">{children}</p>,
                                    h1: ({ children }) => <h1 className="text-gray-900 dark:text-white text-2xl font-bold mb-4 mt-6">{children}</h1>,
                                    h2: ({ children }) => <h2 className="text-gray-900 dark:text-white text-xl font-bold mb-3 mt-5">{children}</h2>,
                                    h3: ({ children }) => <h3 className="text-gray-900 dark:text-white text-lg font-semibold mb-2 mt-4">{children}</h3>,
                                    h4: ({ children }) => <h4 className="text-gray-900 dark:text-white text-base font-semibold mb-2 mt-3">{children}</h4>,
                                    strong: ({ children }) => <strong className="text-gray-900 dark:text-white font-semibold">{children}</strong>,
                                    em: ({ children }) => <em className="text-gray-700 dark:text-gray-200">{children}</em>,
                                    ul: ({ children }) => <ul className="text-gray-800 dark:text-gray-100 list-disc list-inside mb-3 space-y-1">{children}</ul>,
                                    ol: ({ children }) => <ol className="text-gray-800 dark:text-gray-100 list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                                    li: ({ children }) => <li className="text-gray-800 dark:text-gray-100 mb-1">{children}</li>,
                                    table: ({ children }) => (
                                      <div className="overflow-x-auto my-4">
                                        <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 border border-gray-300 dark:border-gray-600">
                                          {children}
                                        </table>
                                      </div>
                                    ),
                                    thead: ({ children }) => <thead className="bg-gray-100 dark:bg-gray-800">{children}</thead>,
                                    tbody: ({ children }) => <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">{children}</tbody>,
                                    tr: ({ children }) => <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">{children}</tr>,
                                    th: ({ children }) => <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider border-r border-gray-300 dark:border-gray-600 last:border-r-0">{children}</th>,
                                    td: ({ children }) => <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-700 last:border-r-0">{children}</td>,
                                    code: ({ node, inline, className, children, ...props }) => {
                                      const match = /language-(\w+)/.exec(className || '');
                                      const codeString = String(children).replace(/\n$/, '');

                                      return !inline && match ? (
                                        <div className="relative group my-3">
                                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                              onClick={() => {
                                                navigator.clipboard.writeText(codeString);
                                                setCopiedCodeId(codeString);
                                                setTimeout(() => setCopiedCodeId(null), 2000);
                                              }}
                                              className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs flex items-center gap-1"
                                            >
                                              {copiedCodeId === codeString ? (
                                                <><Check className="h-3 w-3" /> Copied</>
                                              ) : (
                                                <><Copy className="h-3 w-3" /> Copy</>
                                              )}
                                            </button>
                                          </div>
                                          <SyntaxHighlighter
                                            style={vscDarkPlus}
                                            language={match[1]}
                                            PreTag="div"
                                            className="rounded-lg text-sm"
                                            {...props}
                                          >
                                            {codeString}
                                          </SyntaxHighlighter>
                                        </div>
                                      ) : (
                                        <code className="text-gray-900 dark:text-gray-100 bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>
                                          {children}
                                        </code>
                                      );
                                    }
                                  }}
                                >
                                  {message.reviewContent || reviewContent[activeTabId]}
                                </ReactMarkdown>
                              </div>
                            </div>
                          )}

                          {/* Deep Review Loading Indicator with Console Output (Virgo and Taurus) */}
                          {deepReviewLoading[message.id] && (
                            <div className="mt-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-700 overflow-hidden">
                              <div className="p-4 flex items-center gap-3">
                                <div className="relative">
                                  <ShieldCheck className="h-5 w-5 text-purple-500 dark:text-purple-400" />
                                  <div className="absolute inset-0 animate-ping">
                                    <ShieldCheck className="h-5 w-5 text-purple-400 dark:text-purple-500 opacity-50" />
                                  </div>
                                </div>
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-purple-900 dark:text-purple-200">
                                    Deep Review in Progress
                                  </div>
                                  <div className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                                    Verifying calculations, checking for inconsistencies, and validating charts...
                                  </div>
                                </div>
                                <Loader2 className="h-4 w-4 text-purple-500 dark:text-purple-400 animate-spin" />
                              </div>
                              {/* Streaming console output during deep review */}
                              {deepReviewFindings[message.id] && (
                                <div
                                  className="border-t border-purple-200 dark:border-purple-700 bg-gray-900 dark:bg-gray-950 max-h-48 overflow-y-auto"
                                  ref={el => { if (el) el.scrollTop = el.scrollHeight; }}
                                >
                                  <pre className="p-3 text-xs text-green-400 font-mono whitespace-pre-wrap break-words">
                                    {deepReviewFindings[message.id]}
                                  </pre>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Deep Review Findings - Collapsible (Virgo and Taurus) */}
                          {(message.deepReviewFindings || deepReviewFindings[message.id]) && !deepReviewLoading[message.id] && (
                            <div className="mt-4 bg-gradient-to-br from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-lg border border-purple-200 dark:border-purple-700 shadow-sm overflow-hidden">
                              <button
                                onClick={() => setDeepReviewExpanded(prev => ({ ...prev, [message.id]: !prev[message.id] }))}
                                className="w-full px-4 py-3 flex items-center justify-between bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-150 dark:hover:bg-purple-900/40 transition-colors"
                              >
                                <div className="flex items-center gap-2">
                                  <ShieldCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                                  <span className="text-sm font-semibold text-purple-900 dark:text-purple-200">
                                    Deep Review Findings
                                  </span>
                                </div>
                                {deepReviewExpanded[message.id] !== false ? (
                                  <ChevronUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                )}
                              </button>

                              {deepReviewExpanded[message.id] !== false && (
                                <div className="p-5">
                                  <div className="prose dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-800 dark:prose-p:text-gray-100">
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      components={{
                                        p: ({ children }) => <p className="text-gray-800 dark:text-gray-100 mb-3 leading-relaxed">{children}</p>,
                                        h2: ({ children }) => <h2 className="text-gray-900 dark:text-white text-xl font-bold mb-3 mt-5">{children}</h2>,
                                        h3: ({ children }) => <h3 className="text-gray-900 dark:text-white text-lg font-semibold mb-2 mt-4">{children}</h3>,
                                        strong: ({ children }) => <strong className="text-gray-900 dark:text-white font-semibold">{children}</strong>,
                                        ul: ({ children }) => <ul className="text-gray-800 dark:text-gray-100 list-disc list-inside mb-3 space-y-1">{children}</ul>,
                                        ol: ({ children }) => <ol className="text-gray-800 dark:text-gray-100 list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                                        li: ({ children }) => <li className="text-gray-800 dark:text-gray-100 mb-1">{children}</li>,
                                        code: ({ children }) => <code className="text-gray-900 dark:text-gray-100 bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>,
                                        hr: () => <hr className="my-4 border-purple-200 dark:border-purple-700" />
                                      }}
                                    >
                                      {message.deepReviewFindings || deepReviewFindings[message.id]}
                                    </ReactMarkdown>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* LLM Analysis / Final Summary - appears LAST */}
                          {/* For Taurus: only show if Taurus Review is disabled. For Virgo: always show */}
                          {message.content && message.content.trim() && message.finalResponse &&
                            (!message.isTaurus || !settings.use_taurus_review) && (
                              <div className="mt-4 bg-gray-100 dark:bg-gray-800 p-4 rounded-lg border border-gray-300 dark:border-gray-700">
                                {message.isTaurus ? (
                                  // Taurus: Use markdown-lite renderer (preserves structure, handles bold/headers)
                                  <div
                                    className="whitespace-pre-wrap text-sm text-gray-800 dark:text-gray-100"
                                    dangerouslySetInnerHTML={{
                                      __html: renderTaurusMarkdown(formatDictResponse(message.content))
                                    }}
                                  />
                                ) : (
                                  // Virgo: Use full ReactMarkdown with GFM
                                  <div className="prose dark:prose-invert max-w-none prose-headings:text-gray-900 dark:prose-headings:text-white prose-p:text-gray-800 dark:prose-p:text-gray-100 prose-strong:text-gray-900 dark:prose-strong:text-white prose-code:text-gray-900 dark:prose-code:text-gray-100 prose-ul:text-gray-800 dark:prose-ul:text-gray-100 prose-ol:text-gray-800 dark:prose-ol:text-gray-100 prose-li:text-gray-800 dark:prose-li:text-gray-100 prose-table:text-gray-800 dark:prose-table:text-gray-100 text-gray-800 dark:text-gray-100">
                                    <ReactMarkdown
                                      remarkPlugins={[remarkGfm]}
                                      components={{
                                        p: ({ children }) => <p className="text-gray-800 dark:text-gray-100 mb-3 leading-relaxed whitespace-pre-wrap">{children}</p>,
                                        h1: ({ children }) => <h1 className="text-gray-900 dark:text-white text-2xl font-bold mb-4 mt-6">{children}</h1>,
                                        h2: ({ children }) => <h2 className="text-gray-900 dark:text-white text-xl font-bold mb-3 mt-5">{children}</h2>,
                                        h3: ({ children }) => <h3 className="text-gray-900 dark:text-white text-lg font-semibold mb-2 mt-4">{children}</h3>,
                                        h4: ({ children }) => <h4 className="text-gray-900 dark:text-white text-base font-semibold mb-2 mt-3">{children}</h4>,
                                        strong: ({ children }) => <strong className="text-gray-900 dark:text-white font-semibold">{children}</strong>,
                                        em: ({ children }) => <em className="text-gray-700 dark:text-gray-200">{children}</em>,
                                        ul: ({ children }) => <ul className="text-gray-800 dark:text-gray-100 list-disc list-inside mb-3 space-y-1">{children}</ul>,
                                        ol: ({ children }) => <ol className="text-gray-800 dark:text-gray-100 list-decimal list-inside mb-3 space-y-1">{children}</ol>,
                                        li: ({ children }) => <li className="text-gray-800 dark:text-gray-100 mb-1">{children}</li>,
                                        table: ({ children }) => (
                                          <div className="overflow-x-auto my-4">
                                            <table className="min-w-full divide-y divide-gray-300 dark:divide-gray-600 border border-gray-300 dark:border-gray-600">
                                              {children}
                                            </table>
                                          </div>
                                        ),
                                        thead: ({ children }) => <thead className="bg-gray-100 dark:bg-gray-800">{children}</thead>,
                                        tbody: ({ children }) => <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">{children}</tbody>,
                                        tr: ({ children }) => <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">{children}</tr>,
                                        th: ({ children }) => <th className="px-4 py-3 text-left text-xs font-semibold text-gray-900 dark:text-gray-100 uppercase tracking-wider border-r border-gray-300 dark:border-gray-600 last:border-r-0">{children}</th>,
                                        td: ({ children }) => <td className="px-4 py-3 text-sm text-gray-800 dark:text-gray-200 border-r border-gray-200 dark:border-gray-700 last:border-r-0">{children}</td>,
                                        code: ({ node, inline, className, children, ...props }) => {
                                          const match = /language-(\w+)/.exec(className || '');
                                          const codeString = String(children).replace(/\n$/, '');
                                          return !inline && match ? (
                                            <div className="relative group my-3">
                                              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <button
                                                  onClick={() => { navigator.clipboard.writeText(codeString); setCopiedCodeId(codeString); setTimeout(() => setCopiedCodeId(null), 2000); }}
                                                  className="p-2 bg-gray-700 hover:bg-gray-600 text-white rounded text-xs flex items-center gap-1"
                                                >
                                                  {copiedCodeId === codeString ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                                                </button>
                                              </div>
                                              <SyntaxHighlighter style={vscDarkPlus} language={match[1]} PreTag="div" className="rounded-lg text-sm" {...props}>{codeString}</SyntaxHighlighter>
                                            </div>
                                          ) : (
                                            <code className="text-gray-900 dark:text-gray-100 bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 rounded text-sm font-mono" {...props}>{children}</code>
                                          );
                                        },
                                        hr: () => <hr className="my-4 border-gray-300 dark:border-gray-600" />
                                      }}
                                    >
                                      {formatDictResponse(message.content)}
                                    </ReactMarkdown>
                                  </div>
                                )}
                              </div>
                            )}

                          {/* Feedback buttons - hidden during review streaming */}
                          {!reviewLoading[activeTabId] && (
                            <div className="flex items-center gap-2 mt-4">
                              <button
                                onClick={() => handleFeedback(message.dbMessageId || message.id, 'positive', message.id)}
                                className={`p-1 transition-colors ${feedback[message.id] === 'positive'
                                    ? 'text-green-500 dark:text-green-400'
                                    : 'text-gray-600 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400'
                                  }`}
                                title="Thumbs up"
                              >
                                <ThumbsUp className="h-4 w-4" />
                              </button>
                              <button
                                onClick={() => handleFeedback(message.dbMessageId || message.id, 'negative', message.id)}
                                className={`p-1 transition-colors ${feedback[message.id] === 'negative'
                                    ? 'text-red-500 dark:text-red-400'
                                    : 'text-gray-600 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400'
                                  }`}
                                title="Thumbs down"
                              >
                                <ThumbsDown className="h-4 w-4" />
                              </button>
                              {/* Deep Review Button - Virgo (consoleOutput) or Taurus (investigationThread) */}
                              {((message.isTaurus && message.investigationThread) ||
                                (!message.isTaurus && (message.consoleOutput || message.execution_output))) && (
                                  <button
                                    onClick={() => {
                                      setPendingReviewMessage({ message, tabId: tab.id });
                                      setReviewNotesModalOpen(true);
                                    }}
                                    disabled={deepReviewLoading[message.id]}
                                    className={`p-1 transition-colors ${deepReviewLoading[message.id]
                                        ? 'text-purple-400 dark:text-purple-500 animate-pulse cursor-wait'
                                        : message.deepReviewFindings || deepReviewFindings[message.id]
                                          ? 'text-purple-600 dark:text-purple-400'
                                          : 'text-gray-600 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400'
                                      }`}
                                    title={
                                      deepReviewLoading[message.id]
                                        ? 'Deep review in progress...'
                                        : 'Deep Review - Verify accuracy and fix issues'
                                    }
                                  >
                                    <ShieldCheck className="h-4 w-4" />
                                  </button>
                                )}
                              <button
                                onClick={() => {
                                  // Both Virgo and Taurus store summary in reviewContent (content is empty by design)
                                  // Fallback chain: persisted reviewContent → streaming reviewContent → investigationThread → content
                                  const textToCopy = message.reviewContent || reviewContent[activeTabId] || message.investigationThread || message.content || '';
                                  copyToClipboard(textToCopy, message.id);
                                }}
                                className={`p-1 transition-colors ${copiedMessageId === message.id
                                    ? 'text-green-400'
                                    : 'text-gray-600 dark:text-gray-500 hover:text-gray-800 dark:hover:text-gray-300'
                                  }`}
                                title={copiedMessageId === message.id ? 'Copied!' : 'Copy message'}
                              >
                                {copiedMessageId === message.id ? (
                                  <Check className="h-4 w-4" />
                                ) : (
                                  <Copy className="h-4 w-4" />
                                )}
                              </button>
                              <button
                                onClick={() => downloadAnalysisReport(message, tab.id)}
                                className="p-1 text-gray-600 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                title="Download analysis report"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                              {/* Download all charts button - only for messages with multiple plots */}
                              {message.plots && message.plots.length > 1 && (
                                <button
                                  onClick={() => downloadAllPlots(message)}
                                  className="p-1 text-gray-600 dark:text-gray-500 hover:text-purple-600 dark:hover:text-purple-400 transition-colors"
                                  title={`Download all ${message.plots.length} charts`}
                                >
                                  <Images className="h-4 w-4" />
                                </button>
                              )}
                              {/* Save conversation button - only on last assistant message */}
                              {user && index === tab.messages.length - 1 && (
                                <button
                                  onClick={() => saveConversation(tab.id)}
                                  disabled={savingConversation[tab.id]}
                                  className={`p-1 transition-colors ${savingConversation[tab.id]
                                      ? 'text-blue-500 dark:text-blue-400 cursor-wait'
                                      : tab.isSaved
                                        ? 'text-green-500 dark:text-green-400'
                                        : 'text-gray-600 dark:text-gray-500 hover:text-blue-600 dark:hover:text-blue-400'
                                    }`}
                                  title={savingConversation[tab.id] ? 'Saving...' : tab.isSaved ? 'Conversation saved' : 'Save conversation'}
                                >
                                  {savingConversation[tab.id] ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : tab.isSaved ? (
                                    <CheckCircle className="h-4 w-4" />
                                  ) : (
                                    <Save className="h-4 w-4" />
                                  )}
                                </button>
                              )}
                              {/* Share button - only on last assistant message */}
                              {user && index === tab.messages.length - 1 && (
                                <button
                                  onClick={() => setShowShareChatModal(true)}
                                  className="p-1 text-gray-600 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                                  title="Share this chat"
                                >
                                  <Link className="h-4 w-4" />
                                </button>
                              )}
                              {/* Response time display */}
                              {message.responseTime && (
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
                                  {message.responseTime < 60000
                                    ? `${(message.responseTime / 1000).toFixed(1)}s`
                                    : `${Math.floor(message.responseTime / 60000)}m ${Math.round((message.responseTime % 60000) / 1000)}s`}
                                </span>
                              )}
                              {/* Query cost display - owner only */}
                              {message.queryCost != null && user?.role === 'owner' && (
                                <span className="text-xs text-gray-400 dark:text-gray-500 ml-1">
                                  {message.queryCost < 0.0001
                                    ? '~$0.00'
                                    : `~$${message.queryCost.toFixed(4)}`}
                                </span>
                              )}
                            </div>
                          )}

                          {/* AI Disclaimer - only on last assistant message, after review completes */}
                          {index === tab.messages.length - 1 && !reviewLoading[tab.id] && (
                            <div
                              className="mt-3 text-xs text-gray-500 dark:text-gray-400 italic animate-fade-in"
                              style={{
                                animation: 'fadeIn 0.5s ease-in',
                                animationFillMode: 'backwards'
                              }}
                            >
                              AI-generated analysis. Please verify critical results.
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )
              ));
            })()}

            {/* Taurus streaming content - shows while loading */}
            {chatMode === 'taurus' && tab.isLoading && tab.taurusStreamingContent && (
              <div className="flex justify-start">
                <div className="max-w-4xl mr-12">
                  <div className="space-y-4">
                    <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded-lg border border-gray-300 dark:border-gray-700">
                      <div className="text-sm font-mono text-gray-800 dark:text-gray-100">
                        {renderTaurusStreamingContent(
                          tab.taurusStreamingContent.replace(/__TAURUS_PLOT__([A-Za-z0-9+/=]+)/g, (match, base64) => `__TAURUS_PLOT__${base64.substring(0, 10)}... [visualization data truncated]`)
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-600 dark:text-gray-400">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Streaming...</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Scroll Navigation Arrows - appear when conversation is long */}
      {(showScrollTop || showScrollBottom) && (
        <div
          className="absolute right-6 bottom-24 flex flex-col gap-2 z-10"
          onMouseEnter={() => {
            // Cancel any pending hide
            if (answerNavHideTimerRef.current) clearTimeout(answerNavHideTimerRef.current);
            // Don't show navigator during streaming
            if (tab.isLoading) return;
            answerNavShowTimerRef.current = setTimeout(() => setAnswerNavVisible(true), 150);
          }}
          onMouseLeave={() => {
            if (answerNavShowTimerRef.current) clearTimeout(answerNavShowTimerRef.current);
            // Delay hide to allow moving to navigator
            answerNavHideTimerRef.current = setTimeout(() => setAnswerNavVisible(null), 200);
          }}
        >
          <AnswerNavigator
            messages={tab.messages}
            chatScrollRef={chatScrollRef}
            visible={answerNavVisible === true}
            onNavigate={() => setAnswerNavVisible(null)}
            onMouseEnter={() => {
              if (answerNavHideTimerRef.current) clearTimeout(answerNavHideTimerRef.current);
            }}
            onMouseLeave={() => {
              answerNavHideTimerRef.current = setTimeout(() => setAnswerNavVisible(null), 200);
            }}
          />
          {showScrollTop && (
            <button
              onClick={scrollToTop}
              className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 shadow-lg opacity-70 hover:opacity-100"
              title="Scroll to top"
            >
              <ChevronUp className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          )}
          {showScrollBottom && (
            <button
              onClick={() => scrollToBottom()}
              className="p-2 rounded-full bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 shadow-lg opacity-70 hover:opacity-100"
              title="Scroll to bottom"
            >
              <ChevronDown className="w-5 h-5 text-gray-600 dark:text-gray-300" />
            </button>
          )}
        </div>
      )}

      {/* Project Execution Status Bar */}
      {projectExecution && projectExecution.tabId === tab.id && (
        <div className="border-t border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/30 px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              <div>
                <span className="font-medium text-blue-900 dark:text-blue-100">
                  {projectExecution.projectName}
                </span>
                <span className="ml-2 text-sm text-blue-700 dark:text-blue-300">
                  Step {projectExecution.currentStepIndex + 1} of {projectExecution.steps.length}
                  {projectExecution.status === 'running' && ' — Running...'}
                  {projectExecution.status === 'paused' && ' — Waiting for approval'}
                  {projectExecution.status === 'completed' && ' — Complete!'}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {projectExecution.status === 'paused' && (
                <button
                  onClick={continueProjectExecution}
                  className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
                >
                  <Play className="h-4 w-4" />
                  Continue
                </button>
              )}
              {projectExecution.status !== 'completed' && (
                <button
                  onClick={cancelProjectExecution}
                  className="px-3 py-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg transition-colors"
                >
                  Cancel
                </button>
              )}
              {projectExecution.status === 'completed' && (
                <button
                  onClick={() => {
                    setProjectExecution(null);
                    setRunningProject(null);
                  }}
                  className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div
        className="border-t border-gray-200 dark:border-gray-700 p-2 sm:p-4 bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg shadow-[0_-4px_20px_-2px_rgba(0,0,0,0.1)] dark:shadow-[0_-4px_20px_-2px_rgba(0,0,0,0.3)]"
        onDrop={tab.mode === 'taurus' ? handleTaurusImageDrop : undefined}
        onDragOver={tab.mode === 'taurus' ? (e) => e.preventDefault() : undefined}
      >
        {/* Virgo Project Indicator */}
        {tab.mode === 'virgo' && activeProject && (
          <div className="flex items-center gap-2 mb-2 max-w-4xl mx-auto">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium border border-purple-200 dark:border-purple-700">
              <FolderOpen className="w-3 h-3" />
              {activeProject.name}
            </span>
            <button
              onClick={handleDeactivateProject}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
              title="Exit project mode"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {/* Taurus Image Preview Strip */}
        {tab.mode === 'taurus' && taurusAttachedImages.length > 0 && (
          <div className="flex gap-2 mb-3 max-w-4xl mx-auto overflow-x-auto pb-2">
            {taurusAttachedImages.map((img) => (
              <div key={img.id} className="relative flex-shrink-0 group">
                <img
                  src={img.preview}
                  alt={img.name || 'Attached image'}
                  className="h-16 w-16 object-cover rounded-lg border border-gray-300 dark:border-gray-600"
                />
                <button
                  onClick={() => handleTaurusImageRemove(img.id)}
                  className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove image"
                >
                  ×
                </button>
              </div>
            ))}
            {taurusAttachedImages.length < TAURUS_IMAGE_MAX_COUNT && (
              <div className="h-16 w-16 flex-shrink-0 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 text-xs text-center">
                Drop or<br />paste
              </div>
            )}
          </div>
        )}
        <div className="flex items-start gap-2 sm:gap-3 max-w-4xl mx-auto">
          <textarea
            data-tour="chat-input"
            value={tab.inputMessage || ''}
            onChange={(e) => updateTabData(tab.id, { inputMessage: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !tab.isLoading) {
                e.preventDefault();
                // Prevent sending in web mode + Taurus
                if (tab.mode === 'taurus' && !taurusAvailable) {
                  alert('Taurus mode is only available in VerbaGPT Desktop.\n\nVisit https://VerbaGPT.com to learn more and download.');
                  return;
                }
                // Use tab.mode instead of chatMode to avoid race conditions
                return submitChatText(tab.inputMessage, tab.mode, tab.id);
              }
            }}
            onPaste={tab.mode === 'taurus' ? handleTaurusImagePaste : undefined}
            placeholder={
              tab.mode === 'taurus' && !taurusAvailable
                ? "You need local Taurus server - click on Get Started - Download Taurus"
                : "Ask a question or create a chart... (Shift+Enter for new line)"
            }
            className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-4 py-3 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none overflow-y-auto"
            style={{ minHeight: '48px', maxHeight: '200px' }}
            rows={1}
            disabled={tab.isLoading || (tab.mode === 'taurus' && !taurusAvailable)}
          />
          {/* Add context button - show for both modes */}
          <button
            data-tour="snippets"
            type="button"
            onClick={handleOpenSnippetModal}
            disabled={tab.mode === 'taurus' && !taurusAvailable}
            className={`${tab.mode === 'taurus'
                ? 'bg-amber-600 hover:bg-amber-700'
                : 'bg-purple-500 hover:bg-purple-600'
              } text-white p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            title="Add context from snippets"
          >
            <Plus className="h-5 w-5" />
          </button>
          {/* Clear chat button */}
          <button
            type="button"
            onClick={() => {
              // Cancel any active Taurus request before clearing
              // Use ref (always current) instead of tab state (may be stale closure)
              const taurusRef = taurusAbortControllersRef.current[tab.id];
              if (taurusRef) {
                console.log('[Trash] Aborting active Taurus request for tab:', tab.id, 'requestId:', taurusRef.requestId);
                taurusRef.controller.abort();
                // Also tell backend to stop processing (fire and forget)
                if (taurusRef.requestId) {
                  fetch(`${API_ENDPOINTS.taurus}/api/cancel/${taurusRef.requestId}`, { method: 'POST' }).catch(() => { });
                }
              }

              // Cancel any active Virgo request before clearing
              const virgoController = virgoAbortControllersRef.current[tab.id];
              if (virgoController) {
                console.log('[Trash] Aborting active Virgo request for tab:', tab.id);
                virgoController.abort();
                const sessionId = virgoSessionIdsRef.current[tab.id];
                if (sessionId) {
                  fetch(`${API_ENDPOINTS.main}/virgo-abort`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ session_id: sessionId })
                  }).catch(() => { });
                }
              }

              // Revoke all blob URLs in messages before clearing to free memory
              // Also collect taurusRequestIds for backend cleanup
              const requestIdsToCleanup = [];
              if (tab.messages) {
                tab.messages.forEach(msg => {
                  // Collect taurusRequestId for backend folder cleanup
                  if (msg.taurusRequestId) {
                    requestIdsToCleanup.push(msg.taurusRequestId);
                  }
                  if (msg.plots) {
                    msg.plots.forEach(plot => {
                      // Check if plot is a blob URL string
                      if (typeof plot === 'string' && plot.startsWith('blob:')) {
                        URL.revokeObjectURL(plot);
                      }
                      // Check if plot is an object with blob URL
                      else if (plot && typeof plot === 'object') {
                        // New format: {url: 'blob:...', data: 'base64'}
                        if (plot.url && typeof plot.url === 'string' && plot.url.startsWith('blob:')) {
                          URL.revokeObjectURL(plot.url);
                        }
                        // Legacy format: {data: 'blob:...'}
                        const plotData = plot.data || plot.base64 || plot.image || plot;
                        if (typeof plotData === 'string' && plotData.startsWith('blob:')) {
                          URL.revokeObjectURL(plotData);
                        }
                      }
                    });
                  }
                });
              }

              // Clean up Taurus visuals folders on backend (fire and forget)
              if (requestIdsToCleanup.length > 0 && tab.mode === 'taurus') {
                fetch(`${API_ENDPOINTS.taurus}/api/cleanup-visuals`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ request_ids: requestIdsToCleanup })
                }).catch(() => { });
              }

              updateTabData(tab.id, {
                messages: [],
                inputMessage: '',
                title: 'New Chat',
                sessionId: uuidv4(), // Generate new session ID
                isLoading: false, // Reset loading state (request was aborted)
                taurusStreamingContent: '', // Clear Taurus streaming content
                taurusRequestId: null, // Clear Taurus request ID
                taurusSessionId: null // Clear Claude SDK session ID (forces new session)
              });
              // Clear review states for this tab
              setReviewContent(prev => {
                const newState = { ...prev };
                delete newState[tab.id];
                return newState;
              });
              setReviewLoading(prev => {
                const newState = { ...prev };
                delete newState[tab.id];
                return newState;
              });
              // Clear project execution if it was running on this tab
              if (projectExecution?.tabId === tab.id) {
                setProjectExecution(null);
                setRunningProject(null);
              }
            }}
            disabled={!tab.messages || tab.messages.length === 0}
            className="bg-gray-600 hover:bg-red-600 text-white p-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Clear conversation"
          >
            <Trash2 className="h-5 w-5" />
          </button>
          {/* File tree button - show for Taurus mode only (and only in desktop mode) */}
          {tab.mode === 'taurus' && taurusAvailable && (
            <button
              type="button"
              onClick={() => setShowFilePanel(!showFilePanel)}
              className="bg-gray-600 hover:bg-gray-700 text-white p-3 rounded-lg transition-colors"
              title="Browse files"
            >
              <FolderTree className="h-5 w-5" />
            </button>
          )}
          {/* Projects button - show for Taurus mode only */}
          {tab.mode === 'taurus' && taurusAvailable && (
            <button
              type="button"
              onClick={() => setShowProjectsPanel(!showProjectsPanel)}
              className={`p-3 rounded-lg transition-colors ${showProjectsPanel
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
                }`}
              title="Taurus Projects"
            >
              <Layers className="h-5 w-5" />
            </button>
          )}
          {/* Data sources button - show for Virgo mode */}
          {tab.mode === 'virgo' && (
            <button
              type="button"
              onClick={() => setShowDataSourcesPanel(true)}
              className={`relative p-3 rounded-lg transition-colors ${selectedFiles.length > 0 || !includeDatabase
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-gray-600 hover:bg-gray-700 text-white'
                }`}
              title="Configure data sources"
            >
              <Database className="h-5 w-5" />
              {(selectedFiles.length > 0 || (includeDatabase && activeDatasource)) && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-500 text-white text-xs rounded-full flex items-center justify-center font-medium">
                  {(includeDatabase && activeDatasource ? 1 : 0) + selectedFiles.length}
                </span>
              )}
            </button>
          )}
          {/* Show Stop button for Taurus when loading */}
          {tab.mode === 'taurus' && tab.isLoading && (
            <button
              onClick={() => cancelTaurusRequest(tab.id)}
              className="bg-red-600 hover:bg-red-700 text-white p-3 rounded-lg transition-colors"
              title="Stop Taurus"
            >
              <XCircle className="h-5 w-5" />
            </button>
          )}
          {/* Send button - shows spinner when loading */}
          <button
            onClick={() => {
              // Prevent sending in web mode + Taurus
              if (tab.mode === 'taurus' && !taurusAvailable) {
                alert('Taurus mode is only available in VerbaGPT Desktop.\n\nVisit https://VerbaGPT.com to learn more and download.');
                return;
              }
              // Use tab.mode instead of chatMode to avoid race conditions
              return submitChatText(tab.inputMessage, tab.mode, tab.id);
            }}
            disabled={tab.isLoading || !tab.inputMessage?.trim() || (tab.mode === 'taurus' && !taurusAvailable)}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white p-3 rounded-lg transition-colors disabled:cursor-not-allowed"
          >
            {tab.isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Send className="h-5 w-5" />
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Render settings tab content
  const renderSettingsTab = () => {
    return (
      <div className="p-6 bg-white dark:bg-gray-900 h-full overflow-y-auto">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Settings</h2>

        {/* Tabs Navigation */}
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setSettingsTab('databases')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${settingsTab === 'databases'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              Databases
            </button>
            <button
              onClick={() => setSettingsTab('virgo')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${settingsTab === 'virgo'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              Virgo
            </button>
            <button
              onClick={() => setSettingsTab('taurus')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${settingsTab === 'taurus'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              Taurus
            </button>
            <button
              onClick={() => setSettingsTab('plans')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${settingsTab === 'plans'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              Plans
            </button>
            {(user?.role === 'admin' || user?.role === 'owner') && (
              <button
                onClick={() => setSettingsTab('admin')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${settingsTab === 'admin'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
              >
                Admin
              </button>
            )}
            {user?.role === 'owner' && (
              <button
                onClick={() => setSettingsTab('owner')}
                className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${settingsTab === 'owner'
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
              >
                Owner
              </button>
            )}
          </nav>
        </div>

        <div className="space-y-6 max-w-2xl">
          {/* Databases Tab */}
          {settingsTab === 'databases' && (
            <>
              {/* Datasources Section */}
              {datasources.length > 0 && (
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Active Datasource
                    </label>
                    <button
                      onClick={() => {
                        setAddDatasourceModalOpen(true);
                        trackFeatureUse('add_datasource_modal_opened', { source: 'settings_tab' });
                      }}
                      className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      + Add New
                    </button>
                  </div>
                  <div className="space-y-2">
                    {datasources.map((ds) => (
                      <div
                        key={ds.name}
                        className={`flex items-center justify-between p-3 rounded-md border ${ds.is_active
                            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
                            : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600'
                          }`}
                      >
                        <div className="flex items-center space-x-3">
                          <Database className={`h-5 w-5 ${ds.is_active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                {ds.name}
                              </span>
                              {ds.privacy === 'published' && (
                                <span className="text-xs font-medium text-purple-600 dark:text-purple-400 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 rounded">
                                  {ds.creator_role === 'owner' ? 'Demo' : 'Shared'}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDbType(ds.db_type)}
                              {ds.read_only === false ? (
                                <span className="text-amber-600 dark:text-amber-400"> · Full access</span>
                              ) : (
                                <span> · Read-only</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {generatingDataNotes[ds.name] && (
                            <span className="text-xs font-medium text-amber-600 dark:text-amber-400 px-2 py-1 bg-amber-100 dark:bg-amber-900/40 rounded flex items-center gap-1">
                              <Loader2 className="h-3 w-3 animate-spin" />
                              Generating notes...
                            </span>
                          )}
                          {activatingDatasource === ds.name ? (
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 px-2 py-1 bg-blue-100 dark:bg-blue-900/40 rounded">
                              Activating...
                            </span>
                          ) : ds.is_active ? (
                            <span className="text-xs font-medium text-blue-600 dark:text-blue-400 px-2 py-1 bg-blue-100 dark:bg-blue-900/40 rounded">
                              Active
                            </span>
                          ) : (
                            <button
                              onClick={() => handleActivateDatasource(ds.name)}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                              disabled={activatingDatasource !== null}
                            >
                              Activate
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedDatasource(ds.name);
                              setDataNotesModalOpen(true);
                              trackFeatureUse('data_notes_opened', { source: 'datasource_list' });
                            }}
                            className="text-xs text-green-600 dark:text-green-400 hover:underline"
                          >
                            Data Notes
                          </button>
                          {ds.is_editable && (
                            <button
                              onClick={() => handleEditDatasource(ds)}
                              className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                            >
                              <Edit className="h-3 w-3" />
                              Edit
                            </button>
                          )}
                          {ds.is_editable && (
                            <>
                              {refreshingStatsDatasource === ds.name ? (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  Updating...
                                </span>
                              ) : (
                                <button
                                  onClick={() => handleRefreshStats(ds.name)}
                                  className="text-xs text-purple-600 dark:text-purple-400 hover:underline"
                                  disabled={refreshingStatsDatasource !== null}
                                  title="Update table row counts (fast, no full table scan)"
                                >
                                  Update
                                </button>
                              )}
                              <button
                                onClick={() => handleDeleteDatasource(ds.name)}
                                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                              >
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Virgo Tab - Placeholder for Future User Settings */}
          {settingsTab === 'virgo' && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="bg-gray-50 dark:bg-gray-800 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 max-w-2xl text-center">
                <Sparkles className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
                  User-Specific Virgo Settings
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  This space is reserved for future user-customizable Virgo settings. Currently, all Virgo configuration is managed by your organization owner in the Owner Settings tab.
                </p>
                {user?.role === 'owner' && (
                  <p className="text-sm text-blue-600 dark:text-blue-400">
                    💡 As an owner, you can configure Virgo settings in <strong>Settings → Owner → Virgo LLM</strong>
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Taurus Tab - Future Taurus Settings */}
          {settingsTab === 'taurus' && (
            <>
              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-purple-800 dark:text-purple-200">
                  🤖 Configure your personal Taurus mode settings (Claude Code SDK). These settings affect only your Taurus queries.
                </p>
              </div>

              {/* Taurus Sub-Tabs */}
              <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="-mb-px flex space-x-6">
                  <button
                    onClick={() => setTaurusSubTab('model')}
                    className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${taurusSubTab === 'model'
                        ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                  >
                    Model Settings
                  </button>
                  <button
                    onClick={() => setTaurusSubTab('usage')}
                    className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${taurusSubTab === 'usage'
                        ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                  >
                    Usage Tracking
                  </button>
                  <button
                    onClick={() => setTaurusSubTab('skills')}
                    className={`pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${taurusSubTab === 'skills'
                        ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                  >
                    Skills
                  </button>
                </nav>
              </div>

              {/* Model Settings Sub-Tab */}
              {taurusSubTab === 'model' && (
                <>
                  {/* LLM Provider Toggle */}
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      LLM Provider
                    </label>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const wasOpenRouter = settings.taurus_llm_provider === 'openrouter';
                          setSettings(prev => ({ ...prev, taurus_llm_provider: 'anthropic' }));
                          setTaurusProviderTest(null);
                          // Auto-save when switching back to Anthropic (saves user a click)
                          if (wasOpenRouter) {
                            saveUserSettings({ taurus_llm_provider: 'anthropic' });
                          }
                        }}
                        className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${settings.taurus_llm_provider !== 'openrouter'
                            ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-500 text-purple-700 dark:text-purple-300'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                      >
                        Anthropic (Direct)
                      </button>
                      <button
                        onClick={() => {
                          setSettings(prev => ({ ...prev, taurus_llm_provider: 'openrouter' }));
                          setTaurusProviderTest(null);
                        }}
                        className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${settings.taurus_llm_provider === 'openrouter'
                            ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-500 text-purple-700 dark:text-purple-300'
                            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                          }`}
                      >
                        OpenRouter
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      {settings.taurus_llm_provider === 'openrouter'
                        ? 'Use OpenRouter for access to multiple LLM providers (requires OpenRouter API key)'
                        : 'Direct connection to Anthropic API (uses your Anthropic API key)'}
                    </p>
                  </div>

                  {/* Anthropic Model (shown when using Anthropic) */}
                  {settings.taurus_llm_provider !== 'openrouter' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Claude Model
                      </label>
                      <select
                        value={settings.claude_model || 'claude-haiku-4-5-20251001'}
                        onChange={(e) => setSettings(prev => ({ ...prev, claude_model: e.target.value }))}
                        className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="claude-haiku-4-5-20251001">Claude Haiku 4.5 (Fast, affordable)</option>
                        <option value="claude-sonnet-4-6">Claude Sonnet 4.6 (Balanced)</option>
                        <option value="claude-opus-4-6">Claude Opus 4.6 (Most capable)</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Claude model used for Taurus mode queries
                      </p>
                    </div>
                  )}

                  {/* OpenRouter Model (shown when using OpenRouter) */}
                  {settings.taurus_llm_provider === 'openrouter' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          OpenRouter Model
                          {loadingOpenRouterModels && <span className="ml-2 text-xs text-gray-400">(loading pricing...)</span>}
                        </label>
                        <select
                          value={settings.taurus_openrouter_model || 'google/gemini-2.0-flash-001'}
                          onChange={(e) => {
                            setSettings(prev => ({ ...prev, taurus_openrouter_model: e.target.value }));
                            setTaurusProviderTest(null);
                          }}
                          className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {/* Use dynamic pricing data if available, otherwise static fallback */}
                          {openRouterModels.length > 0 ? (
                            // Dynamic list with pricing from API (backend returns only curated models)
                            (() => {
                              // Group models by provider
                              const groups = {};
                              openRouterModels.forEach(m => {
                                const provider = m.id.split('/')[0];
                                if (!groups[provider]) groups[provider] = [];
                                groups[provider].push(m);
                              });
                              const formatPrice = (p) => p < 0.01 ? '<$0.01' : `$${p.toFixed(2)}`;
                              const providerLabels = {
                                'google': 'Google Models', 'anthropic': 'Claude Models', 'openai': 'OpenAI Models',
                                'deepseek': 'DeepSeek Models', 'x-ai': 'xAI Models', 'mistralai': 'Mistral',
                                'kwaipilot': 'Kuaishou', 'moonshotai': 'Moonshot', 'xiaomi': 'Xiaomi', 'minimax': 'MiniMax',
                                'z-ai': 'Zhipu', 'bytedance-seed': 'ByteDance'
                              };
                              return Object.entries(groups).map(([provider, models]) => (
                                <optgroup key={provider} label={providerLabels[provider] || provider}>
                                  {models.map(m => (
                                    <option key={m.id} value={m.id}>
                                      {m.name} ({formatPrice(m.pricing.prompt)}/1M tok)
                                    </option>
                                  ))}
                                </optgroup>
                              ));
                            })()
                          ) : (
                            // Static fallback when API data not loaded
                            <>
                              <optgroup label="Fastest (Recommended)">
                                <option value="google/gemini-2.0-flash-001">Gemini 2.0 Flash (~5s, great value)</option>
                              </optgroup>
                              <optgroup label="Claude Models">
                                <option value="anthropic/claude-sonnet-4">Claude Sonnet 4 (~8s)</option>
                                <option value="anthropic/claude-3.5-haiku">Claude Haiku 3.5 (fast)</option>
                              </optgroup>
                              <optgroup label="Google Models">
                                <option value="google/gemini-2.5-pro-preview">Gemini 2.5 Pro (~12s, most capable)</option>
                              </optgroup>
                              <optgroup label="OpenAI Models">
                                <option value="openai/gpt-4o">GPT-4o (~16s)</option>
                                <option value="openai/gpt-4o-mini">GPT-4o Mini (~16s, cheaper)</option>
                                <option value="openai/gpt-5.1-codex-mini">GPT-5.1 Codex Mini (coding)</option>
                                <option value="openai/gpt-5.1-codex">GPT-5.1 Codex (coding)</option>
                                <option value="openai/gpt-5.2">GPT-5.2</option>
                              </optgroup>
                              <optgroup label="DeepSeek Models">
                                <option value="deepseek/deepseek-v3.2">DeepSeek V3.2</option>
                              </optgroup>
                              <optgroup label="xAI Models">
                                <option value="x-ai/grok-4.1-fast">Grok 4.1 Fast</option>
                              </optgroup>
                              <optgroup label="Other">
                                <option value="mistralai/mistral-large-2411">Mistral Large (~13s)</option>
                                <option value="kwaipilot/kat-coder-pro">KatCoder Pro (coding)</option>
                                <option value="moonshotai/kimi-k2-thinking">Kimi K2 Thinking (reasoning)</option>
                                <option value="xiaomi/mimo-v2-flash">Xiaomi MiMo V2 Flash</option>
                                <option value="minimax/minimax-m2.1">MiniMax M2.1</option>
                                <option value="z-ai/glm-4.7-flash">GLM 4.7 Flash</option>
                                <option value="z-ai/glm-4.7">GLM 4.7</option>
                                <option value="bytedance-seed/seed-1.6-flash">Seed 1.6 Flash</option>
                              </optgroup>
                            </>
                          )}
                        </select>
                        <div className="flex items-center justify-between mt-1">
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {openRouterModels.length > 0 ? 'Prices per 1M input tokens' : 'Only models with tool calling support are listed'}
                          </p>
                          <div className="flex items-center gap-2">
                            {openRouterModels.length > 0 && (
                              <button
                                onClick={() => fetchOpenRouterModels(true)}
                                disabled={loadingOpenRouterModels}
                                className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 disabled:opacity-50"
                              >
                                {loadingOpenRouterModels ? 'Refreshing...' : 'Refresh prices'}
                              </button>
                            )}
                            <a
                              href="https://openrouter.ai/models?supported_parameters=tools"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400"
                            >
                              Browse all models →
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* OpenRouter API Key */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          OpenRouter API Key
                        </label>
                        <div className="relative">
                          <input
                            type={showApiKeys.taurus_openrouter_key ? "text" : "password"}
                            value={settings.taurus_openrouter_key || ''}
                            onChange={(e) => setSettings(prev => ({ ...prev, taurus_openrouter_key: e.target.value }))}
                            placeholder="sk-or-v1-..."
                            className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 pr-10 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setShowApiKeys(prev => ({ ...prev, taurus_openrouter_key: !prev.taurus_openrouter_key }))}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          >
                            {showApiKeys.taurus_openrouter_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Get your API key from <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-600">openrouter.ai/keys</a>
                        </p>
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          💡 Set a spending limit for your key at <a href="https://openrouter.ai/settings/keys" target="_blank" rel="noopener noreferrer" className="text-amber-700 dark:text-amber-300 hover:underline">openrouter.ai/settings/keys</a>
                        </p>
                      </div>

                      {/* Test Connection Button */}
                      <div className="flex items-center gap-3 flex-wrap">
                        <button
                          onClick={testTaurusProvider}
                          disabled={taurusProviderTest?.status === 'testing' || !settings.taurus_openrouter_key}
                          title={!settings.taurus_openrouter_key ? 'Enter your OpenRouter API key first' : 'Test the connection and tool calling support'}
                          className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                        >
                          {taurusProviderTest?.status === 'testing' ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
                              Testing...
                            </>
                          ) : 'Test Connection'}
                        </button>
                        {taurusProviderTest && taurusProviderTest.status !== 'testing' && (
                          <span className={`text-sm ${taurusProviderTest.status === 'success' ? 'text-green-600 dark:text-green-400' :
                              taurusProviderTest.status === 'warning' ? 'text-yellow-600 dark:text-yellow-400' :
                                'text-red-600 dark:text-red-400'
                            }`}>
                            {taurusProviderTest.status === 'success' ? '✓ ' : taurusProviderTest.status === 'warning' ? '⚠ ' : '✗ '}
                            {taurusProviderTest.message}
                          </span>
                        )}
                        {/* Save reminder after successful test */}
                        {taurusProviderTest?.status === 'success' && (
                          <button
                            onClick={saveUserSettings}
                            disabled={settingsLoading}
                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white text-sm rounded-lg transition-colors animate-pulse"
                          >
                            {settingsLoading ? 'Saving...' : 'Save to persist →'}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Max Output Tokens */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Max Output Tokens
                    </label>
                    <input
                      type="number"
                      min="1000"
                      max="200000"
                      step="1000"
                      value={settings.claude_max_output_tokens || ''}
                      onChange={(e) => setSettings(prev => ({ ...prev, claude_max_output_tokens: e.target.value }))}
                      placeholder="100000"
                      className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Maximum tokens for Claude's responses (default: 100000, max: 200000)
                    </p>
                  </div>

                  {/* Incognito Mode */}
                  <div className="flex items-start gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <input
                      id="taurus_incognito"
                      type="checkbox"
                      checked={settings.taurus_incognito || false}
                      onChange={(e) => setSettings(prev => ({ ...prev, taurus_incognito: e.target.checked }))}
                      className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-amber-600 focus:ring-amber-500 dark:bg-gray-800"
                    />
                    <div className="flex-1">
                      <label htmlFor="taurus_incognito" className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                        Incognito Mode
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        When enabled, Taurus conversations won't be saved to chat history or add to contextual memory for your data.
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5 italic">
                        Note: For organizational/team plans, data may still be available to account admins in accordance with your organization's data retention policy.
                      </p>
                    </div>
                  </div>

                  {/* Allow Questions */}
                  <div className="flex items-start gap-3">
                    <input
                      id="taurus_allow_questions"
                      type="checkbox"
                      checked={settings.taurus_allow_questions ?? true}
                      onChange={(e) => setSettings(prev => ({ ...prev, taurus_allow_questions: e.target.checked }))}
                      className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-800"
                    />
                    <div className="flex-1">
                      <label htmlFor="taurus_allow_questions" className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                        Allow Clarifying Questions
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        When enabled, Claude can ask you clarifying questions during analysis. When disabled, Claude will proceed with its best judgment.
                      </p>
                    </div>
                  </div>

                  {/* Bash Restrictions */}
                  <div className="flex items-start gap-3">
                    <input
                      id="taurus_bash_restrictions"
                      type="checkbox"
                      checked={settings.taurus_bash_restrictions || settings.bash_restrictions_enforced_by_org || false}
                      onChange={(e) => setSettings(prev => ({ ...prev, taurus_bash_restrictions: e.target.checked }))}
                      disabled={settings.bash_restrictions_enforced_by_org}
                      className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <div className="flex-1">
                      <label htmlFor="taurus_bash_restrictions" className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                        Bash Security Restrictions
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        When enabled, blocks potentially dangerous bash commands (rm, curl, sudo, etc.) to prevent accidental data loss or security issues.
                      </p>
                      {settings.bash_restrictions_enforced_by_org && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1.5 flex items-center gap-1">
                          <span>⚠</span> This setting is enforced by your organization.
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Save Button (all users can save their own settings) */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={saveUserSettings}
                      disabled={settingsLoading}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      {settingsLoading ? 'Saving...' : 'Save Settings'}
                    </button>
                    {settingsSaved && (
                      <span className="text-green-600 dark:text-green-400 font-medium animate-pulse">
                        ✓ Settings saved successfully!
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* Usage Tracking Sub-Tab */}
              {taurusSubTab === 'usage' && (
                <TaurusUsageDisplay />
              )}

              {/* Skills Sub-Tab */}
              {taurusSubTab === 'skills' && (
                <SkillsPanel
                  user={user}
                  authToken={localStorage.getItem('token')}
                  showNotification={showNotification}
                />
              )}
            </>
          )}

          {/* Owner Tab - API Keys Only */}
          {settingsTab === 'owner' && user?.role === 'owner' && (
            <>
              {/* Owner Sub-Tabs */}
              <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="-mb-px flex space-x-6">
                  <button
                    onClick={() => setOwnerSubTab('virgo')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${ownerSubTab === 'virgo'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Virgo
                  </button>
                  <button
                    onClick={() => setOwnerSubTab('api')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${ownerSubTab === 'api'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    API
                  </button>
                  <button
                    onClick={() => setOwnerSubTab('credit-limits')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${ownerSubTab === 'credit-limits'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Credit Limits
                  </button>
                  <button
                    onClick={() => setOwnerSubTab('database-publishing')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${ownerSubTab === 'database-publishing'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Database Publishing
                  </button>
                  <button
                    onClick={() => setOwnerSubTab('organizations')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${ownerSubTab === 'organizations'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Organizations
                  </button>
                  <button
                    onClick={() => setOwnerSubTab('promo-codes')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${ownerSubTab === 'promo-codes'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Promo Codes
                  </button>
                  <button
                    onClick={() => setOwnerSubTab('audit')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${ownerSubTab === 'audit'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Audit Logs
                  </button>
                </nav>
              </div>

              {/* Reload Settings Button - visible on all owner tabs */}
              <div className="mb-6 flex items-center justify-between bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  💡 Apply saved changes without restarting server
                </p>
                <button
                  onClick={handleReloadSettings}
                  disabled={reloadingSettings}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg font-medium transition-colors shadow-sm disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <RefreshCw className={`w-4 h-4 ${reloadingSettings ? 'animate-spin' : ''}`} />
                  {reloadingSettings ? 'Reloading...' : 'Reload'}
                </button>
              </div>

              {/* API Balance Checker - visible on all owner tabs */}
              <div className="mb-6 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900 dark:text-white">API Provider Status</h4>
                  <button
                    onClick={fetchApiBalances}
                    disabled={loadingBalances}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white text-sm rounded-lg font-medium transition-colors disabled:cursor-not-allowed"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingBalances ? 'animate-spin' : ''}`} />
                    {loadingBalances ? 'Checking...' : 'Check Balances'}
                  </button>
                </div>
                {apiBalances ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {apiBalances.map((provider) => (
                      <div
                        key={provider.provider}
                        className={`p-3 rounded-lg border ${provider.status === 'ok'
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                            : provider.status === 'no_key'
                              ? 'bg-gray-100 dark:bg-gray-700/50 border-gray-200 dark:border-gray-600'
                              : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                          }`}
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`w-2 h-2 rounded-full ${provider.status === 'ok' ? 'bg-green-500' :
                              provider.status === 'no_key' ? 'bg-gray-400' : 'bg-red-500'
                            }`} />
                          <span className="text-sm font-medium text-gray-900 dark:text-white">
                            {provider.provider}
                          </span>
                        </div>
                        {provider.status === 'ok' && provider.balance && (
                          <p className="text-xs text-green-700 dark:text-green-300 font-mono">
                            {provider.balance}
                          </p>
                        )}
                        {provider.status === 'no_key' && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">No key</p>
                        )}
                        {provider.status === 'error' && (
                          <p className="text-xs text-red-600 dark:text-red-400 truncate" title={provider.message}>
                            {provider.message || 'Error'}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Click "Check Balances" to view provider status and remaining credits
                  </p>
                )}
              </div>

              {/* API Tab */}
              {ownerSubTab === 'api' && (
                <>
                  <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 mb-6">
                    <p className="text-sm text-amber-800 dark:text-amber-200">
                      🔑 API keys configured here are shared across all users in your organization.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 mb-6">
                    {/* OpenRouter API Key */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        OpenRouter API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.openrouter_api_key ? "text" : "password"}
                          value={settings.openrouter_api_key || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, openrouter_api_key: e.target.value }))}
                          placeholder="sk-or-v1-..."
                          className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 pr-10 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button type="button" onClick={() => setShowApiKeys(prev => ({ ...prev, openrouter_api_key: !prev.openrouter_api_key }))} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                          {showApiKeys.openrouter_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    {/* Groq API Key */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Groq API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.groq_api_key ? "text" : "password"}
                          value={settings.groq_api_key || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, groq_api_key: e.target.value }))}
                          placeholder="gsk_..."
                          className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 pr-10 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button type="button" onClick={() => setShowApiKeys(prev => ({ ...prev, groq_api_key: !prev.groq_api_key }))} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                          {showApiKeys.groq_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    {/* Cerebras API Key */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Cerebras API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.cerebras_api_key ? "text" : "password"}
                          value={settings.cerebras_api_key || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, cerebras_api_key: e.target.value }))}
                          placeholder="csk-..."
                          className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 pr-10 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button type="button" onClick={() => setShowApiKeys(prev => ({ ...prev, cerebras_api_key: !prev.cerebras_api_key }))} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                          {showApiKeys.cerebras_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    {/* OpenAI API Key */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        OpenAI API Key
                      </label>
                      <div className="relative">
                        <input
                          type={showApiKeys.openai_api_key ? "text" : "password"}
                          value={settings.openai_api_key || ''}
                          onChange={(e) => setSettings(prev => ({ ...prev, openai_api_key: e.target.value }))}
                          placeholder="sk-proj-..."
                          className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 pr-10 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button type="button" onClick={() => setShowApiKeys(prev => ({ ...prev, openai_api_key: !prev.openai_api_key }))} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                          {showApiKeys.openai_api_key ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Used for embeddings (smart schema selection). Configure the model in Virgo → Embeddings.
                      </p>
                    </div>
                  </div>


                  {/* Save Button for API Keys */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={saveUserSettings}
                      disabled={settingsLoading}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-lg transition-colors"
                    >
                      {settingsLoading ? 'Saving...' : 'Save Settings'}
                    </button>
                    {settingsSaved && (
                      <span className="text-green-600 dark:text-green-400 font-medium animate-pulse">
                        ✓ Settings saved successfully!
                      </span>
                    )}
                  </div>
                </>
              )}

              {/* Virgo Tab */}
              {ownerSubTab === 'virgo' && (
                <div className="space-y-8">

                  {/* ── Model Lists ── */}
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                      Model Lists
                    </h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                      <p className="text-xs text-blue-800 dark:text-blue-200">
                        Models are tried top-to-bottom on failure. Changes take effect immediately — no restart needed.
                      </p>
                    </div>

                    {/* Enable Virgo Review toggle */}
                    <div className="flex items-center gap-3 mb-5">
                      <input
                        id="use_virgo_review_virgo"
                        type="checkbox"
                        checked={settings.use_virgo_review || false}
                        onChange={(e) => setSettings(prev => ({ ...prev, use_virgo_review: e.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-800"
                      />
                      <label htmlFor="use_virgo_review_virgo" className="text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                        Enable Virgo Final Review
                      </label>
                    </div>

                    {!modelConfig ? (
                      <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading model config...
                      </div>
                    ) : (
                      <>
                        <ModelListEditor
                          title="Smolagents LLM (code generation)"
                          models={modelConfig.virgo_models}
                          onChange={(updated) => setModelConfig(prev => ({ ...prev, virgo_models: updated }))}
                          maxRetries={modelConfig.max_virgo_retries}
                          onMaxRetriesChange={(v) => setModelConfig(prev => ({ ...prev, max_virgo_retries: v }))}
                        />

                        <ModelListEditor
                          title="Virgo Review"
                          models={modelConfig.review_models}
                          onChange={(updated) => setModelConfig(prev => ({ ...prev, review_models: updated }))}
                          maxRetries={modelConfig.max_review_retries}
                          onMaxRetriesChange={(v) => setModelConfig(prev => ({ ...prev, max_review_retries: v }))}
                        />

                        <div className="flex items-center gap-4 mt-4">
                          <button
                            onClick={() => { saveModelConfig(); saveUserSettings(); }}
                            disabled={modelConfigSaving || settingsLoading}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-5 py-2 rounded-lg text-sm transition-colors"
                          >
                            {(modelConfigSaving || settingsLoading) ? 'Saving...' : 'Save Model Lists'}
                          </button>
                          {modelConfigSaved && (
                            <span className="text-green-600 dark:text-green-400 text-sm font-medium animate-pulse">
                              ✓ Saved — active immediately
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* ── Schema ── */}
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                      Schema
                    </h4>

                    {/* Provider Selection */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Provider
                      </label>
                      <select
                        value={settings.schema_llm_provider || 'openrouter'}
                        onChange={(e) => setSettings(prev => ({ ...prev, schema_llm_provider: e.target.value }))}
                        className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="openrouter">OpenRouter</option>
                        <option value="groq">Groq (Faster, Limited Models)</option>
                        <option value="cerebras">Cerebras (Ultra-Fast, Limited Models)</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Select which LLM provider Virgo should use for schema selection
                      </p>
                    </div>

                    {settings.schema_llm_provider === 'openrouter' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          OpenRouter Model (Schema Selection)
                        </label>
                        <select
                          value={settings.schema_openrouter_model || 'anthropic/claude-haiku-4.5'}
                          onChange={(e) => setSettings(prev => ({ ...prev, schema_openrouter_model: e.target.value }))}
                          className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {(availableModels?.schema || []).filter(m => m.provider === 'openrouter').map(m => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Select a model for smart schema selection. Edit models_config.py to update this list.
                        </p>
                      </div>
                    )}

                    {settings.schema_llm_provider === 'groq' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Groq Model
                        </label>
                        <select
                          value={settings.schema_groq_model || 'llama-3.3-70b-versatile'}
                          onChange={(e) => setSettings(prev => ({ ...prev, schema_groq_model: e.target.value }))}
                          className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {(availableModels?.schema || []).filter(m => m.provider === 'groq').map(m => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Groq provides hardware-accelerated inference (280-1000 tokens/second)
                        </p>
                      </div>
                    )}

                    {settings.schema_llm_provider === 'cerebras' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Cerebras Model
                        </label>
                        <select
                          value={settings.schema_cerebras_model || 'llama3.1-8b'}
                          onChange={(e) => setSettings(prev => ({ ...prev, schema_cerebras_model: e.target.value }))}
                          className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          {(availableModels?.schema || []).filter(m => m.provider === 'cerebras').map(m => (
                            <option key={m.id} value={m.id}>{m.label}</option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Cerebras provides ultra-fast inference (2000-3000 tokens/second)
                        </p>
                      </div>
                    )}

                    <div className="flex items-center gap-4 mt-5">
                      <button
                        onClick={saveUserSettings}
                        disabled={settingsLoading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-5 py-2 rounded-lg text-sm transition-colors"
                      >
                        {settingsLoading ? 'Saving...' : 'Save Schema Settings'}
                      </button>
                      {settingsSaved && (
                        <span className="text-green-600 dark:text-green-400 text-sm font-medium animate-pulse">
                          ✓ Saved!
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Embeddings ── */}
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                      Embeddings
                    </h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                      <p className="text-sm text-blue-800 dark:text-blue-200">
                        🧮 Embeddings help Virgo intelligently filter database columns based on semantic similarity to your query. API key is configured in the API tab.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Embedding Model
                      </label>
                      <select
                        value={settings.openai_embedding_model || 'text-embedding-ada-002'}
                        onChange={(e) => setSettings(prev => ({ ...prev, openai_embedding_model: e.target.value }))}
                        className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="text-embedding-ada-002">Ada 002 (Legacy)</option>
                        <option value="text-embedding-3-small">Text Embedding 3 Small</option>
                        <option value="text-embedding-3-large">Text Embedding 3 Large</option>
                      </select>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Used for semantic similarity in smart schema selection
                      </p>
                    </div>

                    <div className="flex items-center gap-4 mt-5">
                      <button
                        onClick={saveUserSettings}
                        disabled={settingsLoading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-5 py-2 rounded-lg text-sm transition-colors"
                      >
                        {settingsLoading ? 'Saving...' : 'Save Embeddings Settings'}
                      </button>
                      {settingsSaved && (
                        <span className="text-green-600 dark:text-green-400 text-sm font-medium animate-pulse">
                          ✓ Saved!
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Advanced ── */}
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                      Advanced
                    </h4>
                    <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-4">
                      <p className="text-sm text-purple-800 dark:text-purple-200">
                        ⚙️ Advanced configuration for Virgo's schema selection and query processing. These settings affect all users.
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Schema Similarity Threshold
                      </label>
                      <input
                        type="number"
                        step="0.05"
                        min="0"
                        max="1"
                        value={settings.schema_threshold || '0.70'}
                        onChange={(e) => setSettings(prev => ({ ...prev, schema_threshold: e.target.value }))}
                        className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Minimum similarity score for column inclusion (0.0–1.0, default: 0.70)
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={settings.schema_include_similarity === 'yes'}
                          onChange={(e) => setSettings(prev => ({ ...prev, schema_include_similarity: e.target.checked ? 'yes' : 'no' }))}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Enable Smart Schema Selection
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                        Use embeddings to intelligently select relevant columns
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={settings.schema_db_reuse === 'yes'}
                          onChange={(e) => setSettings(prev => ({ ...prev, schema_db_reuse: e.target.checked ? 'yes' : 'no' }))}
                          className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Reuse Database Connections
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                        Keep database connections alive between requests (faster)
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        QA History Length
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="10"
                        value={settings.qa_history || '1'}
                        onChange={(e) => setSettings(prev => ({ ...prev, qa_history: e.target.value }))}
                        className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Number of previous Q&amp;A pairs to include in context
                      </p>
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Schema Cache Version
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={settings.schema_cache_version || '1'}
                        onChange={(e) => setSettings(prev => ({ ...prev, schema_cache_version: e.target.value }))}
                        className="w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg px-3 py-2 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Increment to invalidate embeddings cache
                      </p>
                    </div>

                    <div className="flex items-center gap-4 mt-5">
                      <button
                        onClick={saveUserSettings}
                        disabled={settingsLoading}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-5 py-2 rounded-lg text-sm transition-colors"
                      >
                        {settingsLoading ? 'Saving...' : 'Save Advanced Settings'}
                      </button>
                      {settingsSaved && (
                        <span className="text-green-600 dark:text-green-400 text-sm font-medium animate-pulse">
                          ✓ Saved!
                        </span>
                      )}
                    </div>
                  </div>

                  {/* ── Model Health ── */}
                  <div>
                    <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-3 pb-2 border-b border-gray-200 dark:border-gray-700">
                      Model Health
                    </h4>
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Attempt and failure counts across all users. Edit <code className="text-xs bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded">models_config.py</code> to disable flaky models, then click Reload Settings.
                      </p>
                      <button
                        onClick={fetchModelStats}
                        disabled={loadingModelStats}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                      >
                        {loadingModelStats ? 'Loading...' : 'Refresh'}
                      </button>
                    </div>

                    {modelStats.length === 0 && !loadingModelStats && (
                      <p className="text-sm text-gray-400 dark:text-gray-500 py-4 text-center">
                        No data yet — stats appear after the first Virgo query.
                      </p>
                    )}

                    {modelStats.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-gray-200 dark:border-gray-700">
                              <th className="text-left py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Model</th>
                              <th className="text-left py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Provider</th>
                              <th className="text-right py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Attempts</th>
                              <th className="text-right py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Failures</th>
                              <th className="text-right py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Fail Rate</th>
                              <th className="text-right py-2 pr-4 font-medium text-gray-600 dark:text-gray-400">Avg Duration</th>
                              <th className="text-left py-2 font-medium text-gray-600 dark:text-gray-400">Last Failed</th>
                            </tr>
                          </thead>
                          <tbody>
                            {modelStats.map((row, i) => {
                              const failRate = parseFloat(row.fail_rate || 0);
                              const rateColor = failRate === 0
                                ? 'text-green-600 dark:text-green-400'
                                : failRate < 10
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-red-600 dark:text-red-400';
                              const avgMs = parseInt(row.avg_duration_ms || 0);
                              const durColor = avgMs === 0
                                ? 'text-gray-400 dark:text-gray-500'
                                : avgMs < 30000
                                ? 'text-green-600 dark:text-green-400'
                                : avgMs < 90000
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-red-600 dark:text-red-400';
                              const durLabel = avgMs === 0 ? '—'
                                : avgMs < 1000 ? `${avgMs}ms`
                                : `${(avgMs / 1000).toFixed(1)}s`;
                              const lastFailed = row.last_failed_at
                                ? new Date(row.last_failed_at).toLocaleString()
                                : '—';
                              return (
                                <tr key={i} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                  <td className="py-2 pr-4 text-gray-900 dark:text-white font-mono text-xs">{row.label || row.model_id}</td>
                                  <td className="py-2 pr-4 text-gray-500 dark:text-gray-400 capitalize">{row.provider}</td>
                                  <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300">{row.attempts}</td>
                                  <td className="py-2 pr-4 text-right text-gray-700 dark:text-gray-300">{row.failures}</td>
                                  <td className={`py-2 pr-4 text-right font-medium ${rateColor}`}>{failRate}%</td>
                                  <td className={`py-2 pr-4 text-right font-medium ${durColor}`}>{durLabel}</td>
                                  <td className="py-2 text-gray-500 dark:text-gray-400 text-xs">{lastFailed}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                </div>
              )}

              {/* Credit Limits Tab */}
              {ownerSubTab === 'credit-limits' && (
                <>
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      📊 Configure daily credit limits for each tier and role combination.
                    </p>
                  </div>

                  {/* Credit Limit Policies */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Credit Limit Policies
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      Configure daily credit limits for each tier and role combination. These policies apply to all users unless they have an individual override set.
                    </p>

                    {policiesLoading && creditPolicies.length === 0 ? (
                      <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center">
                        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
                        <p className="text-gray-700 dark:text-gray-300">Loading policies...</p>
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse">
                            <thead>
                              <tr className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Tier</th>
                                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Role</th>
                                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Daily Limit</th>
                                <th className="text-left px-4 py-3 text-sm font-semibold text-gray-700 dark:text-gray-300">Description</th>
                              </tr>
                            </thead>
                            <tbody>
                              {creditPolicies.map((policy, idx) => (
                                <tr key={`${policy.tier}-${policy.role}`} className="border-b border-gray-200 dark:border-gray-700">
                                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white capitalize">{policy.tier}</td>
                                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white capitalize">{policy.role}</td>
                                  <td className="px-4 py-3">
                                    <input
                                      type="number"
                                      min="1"
                                      max="100000"
                                      value={policy.monthly_credit_limit}
                                      onChange={(e) => {
                                        const newPolicies = [...creditPolicies];
                                        newPolicies[idx].monthly_credit_limit = parseInt(e.target.value) || 0;
                                        setCreditPolicies(newPolicies);
                                      }}
                                      className="w-32 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded px-3 py-1.5 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{policy.description}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Save Policies Button */}
                        <div className="flex items-center gap-4 mt-4">
                          <button
                            onClick={async () => {
                              for (const policy of creditPolicies) {
                                await updateCreditPolicy(policy.tier, policy.role, policy.monthly_credit_limit);
                              }
                            }}
                            disabled={policiesLoading}
                            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-lg transition-colors"
                          >
                            {policiesLoading ? 'Saving...' : 'Save Policies'}
                          </button>
                          {policiesSaved && (
                            <span className="text-green-600 dark:text-green-400 font-medium animate-pulse">
                              ✓ Policies saved successfully!
                            </span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </>
              )}

              {/* Database Publishing Tab */}
              {ownerSubTab === 'database-publishing' && (
                <>
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
                    <p className="text-sm text-purple-800 dark:text-purple-200">
                      🗄️ Manage datasource publishing to organizations. Publish databases globally or to specific organizations.
                    </p>
                  </div>

                  {publishingLoading && !publishedDatasources.length ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">Loading published datasources...</span>
                    </div>
                  ) : publishedDatasources.length === 0 ? (
                    <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center">
                      <Database className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                        No published datasources found
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Users need to mark their datasources as "published" before they appear here.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Datasource List */}
                      {publishedDatasources.map((ds) => (
                        <div
                          key={ds.id}
                          className={`bg-white dark:bg-gray-800 border rounded-lg p-4 cursor-pointer transition-all ${selectedPublishedDatasource?.id === ds.id
                              ? 'border-blue-500 ring-2 ring-blue-200 dark:ring-blue-900'
                              : 'border-gray-300 dark:border-gray-700 hover:border-blue-400'
                            }`}
                          onClick={() => {
                            if (selectedPublishedDatasource?.id === ds.id) {
                              // Collapse if already selected
                              setSelectedPublishedDatasource(null);
                            } else {
                              // Expand and fetch permissions
                              setSelectedPublishedDatasource(ds);
                              fetchDatasourcePermissions(ds.id);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Database className="w-4 h-4 text-blue-500" />
                                <h3 className="font-medium text-gray-900 dark:text-white">
                                  {ds.datasource_name}
                                </h3>
                                <span className="text-xs px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded">
                                  {ds.database_type}
                                </span>
                              </div>
                              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-2">
                                <div className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  <span>Creator: {ds.creator_email}</span>
                                </div>
                                {ds.creator_org_name && (
                                  <div className="flex items-center gap-1">
                                    <Layers className="w-3 h-3" />
                                    <span>Org: {ds.creator_org_name}</span>
                                  </div>
                                )}
                                <div className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  <span>{ds.permission_count} {ds.permission_count === 1 ? 'permission' : 'permissions'}</span>
                                </div>
                              </div>
                            </div>
                            {selectedPublishedDatasource?.id === ds.id && (
                              <ChevronDown className="w-5 h-5 text-blue-500" />
                            )}
                          </div>

                          {/* Permission Management (expanded when selected) */}
                          {selectedPublishedDatasource?.id === ds.id && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                              {/* Current Permissions */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Current Permissions
                                </h4>
                                {datasourcePermissions.length === 0 ? (
                                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                    No permissions granted yet. Add organizations below to grant access.
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    {datasourcePermissions.map((perm, idx) => (
                                      <div
                                        key={idx}
                                        className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded px-3 py-2"
                                      >
                                        <div className="flex items-center gap-2">
                                          {perm.org_id === null ? (
                                            <>
                                              <Zap className="w-4 h-4 text-yellow-500" />
                                              <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                Global (All Organizations)
                                              </span>
                                            </>
                                          ) : (
                                            <>
                                              <Layers className="w-4 h-4 text-blue-500" />
                                              <span className="text-sm text-gray-900 dark:text-white">
                                                {perm.org_name || perm.org_id}
                                              </span>
                                            </>
                                          )}
                                          <span className="text-xs text-gray-500 dark:text-gray-400">
                                            (granted {new Date(perm.granted_at).toLocaleDateString()})
                                          </span>
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Revoke access for ${perm.org_id === null ? 'all organizations' : perm.org_name || perm.org_id}?`)) {
                                              revokeDatasourcePermission(ds.id, perm.org_id);
                                            }
                                          }}
                                          disabled={publishingLoading}
                                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                          title="Revoke permission"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Grant New Permission */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Grant Access
                                </h4>
                                <div className="flex gap-2">
                                  <select
                                    className="flex-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded px-3 py-2 text-sm text-gray-900 dark:text-white"
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        const orgId = e.target.value === 'global' ? null : e.target.value;
                                        grantDatasourcePermission(ds.id, orgId);
                                        e.target.value = ''; // Reset dropdown
                                      }
                                    }}
                                    disabled={publishingLoading}
                                  >
                                    <option value="">Select organization...</option>
                                    <option value="global">🌍 Global (All Organizations)</option>
                                    {organizations.map((org) => (
                                      <option key={org.org_id} value={org.org_id}>
                                        {org.org_name} ({org.user_count} users)
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Select an organization to grant access to this datasource
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Organizations Tab */}
          {settingsTab === 'owner' && ownerSubTab === 'organizations' && user?.role === 'owner' && (
            <>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-green-800 dark:text-green-200">
                  🏢 Manage Organizations: Create organizations and control user membership. Users with matching email domains will automatically join when they register.
                </p>
              </div>

              {/* Create Organization Button */}
              <div className="mb-6">
                <button
                  onClick={() => setShowCreateOrgModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                >
                  <PlusCircle className="w-4 h-4" />
                  Create Organization
                </button>
              </div>

              {/* Organizations List */}
              {orgsLoading && !organizations.length ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  <span className="ml-2 text-gray-600 dark:text-gray-400">Loading organizations...</span>
                </div>
              ) : organizations.length === 0 ? (
                <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center">
                  <Database className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                    No organizations found
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Create your first organization by clicking the button above.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {organizations.map((org) => (
                    <div
                      key={org.org_id}
                      className={`bg-white dark:bg-gray-800 border rounded-lg p-4 cursor-pointer transition-all ${selectedOrganization?.org_id === org.org_id
                          ? 'border-blue-500 dark:border-blue-400 ring-2 ring-blue-100 dark:ring-blue-900'
                          : 'border-gray-300 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'
                        }`}
                      onClick={() => {
                        if (selectedOrganization?.org_id === org.org_id) {
                          setSelectedOrganization(null);
                          setOrganizationMembers([]);
                        } else {
                          setSelectedOrganization(org);
                          fetchOrganizationMembers(org.org_id);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                              {org.org_name}
                            </h3>
                            {selectedOrganization?.org_id === org.org_id ? (
                              <ChevronDown className="w-5 h-5 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-500" />
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {org.email_domain && (
                              <span className="flex items-center gap-1">
                                📧 {org.email_domain}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              👥 {org.user_count} members
                            </span>
                            {org.created_at && (
                              <span className="flex items-center gap-1">
                                📅 {new Date(org.created_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm(`Are you sure you want to delete "${org.org_name}"? All members will be unassigned from this organization.`)) {
                              deleteOrganization(org.org_id, org.org_name);
                            }
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Delete Organization"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>

                      {/* Expanded: Show Members */}
                      {selectedOrganization?.org_id === org.org_id && (
                        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                              Members
                            </h4>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                fetchAvailableUsers(org.org_id);
                                setShowAddMembersModal(true);
                              }}
                              className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                            >
                              <UserPlus className="w-4 h-4" />
                              Add Members
                            </button>
                          </div>
                          {organizationMembers.length === 0 ? (
                            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                              No members yet. Users with @{org.email_domain || 'matching domain'} will auto-join on registration.
                            </p>
                          ) : (
                            <div className="space-y-2">
                              {organizationMembers.map((member) => (
                                <div
                                  key={member.user_id}
                                  className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded px-3 py-2"
                                >
                                  <div className="flex items-center gap-3">
                                    <User className="w-4 h-4 text-gray-400" />
                                    <div>
                                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {member.email}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Joined {new Date(member.joined_at).toLocaleDateString()}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span
                                      className={`px-2 py-1 text-xs font-medium rounded ${member.role === 'admin'
                                          ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                          : member.role === 'owner'
                                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                            : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                                        }`}
                                    >
                                      {member.role}
                                    </span>
                                    {member.role === 'user' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (window.confirm(`Make ${member.email} an Admin?`)) {
                                            updateUserRole(member.user_id, org.org_id, 'admin');
                                          }
                                        }}
                                        className="text-xs px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                                      >
                                        Promote to Admin
                                      </button>
                                    )}
                                    {member.role === 'admin' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (window.confirm(`Demote ${member.email} to User?`)) {
                                            updateUserRole(member.user_id, org.org_id, 'user');
                                          }
                                        }}
                                        className="text-xs px-2 py-1 bg-gray-500 hover:bg-gray-600 text-white rounded transition-colors"
                                      >
                                        Demote to User
                                      </button>
                                    )}
                                    {member.role !== 'owner' && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (window.confirm(`Remove ${member.email} from this organization?`)) {
                                            removeMemberFromOrg(org.org_id, member.user_id, member.email);
                                          }
                                        }}
                                        className="text-xs px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                                        title="Remove from Organization"
                                      >
                                        <UserMinus className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {/* Promo Codes Tab */}
          {settingsTab === 'owner' && ownerSubTab === 'promo-codes' && user?.role === 'owner' && (
            <>
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
                <p className="text-sm text-purple-800 dark:text-purple-200">
                  🎟️ Manage Promo Codes: Create promotional codes for user signups with customizable tiers and durations. After saving, use the "Reload Settings" button above to apply changes.
                </p>
              </div>

              {/* Add New Promo Code Button */}
              <div className="mb-6 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Active Promo Codes
                </h3>
                <button
                  onClick={() => {
                    setPromoCodeForm({ code: '', tier: 'pro', days: 30, expires: '' });
                    setEditingPromoCode(null);
                    setShowPromoCodeModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-colors shadow-sm"
                >
                  <PlusCircle className="w-4 h-4" />
                  Add Promo Code
                </button>
              </div>

              {/* Promo Codes Table */}
              {Object.keys(settings.promo_codes || {}).length === 0 ? (
                <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center">
                  <CreditCard className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                    No promo codes configured
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Create your first promo code to offer promotional tiers to new users.
                  </p>
                </div>
              ) : (
                <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Code
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Tier
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Duration
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Expires
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {Object.entries(settings.promo_codes || {}).map(([code, config]) => {
                        const expiresDate = new Date(config.expires);
                        const isExpired = expiresDate < new Date();

                        return (
                          <tr key={code} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <code className="px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded font-mono text-sm">
                                  {code}
                                </code>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-semibold rounded ${config.tier === 'pro'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                  : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
                                }`}>
                                {config.tier.toUpperCase()}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {config.days} days
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                              {expiresDate.toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`px-2 py-1 text-xs font-semibold rounded ${isExpired
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                  : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                }`}>
                                {isExpired ? 'Expired' : 'Active'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                onClick={() => {
                                  setEditingPromoCode(code);
                                  setPromoCodeForm({
                                    code: code,
                                    tier: config.tier,
                                    days: config.days,
                                    expires: config.expires
                                  });
                                  setShowPromoCodeModal(true);
                                }}
                                className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-4"
                              >
                                <Edit className="w-4 h-4 inline" />
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Delete promo code "${code}"?`)) {
                                    const updatedCodes = { ...settings.promo_codes };
                                    delete updatedCodes[code];
                                    setSettings(prev => ({ ...prev, promo_codes: updatedCodes }));
                                  }
                                }}
                                className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4 inline" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Save Button */}
              <div className="mt-6 flex items-center gap-4">
                <button
                  onClick={saveUserSettings}
                  disabled={settingsLoading}
                  className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  {settingsLoading ? 'Saving...' : 'Save Settings'}
                </button>
                {settingsSaved && (
                  <span className="text-green-600 dark:text-green-400 font-medium animate-pulse">
                    ✓ Settings saved successfully!
                  </span>
                )}
              </div>

              {/* Info Message */}
              <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  💡 <strong>Tip:</strong> After saving, click the "Reload" button above to apply promo codes immediately without restarting the server.
                </p>
              </div>
            </>
          )}

          {/* Owner Audit Logs Tab */}
          {settingsTab === 'owner' && ownerSubTab === 'audit' && user?.role === 'owner' && (
            <AuditDashboard token={localStorage.getItem('token')} showAll={true} />
          )}

          {/* Admin Tab */}
          {settingsTab === 'admin' && (user?.role === 'admin' || user?.role === 'owner') && (
            <>
              {/* Admin Sub-tabs */}
              <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
                <nav className="-mb-px flex space-x-6">
                  <button
                    onClick={() => setAdminSubTab('prompts')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${adminSubTab === 'prompts'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Prompt Libraries
                  </button>
                  <button
                    onClick={() => setAdminSubTab('audit')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${adminSubTab === 'audit'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Audit Logs
                  </button>
                  <button
                    onClick={() => setAdminSubTab('security')}
                    className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${adminSubTab === 'security'
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                      }`}
                  >
                    Security
                  </button>
                </nav>
              </div>

              {/* Security Sub-tab */}
              {adminSubTab === 'security' && (
                <div className="space-y-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      🔒 Configure security settings for your organization's Taurus (local AI) usage.
                    </p>
                  </div>

                  {user?.org_id ? (
                    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Taurus Bash Restrictions</h3>

                      <div className="flex items-start gap-3">
                        <input
                          id="org_bash_enforce"
                          type="checkbox"
                          checked={orgBashEnforced}
                          onChange={(e) => updateOrgBashRestrictions(e.target.checked)}
                          disabled={orgBashLoading}
                          className="mt-1 h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-800 disabled:opacity-50"
                        />
                        <div className="flex-1">
                          <label htmlFor="org_bash_enforce" className="block text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer">
                            Enforce Bash Restrictions for All Organization Members
                          </label>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            When enabled, all organization members will have Taurus bash command restrictions enforced, regardless of their personal settings.
                            This blocks potentially dangerous commands like rm, curl, sudo, and others to prevent accidental data loss or security issues.
                          </p>
                          {orgBashLoading && (
                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-2 flex items-center gap-1">
                              <span className="animate-spin">⟳</span> Updating...
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="mt-4 bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <strong>Blocked commands include:</strong> rm, curl, wget, sudo, ssh, chmod, docker, python, pip, and ~30 others.
                          These restrictions help prevent accidental or malicious system modifications while using Taurus locally.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-8 text-center">
                      <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                        Organization Required
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        Security settings are only available for users who are part of an organization.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Audit Dashboard Sub-tab */}
              {adminSubTab === 'audit' && (
                <AuditDashboard token={localStorage.getItem('token')} />
              )}

              {/* Prompt Libraries Sub-tab */}
              {adminSubTab === 'prompts' && (
                <>
                  <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
                    <p className="text-sm text-purple-800 dark:text-purple-200">
                      📚 Manage Prompt Libraries: Create curated footnote collections and publish them to organizations.
                    </p>
                  </div>

                  {/* Create Library Button */}
                  <div className="mb-6">
                    <button
                      onClick={() => {
                        setShowCreateLibraryModal(true);
                        trackFeatureUse('prompt_library_create_opened');
                      }}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    >
                      <PlusCircle className="w-4 h-4" />
                      Create New Library
                    </button>
                  </div>

                  {/* Libraries List */}
                  {librariesOrgError ? (
                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-8 text-center">
                      <Info className="w-12 h-12 mx-auto mb-4 text-yellow-600 dark:text-yellow-500" />
                      <p className="text-gray-900 dark:text-gray-100 font-medium mb-2">
                        Organization Required
                      </p>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                        You must be part of an organization to create and manage Prompt Libraries.
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Please contact your administrator or use the Owner panel to create an organization first.
                      </p>
                    </div>
                  ) : librariesLoading && !promptLibraries.length ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                      <span className="ml-2 text-gray-600 dark:text-gray-400">Loading Prompt Libraries...</span>
                    </div>
                  ) : promptLibraries.length === 0 ? (
                    <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center">
                      <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                        No Prompt Libraries found
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Create your first library by clicking the button above.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Library List */}
                      {promptLibraries.map((library) => (
                        <div
                          key={library.id}
                          className={`bg-white dark:bg-gray-800 border rounded-lg p-4 cursor-pointer transition-all ${selectedLibrary?.id === library.id
                              ? 'border-purple-500 ring-2 ring-purple-200 dark:ring-purple-900'
                              : 'border-gray-300 dark:border-gray-700 hover:border-purple-400'
                            }`}
                          onClick={() => {
                            if (selectedLibrary?.id === library.id) {
                              // Collapse if already selected
                              setSelectedLibrary(null);
                            } else {
                              // Expand and fetch permissions
                              setSelectedLibrary(library);
                              fetchLibraryPermissions(library.id);
                            }
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <BookOpen className="w-4 h-4 text-purple-500" />
                                <h3 className="font-medium text-gray-900 dark:text-white">
                                  {library.library_name}
                                </h3>
                              </div>
                              {library.description && (
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {library.description}
                                </p>
                              )}
                              <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400 mt-2">
                                <div className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  <span>Creator: {library.creator_email}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <FileText className="w-3 h-3" />
                                  <span>{library.footnote_count} {library.footnote_count === 1 ? 'footnote' : 'footnotes'}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" />
                                  <span>{library.permission_count} {library.permission_count === 1 ? 'permission' : 'permissions'}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {selectedLibrary?.id === library.id && (
                                <ChevronDown className="w-5 h-5 text-purple-500" />
                              )}
                            </div>
                          </div>

                          {/* Permission Management (expanded when selected) */}
                          {selectedLibrary?.id === library.id && (
                            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-4">
                              {/* Current Permissions */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Current Permissions
                                </h4>
                                {libraryPermissions.length === 0 ? (
                                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                                    No permissions granted yet. Add organizations below to grant access.
                                  </p>
                                ) : (
                                  <div className="space-y-2">
                                    {libraryPermissions.map((perm, idx) => (
                                      <div
                                        key={idx}
                                        className="flex items-center justify-between bg-gray-50 dark:bg-gray-900 rounded px-3 py-2"
                                      >
                                        <div className="flex items-center gap-2">
                                          <Layers className="w-4 h-4 text-purple-500" />
                                          <span className="text-sm text-gray-900 dark:text-white">
                                            {perm.org_name || perm.org_id}
                                          </span>
                                          <span className="text-xs text-gray-500 dark:text-gray-400">
                                            (granted {new Date(perm.granted_at).toLocaleDateString()})
                                          </span>
                                        </div>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (window.confirm(`Revoke access for ${perm.org_name || perm.org_id}?`)) {
                                              revokeLibraryPermission(library.id, perm.org_id);
                                            }
                                          }}
                                          disabled={librariesLoading}
                                          className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors disabled:opacity-50"
                                          title="Revoke permission"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </button>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>

                              {/* Grant New Permission */}
                              <div>
                                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  Grant Access
                                </h4>
                                <div className="flex gap-2">
                                  <select
                                    className="flex-1 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded px-3 py-2 text-sm text-gray-900 dark:text-white"
                                    onChange={(e) => {
                                      if (e.target.value) {
                                        grantLibraryPermission(library.id, e.target.value);
                                        e.target.value = ''; // Reset dropdown
                                      }
                                    }}
                                    disabled={librariesLoading}
                                  >
                                    <option value="">Select organization...</option>
                                    {organizations.map((org) => (
                                      <option key={org.org_id} value={org.org_id}>
                                        {org.org_name} ({org.user_count} users)
                                      </option>
                                    ))}
                                  </select>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                  Select an organization to grant access to this library
                                </p>
                              </div>

                              {/* Delete Library */}
                              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (window.confirm(`Are you sure you want to delete "${library.library_name}"? This will also delete all ${library.footnote_count} footnotes in this library.`)) {
                                      deletePromptLibrary(library.id);
                                    }
                                  }}
                                  disabled={librariesLoading}
                                  className="flex items-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition-colors disabled:opacity-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Delete Library
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* Plans Tab */}
          {settingsTab === 'plans' && (
            <>
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      Current Plan: {user?.tier === 'free' ? 'Free' : user?.tier === 'pro' ? 'Pro' : 'Team'}
                    </h3>
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                      {user?.tier === 'free' && 'You are currently on the Free plan with limited daily queries.'}
                      {user?.tier === 'pro' && 'You have access to Pro features with increased query limits.'}
                      {user?.tier === 'team' && 'You have full access to Team features and unlimited queries.'}
                    </p>
                    {creditStatus && !creditStatus.is_unlimited && (
                      <div className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Monthly Credits:</span>
                        <span className={`font-semibold ${creditStatus.credits_remaining <= 5 ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
                          {creditStatus.credits_remaining} / {creditStatus.monthly_limit} remaining
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => openAccountModal('plans')}
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all shadow-sm hover:shadow-md flex-shrink-0"
                  >
                    <CreditCard className="h-4 w-4" />
                    Manage Account
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Render schema tab content
  const renderSchemaTab = () => (
    <div className="p-6 bg-white dark:bg-gray-900 h-full overflow-y-auto">
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <Database className="h-6 w-6" />
        Data Preview
      </h2>
      {!schema ? (
        <div className="bg-gray-100 dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg p-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-700 dark:text-gray-300">Loading schema...</p>
        </div>
      ) : selectedTable ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <button
              className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:underline text-sm font-medium"
              onClick={() => setSelectedTable(null)}
            >
              <ChevronLeft className="h-4 w-4" /> Back to tables
            </button>
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-blue-700 dark:text-blue-300">{selectedTable.toUpperCase()}</h3>
              <button
                onClick={() => fetchTablePreview(selectedTable)}
                disabled={loadingTablePreview}
                className="flex items-center gap-2 px-3 py-1 bg-green-100 hover:bg-green-200 disabled:bg-gray-300 disabled:cursor-not-allowed text-green-700 disabled:text-gray-500 rounded text-sm font-medium transition-colors"
                title="Preview table data"
              >
                <Eye className="h-4 w-4" />
                {loadingTablePreview ? 'Loading...' : 'Preview Data'}
              </button>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            <input
              type="text"
              value={schemaSearch}
              onChange={e => setSchemaSearch(e.target.value)}
              placeholder="Search columns..."
              className="flex-1 p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>

          <div className="space-y-3">
            {filteredColumns(selectedTable).map(col => (
              <div key={col.name} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="font-mono font-medium text-gray-900 dark:text-gray-100">{col.name}</span>
                  {col.type && (
                    <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-200 dark:bg-gray-700 px-2 py-1 rounded">
                      {col.type}
                    </span>
                  )}
                </div>
                {col.value && (
                  <div className="mb-2">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Value: </span>
                    <span className="text-sm text-gray-800 dark:text-gray-200">{col.value}</span>
                  </div>
                )}
                {col.note && (
                  <div>
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Note: </span>
                    <span className="text-sm text-gray-800 dark:text-gray-200">{col.note}</span>
                  </div>
                )}
              </div>
            ))}
            {filteredColumns(selectedTable).length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No columns match your search.
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Header with context */}
          {activeDatasource ? (
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                Previewing: {activeDatasource.name}
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400">
                  ({formatDbType(activeDatasource.db_type)})
                </span>
              </h3>

              {/* Hint about files if any selected */}
              {selectedFiles.length > 0 && (
                <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="flex items-start gap-2 text-sm text-blue-800 dark:text-blue-200">
                    <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium">{selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected</span>
                      <p className="text-blue-600 dark:text-blue-300 mt-1">
                        💡 To preview files, click <Eye className="w-3 h-3 inline mx-1" /> icon next to each file in the Datasources panel
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}

          {/* Show tables if we have an active datasource */}
          {activeDatasource && Object.keys(schema).length > 0 ? (
            <>
              <h4 className="text-md font-medium text-gray-600 dark:text-gray-300 mt-4">Available Tables ({Object.keys(schema).length})</h4>
              <div className="grid gap-3">
                {Object.keys(schema).map(table => (
                  <button
                    key={table}
                    className="text-left p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700 transition-colors flex items-center justify-between group"
                    onClick={() => setSelectedTable(table)}
                  >
                    <div className="flex items-center gap-3">
                      <Table className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                      <div>
                        <span className="font-medium text-gray-900 dark:text-gray-100 block">{table.toUpperCase()}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {schema[table].columns.length} column{schema[table].columns.length !== 1 ? 's' : ''}
                          {schema[table].row_count !== null && schema[table].row_count !== undefined && (
                            <> • ~{schema[table].row_count.toLocaleString()} row{schema[table].row_count !== 1 ? 's' : ''}</>
                          )}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
                  </button>
                ))}
              </div>
            </>
          ) : !activeDatasource && selectedFiles.length > 0 ? (
            // Scenario 2: No DB, Files Selected
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <FileText className="w-6 h-6 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Database Selected</h3>
                  <p className="text-gray-700 dark:text-gray-300 mb-4">
                    You have {selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''} selected:
                  </p>
                  <ul className="space-y-1 mb-4">
                    {selectedFiles.slice(0, 5).map(file => (
                      <li key={file.id} className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-2">
                        <FileText className="w-3 h-3" />
                        {file.original_filename}
                      </li>
                    ))}
                    {selectedFiles.length > 5 && (
                      <li className="text-sm text-gray-500 dark:text-gray-500">
                        ...and {selectedFiles.length - 5} more
                      </li>
                    )}
                  </ul>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-start gap-2">
                      <Eye className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700 dark:text-gray-300">
                        <strong>To preview files:</strong> Click <Eye className="w-3 h-3 inline mx-1" /> icon next to each file in the Datasources panel
                      </p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Database className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700 dark:text-gray-300">
                        <strong>To preview a database:</strong> Activate one in the Datasources panel
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : !activeDatasource && selectedFiles.length === 0 ? (
            // Scenario 3: Nothing Selected
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-8">
              <div className="text-center max-w-md mx-auto">
                <Database className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Datasource Selected</h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Select a database or files to preview their structure
                </p>

                <div className="space-y-4 text-left">
                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start gap-3">
                      <Database className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">Preview a Database</h4>
                        <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          <li>1. Open Datasources panel</li>
                          <li>2. Click on a database to activate</li>
                          <li>3. Return here to see tables</li>
                        </ol>
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-900 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-start gap-3">
                      <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white mb-1">Preview Files</h4>
                        <ol className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
                          <li>1. Open Datasources panel</li>
                          <li>2. Upload or select files</li>
                          <li>3. Click <Eye className="w-3 h-3 inline mx-1" /> icon to preview</li>
                        </ol>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );

  // Render history tab content
  const renderHistoryTab = () => {
    const filteredSessions = chatSessions.filter(session =>
      session.title.toLowerCase().includes(chatHistorySearchQuery.toLowerCase()) ||
      session.lastMessage.toLowerCase().includes(chatHistorySearchQuery.toLowerCase())
    );

    return (
      <div className="p-6 bg-white dark:bg-gray-900 h-full overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Chat History</h2>
        </div>

        {/* Retention Policy Banner (Free Tier) */}
        {isAuthenticated && user?.tier === 'free' && (
          <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <p className="text-xs text-blue-800 dark:text-blue-200">
              💡 Free tier: Chat history kept for 14 days
            </p>
          </div>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={chatHistorySearchQuery}
              onChange={(e) => setChatHistorySearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>
        </div>

        {/* Chat Sessions */}
        {!isAuthenticated ? (
          <div className="text-center py-12">
            <MessageSquare size={48} className="mx-auto mb-4 text-gray-600" />
            <p className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-300">Sign in Required</p>
            <p className="text-sm text-gray-600 dark:text-gray-500">Sign in to view your chat history</p>
          </div>
        ) : chatHistoryLoading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
            <p className="text-gray-600 dark:text-gray-400">Loading chat history...</p>
          </div>
        ) : filteredSessions.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare size={48} className="mx-auto mb-4 text-gray-600" />
            <p className="text-lg font-medium mb-2 text-gray-700 dark:text-gray-300">No conversations yet</p>
            <p className="text-sm text-gray-600 dark:text-gray-500">Your chat history will appear here</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {filteredSessions.map((session) => {
                const isLoading = loadingConversationId === session.conversationId;
                return (
                  <div
                    key={session.sessionId}
                    onClick={() => !isLoading && handleSelectChat(session)}
                    className={`group border rounded-lg p-4 transition-colors ${isLoading
                        ? 'cursor-wait opacity-75'
                        : 'cursor-pointer'
                      } ${session.mode === 'taurus'
                        ? 'bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20 border-amber-200 dark:border-amber-800/30'
                        : 'bg-purple-50 dark:bg-purple-900/10 hover:bg-purple-100 dark:hover:bg-purple-900/20 border-purple-200 dark:border-purple-800/30'
                      }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-400 mb-2">
                          <History size={12} className="mr-1" />
                          {formatRelativeTime(session.timestamp)}
                          <span className="ml-2">
                            {session.messageCount} message{session.messageCount !== 1 ? 's' : ''}
                          </span>
                          {/* Mode Badge */}
                          <span className={`ml-2 px-2 py-0.5 rounded text-xs font-medium ${session.mode === 'taurus'
                              ? 'bg-amber-200 dark:bg-amber-800/50 text-amber-900 dark:text-amber-200'
                              : 'bg-purple-200 dark:bg-purple-800/50 text-purple-900 dark:text-purple-200'
                            }`}>
                            {session.mode === 'taurus' ? 'Taurus' : 'Virgo'}
                          </span>
                        </div>
                        <p className="text-gray-900 dark:text-white font-medium truncate">
                          {session.title}
                        </p>
                        {session.lastMessage && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 truncate mt-1">
                            {session.lastMessage}
                          </p>
                        )}
                      </div>
                      {isLoading ? (
                        <Loader2 size={16} className="animate-spin text-gray-500 dark:text-gray-400" />
                      ) : (
                        <ChevronRight size={16} className="text-gray-500 dark:text-gray-500 group-hover:text-gray-700 dark:group-hover:text-gray-300" />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More Button */}
            {chatHistoryHasMore && (
              <div className="mt-6">
                <button
                  onClick={() => fetchChatHistory(false)}
                  disabled={chatHistoryLoadingMore}
                  className="w-full py-3 px-4 bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2 font-medium"
                >
                  {chatHistoryLoadingMore ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Loading...</span>
                    </>
                  ) : (
                    <span>Load more</span>
                  )}
                </button>
              </div>
            )}

            {/* Stats */}
            <div className="mt-4 text-center">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {chatHistoryHasMore ? (
                  <>Showing {chatSessions.length} session{chatSessions.length !== 1 ? 's' : ''} • More available</>
                ) : (
                  <>All {chatSessions.length} session{chatSessions.length !== 1 ? 's' : ''} loaded</>
                )}
              </p>
            </div>
          </>
        )}
      </div>
    );
  };

  // Render tab content
  const renderTabContent = (tab) => {
    switch (tab.type) {
      case TAB_TYPES.CHAT:
        return renderChatTab(tab);
      case TAB_TYPES.SETTINGS:
        return renderSettingsTab();
      case TAB_TYPES.SCHEMA:
        return renderSchemaTab();
      case TAB_TYPES.HISTORY:
        return renderHistoryTab();
      default:
        return <div>Unknown tab type</div>;
    }
  };

  return (
    <Routes>
      {/* Reset Password Page */}
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Email Verification Page */}
      <Route path="/verify-email" element={<VerifyEmailPage />} />

      {/* OAuth Callback Page */}
      <Route path="/oauth/callback" element={<OAuthCallback />} />

      {/* Shared Chat View */}
      <Route path="/shared/:token" element={<SharedChatView />} />


      {/* Main App */}
      <Route path="*" element={
        <div className={`fixed inset-0 flex overflow-hidden ${darkMode ? 'dark' : ''}`} style={{ height: 'var(--app-height, 100dvh)' }}>
          {/* Loading Overlay - shown during initial sign-in data loading */}
          {isInitializing && (
            <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-8 flex flex-col items-center space-y-4">
                <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
                <div className="text-center">
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">Initializing...</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Loading your workspace</p>
                </div>
              </div>
            </div>
          )}

          {/* Toast Notification */}
          {notification && (
            <div className="fixed top-4 right-4 z-[9999] animate-slide-in-right">
              <div className={`rounded-lg shadow-lg p-4 flex items-center space-x-3 min-w-[300px] max-w-md ${notification.type === 'success'
                  ? 'bg-green-50 dark:bg-green-900 border border-green-200 dark:border-green-700'
                  : notification.type === 'info'
                    ? 'bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-700'
                    : 'bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700'
                }`}>
                {notification.type === 'success' ? (
                  <Check className="h-5 w-5 text-green-600 dark:text-green-300 flex-shrink-0" />
                ) : notification.type === 'info' ? (
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-300 flex-shrink-0" />
                ) : (
                  <XCircle className="h-5 w-5 text-red-600 dark:text-red-300 flex-shrink-0" />
                )}
                <p className={`text-sm font-medium ${notification.type === 'success'
                    ? 'text-green-800 dark:text-green-200'
                    : notification.type === 'info'
                      ? 'text-blue-800 dark:text-blue-200'
                      : 'text-red-800 dark:text-red-200'
                  }`}>
                  {notification.message}
                </p>
                <button
                  onClick={() => setNotification(null)}
                  className={`ml-auto flex-shrink-0 ${notification.type === 'success'
                      ? 'text-green-600 dark:text-green-300 hover:text-green-800 dark:hover:text-green-100'
                      : notification.type === 'info'
                        ? 'text-blue-600 dark:text-blue-300 hover:text-blue-800 dark:hover:text-blue-100'
                        : 'text-red-600 dark:text-red-300 hover:text-red-800 dark:hover:text-red-100'
                    }`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Left Sidebar */}
          <div className={`bg-white dark:bg-gray-950 border-r border-gray-200 dark:border-gray-800 transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'} flex-shrink-0 flex flex-col h-full min-h-0`}>
            <div className="p-4 flex-1 flex flex-col min-h-0 overflow-y-auto">
              {/* Logo */}
              <div className={`flex items-center mb-8 ${sidebarCollapsed ? 'justify-center' : 'justify-between'}`}>
                {sidebarCollapsed ? (
                  // Collapsed: Show only toggle button centered
                  <button
                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                    className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                    title="Expand sidebar (Ctrl+/)"
                  >
                    <Menu className="h-4 w-4" />
                  </button>
                ) : (
                  // Expanded: Show logo and toggle button
                  <>
                    <a
                      href="https://verbagpt.com/#white-label"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 cursor-pointer"
                      title="Click to learn about white labeling for teams"
                    >
                      <CodeIcon className="h-8 w-8 text-blue-500" />
                      <span className="text-xl font-bold text-gray-900 dark:text-white">VerbaGPT</span>
                      {demoMode && (
                        <span className="px-2 py-1 text-xs bg-blue-900 text-blue-200 rounded-full">
                          Demo
                        </span>
                      )}
                    </a>

                    <button
                      onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                      className="p-1.5 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors"
                      title="Collapse sidebar (Ctrl+/)"
                    >
                      <Menu className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>

              {/* Navigation */}
              <nav className="space-y-2">
                <button
                  onClick={createNewChatTab}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
                  title={sidebarCollapsed ? 'New Chat' : ''}
                >
                  <Plus className="h-5 w-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>New Chat</span>}
                </button>

                <button
                  data-tour="add-datasource"
                  onClick={() => {
                    setAddDatasourceModalOpen(true);
                    trackFeatureUse('add_datasource_modal_opened', { source: 'sidebar' });
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
                  title={sidebarCollapsed ? 'Add Datasource' : ''}
                >
                  <Layers className="h-5 w-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>Add Datasource</span>}
                </button>

                <button
                  onClick={() => openTab(TAB_TYPES.SETTINGS, 'Settings')}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
                  title={sidebarCollapsed ? 'Settings' : ''}
                >
                  <Settings className="h-5 w-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>Settings</span>}
                </button>

                <button
                  data-tour="data-notes"
                  onClick={() => {
                    if (activeDatasource) {
                      setSelectedDatasource(activeDatasource.name);
                      setDataNotesModalOpen(true);
                      trackFeatureUse('data_notes_opened', { source: 'toolbar' });
                    } else {
                      alert('Please add and activate a datasource first');
                    }
                  }}
                  disabled={!activeDatasource}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors ${sidebarCollapsed ? 'justify-center' : ''} ${!activeDatasource ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={sidebarCollapsed ? 'Data Notes' : ''}
                >
                  <FileText className="h-5 w-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>Data Notes</span>}
                </button>

                <button
                  data-tour="data-preview"
                  onClick={() => openTab(TAB_TYPES.SCHEMA, 'Data Preview')}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
                  title={sidebarCollapsed ? 'Data Preview' : ''}
                >
                  <Database className="h-5 w-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>Data Preview</span>}
                </button>

                <button
                  onClick={() => openTab(TAB_TYPES.HISTORY, 'History')}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
                  title={sidebarCollapsed ? 'Chat History' : ''}
                >
                  <History className="h-5 w-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span>Chat History</span>}
                </button>

                {/* Divider */}
                <div className="my-2 border-t border-gray-300 dark:border-gray-700"></div>

                {/* Chat mode toggle: Virgo <-> Taurus */}
                {!sidebarCollapsed ? (
                  <div data-tour="mode-switcher" className="w-full bg-gray-100 dark:bg-gray-800 rounded-lg p-1 flex flex-col gap-1">
                    <button
                      onClick={() => {
                        if (chatMode !== 'virgo') {
                          const activeTab = tabs.find(tab => tab.id === activeTabId);
                          if (activeTab && activeTab.messages && activeTab.messages.length > 0) {
                            const newTab = {
                              id: uuidv4(),
                              type: TAB_TYPES.CHAT,
                              title: 'New Chat',
                              sessionId: uuidv4(),
                              messages: [],
                              inputMessage: '',
                              isLoading: false,
                              mode: 'virgo',
                              isSaved: true,
                              savedConversationId: null,
                              taurusDatasource: null
                            };
                            setTabs([...tabs, newTab]);
                            setActiveTabId(newTab.id);
                          } else if (activeTab) {
                            updateTabData(activeTab.id, { mode: 'virgo' });
                          }
                          setChatMode('virgo');
                          localStorage.setItem('chatMode', 'virgo');
                          trackModeSwitch('virgo');
                        }
                      }}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md transition-all ${chatMode === 'virgo'
                          ? 'bg-purple-500 text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                    >
                      <Sparkles className="h-4 w-4" />
                      <span className="text-sm font-medium">Virgo</span>
                    </button>
                    <button
                      onClick={() => {
                        if (chatMode !== 'taurus') {
                          const activeTab = tabs.find(tab => tab.id === activeTabId);
                          if (activeTab && activeTab.messages && activeTab.messages.length > 0) {
                            const newTab = {
                              id: uuidv4(),
                              type: TAB_TYPES.CHAT,
                              title: 'New Chat',
                              sessionId: uuidv4(),
                              messages: [],
                              inputMessage: '',
                              isLoading: false,
                              mode: 'taurus',
                              isSaved: true,
                              savedConversationId: null,
                              taurusDatasource: null
                            };
                            setTabs([...tabs, newTab]);
                            setActiveTabId(newTab.id);
                          } else if (activeTab) {
                            updateTabData(activeTab.id, { mode: 'taurus' });
                          }
                          setChatMode('taurus');
                          localStorage.setItem('chatMode', 'taurus');
                          trackModeSwitch('taurus');
                        }
                      }}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md transition-all ${chatMode === 'taurus'
                          ? 'bg-amber-600 text-white shadow-sm'
                          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                        }`}
                    >
                      <Mountain className="h-4 w-4" />
                      <span className="text-sm font-medium">Taurus</span>
                    </button>
                  </div>
                ) : (
                  // Collapsed sidebar - show single toggle button
                  <button
                    onClick={() => {
                      const newMode = chatMode === 'virgo' ? 'taurus' : 'virgo';
                      const activeTab = tabs.find(tab => tab.id === activeTabId);
                      if (activeTab && activeTab.messages && activeTab.messages.length > 0) {
                        const newTab = {
                          id: uuidv4(),
                          type: TAB_TYPES.CHAT,
                          title: 'New Chat',
                          sessionId: uuidv4(),
                          messages: [],
                          inputMessage: '',
                          isLoading: false,
                          mode: newMode,
                          isSaved: true,
                          savedConversationId: null,
                          taurusDatasource: null
                        };
                        setTabs([...tabs, newTab]);
                        setActiveTabId(newTab.id);
                      } else if (activeTab) {
                        updateTabData(activeTab.id, { mode: newMode });
                      }
                      setChatMode(newMode);
                      localStorage.setItem('chatMode', newMode);
                    }}
                    className="w-full flex items-center justify-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    title={`Switch to ${chatMode === 'virgo' ? 'Taurus' : 'Virgo'} mode`}
                  >
                    {chatMode === 'virgo' ? (
                      <Sparkles className="h-5 w-5 flex-shrink-0 text-purple-500" />
                    ) : (
                      <Mountain className="h-5 w-5 flex-shrink-0 text-amber-600" />
                    )}
                  </button>
                )}

                {/* Theme toggle button */}
                <button
                  onClick={() => setDarkMode(prev => !prev)}
                  className={`w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors ${sidebarCollapsed ? 'justify-center' : ''}`}
                  title={sidebarCollapsed ? (darkMode ? 'Switch to light mode' : 'Switch to dark mode') : ''}
                >
                  {darkMode ? (
                    <Sun className="h-5 w-5 flex-shrink-0" />
                  ) : (
                    <Moon className="h-5 w-5 flex-shrink-0" />
                  )}
                  {!sidebarCollapsed && (
                    <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  )}
                </button>
              </nav>
            </div>

            {/* Bottom section */}
            <div className="mt-auto p-4">
              <div className="space-y-2">
                {/* Credit limit counter - shown for authenticated non-unlimited users */}
                {isAuthenticated && creditStatus && !creditStatus.is_unlimited && !sidebarCollapsed && (
                  <div className="relative">
                    <div
                      className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 transition-colors"
                      onClick={() => setCreditPopoverOpen(prev => !prev)}
                    >
                      <div className="flex items-center justify-between text-xs mb-1.5">
                        <span className="text-gray-600 dark:text-gray-400">Credits</span>
                        <span className={`font-semibold ${creditStatus.credits_remaining <= 5 ? 'text-red-500' : 'text-gray-900 dark:text-white'}`}>
                          {creditStatus.credits_remaining}/{creditStatus.monthly_limit}
                        </span>
                      </div>
                      <div className="h-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            creditStatus.credits_remaining / creditStatus.monthly_limit < 0.1 ? 'bg-red-500' :
                            creditStatus.credits_remaining / creditStatus.monthly_limit < 0.3 ? 'bg-yellow-500' :
                            'bg-blue-500'
                          }`}
                          style={{ width: `${Math.max(0, (creditStatus.credits_remaining / creditStatus.monthly_limit) * 100)}%` }}
                        />
                      </div>
                    </div>
                    {creditPopoverOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setCreditPopoverOpen(false)} />
                        <div className="absolute bottom-full left-0 right-0 mb-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3 z-50">
                          <div className="text-xs space-y-2">
                            <div className="flex justify-between text-gray-700 dark:text-gray-300">
                              <span>Used this month</span>
                              <span className="font-medium">{creditStatus.credits_used} / {creditStatus.monthly_limit}</span>
                            </div>
                            <div className="flex justify-between text-gray-500 dark:text-gray-400">
                              <span>Resets</span>
                              <span className="font-medium">
                                {creditStatus.resets_at
                                  ? new Date(creditStatus.resets_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                  : '—'}
                              </span>
                            </div>
                            {user?.tier === 'free' && (
                              <button
                                onClick={(e) => { e.stopPropagation(); setCreditPopoverOpen(false); openAccountModal('plans'); }}
                                className="w-full mt-1 px-2 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
                              >
                                Upgrade to Pro →
                              </button>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* User section */}
                {isAuthenticated ? (
                  <UserMenu
                    onOpenChatHistory={() => openTab(TAB_TYPES.HISTORY, 'Chat History')}
                    onOpenSettings={() => openTab(TAB_TYPES.SETTINGS, 'Settings')}
                    onOpenUpgrade={() => openAccountModal('plans')}
                    onOpenFeedback={() => setFeedbackModalOpen(true)}
                    onOpenChangelog={() => setShowChangelogModal(true)}
                    collapsed={sidebarCollapsed}
                  />
                ) : sidebarCollapsed ? (
                  // Collapsed auth buttons
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setAuthModalOpen(true);
                        setAuthModalMode('login');
                      }}
                      className="w-full flex items-center justify-center px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                      title="Sign in"
                    >
                      <User className="h-5 w-5" />
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setAuthModalOpen(true);
                        setAuthModalMode('login');
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors text-sm font-medium"
                    >
                      <User className="h-4 w-4" />
                      Sign in
                    </button>
                    <button
                      onClick={() => {
                        setAuthModalOpen(true);
                        setAuthModalMode('register');
                      }}
                      className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg transition-all duration-200 text-sm font-medium shadow-sm"
                    >
                      <User className="h-4 w-4" />
                      Get Started
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 min-w-0 flex flex-col overflow-hidden bg-gray-50 dark:bg-gray-900">
            {/* Tab Bar */}
            <div className="bg-gray-100 dark:bg-gray-950 border-b border-gray-300 dark:border-gray-800 px-4">
              <div className="flex items-center gap-1 overflow-x-auto">
                {tabs.map((tab) => {
                  // Calculate responsive max width based on number of tabs
                  let maxWidth = 'max-w-xs'; // Default: ~320px for 1-2 tabs
                  if (tabs.length >= 5) {
                    maxWidth = 'max-w-[120px]';
                  } else if (tabs.length >= 3) {
                    maxWidth = 'max-w-[200px]';
                  }

                  return (
                    <div key={tab.id} className="flex items-center">
                      <button
                        onClick={() => setActiveTabId(tab.id)}
                        className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${maxWidth} ${activeTabId === tab.id
                            ? 'text-gray-900 dark:text-white border-blue-500 bg-white dark:bg-gray-900'
                            : 'text-gray-600 dark:text-gray-400 border-transparent hover:text-gray-800 dark:hover:text-gray-300'
                          }`}
                      >
                        {tab.type === TAB_TYPES.CHAT && tab.mode === 'virgo' && <Sparkles className="h-4 w-4 flex-shrink-0 text-purple-500" />}
                        {tab.type === TAB_TYPES.CHAT && tab.mode === 'taurus' && <Mountain className="h-4 w-4 flex-shrink-0 text-amber-600" />}
                        {tab.type === TAB_TYPES.CHAT && !tab.mode && <MessageSquare className="h-4 w-4 flex-shrink-0" />}
                        {tab.type === TAB_TYPES.SETTINGS && <Settings className="h-4 w-4 flex-shrink-0" />}
                        {tab.type === TAB_TYPES.SCHEMA && <Database className="h-4 w-4 flex-shrink-0" />}
                        {tab.type === TAB_TYPES.HISTORY && <History className="h-4 w-4 flex-shrink-0" />}
                        <span className="truncate">{tab.title}</span>
                        {tab.isLoading && <Loader2 className="h-3 w-3 animate-spin ml-1 flex-shrink-0" />}
                      </button>
                      {tabs.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            closeTab(tab.id);
                          }}
                          className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 ml-1"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  );
                })}

                {/* Add Tab Button */}
                <button
                  onClick={createNewChatTab}
                  className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 rounded transition-colors ml-2"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 min-h-0 overflow-hidden">
              {activeTab ? renderTabContent(activeTab) : <div>No active tab</div>}
            </div>
          </div>

          {/* Table Preview Modal */}
          {tablePreviewModalOpen && tablePreviewData && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden p-6 relative mx-4">
                <button
                  className="absolute top-4 right-4 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                  onClick={() => setTablePreviewModalOpen(false)}
                  title="Close"
                >
                  <X className="h-6 w-6 text-gray-700 dark:text-gray-200" />
                </button>
                <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
                  <Table className="h-5 w-5" />
                  {tablePreviewData.table_name.toUpperCase()} - Data Preview
                </h2>
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Showing first {tablePreviewData.row_count} rows
                </div>

                {tablePreviewData.sample_data.length > 0 ? (
                  <div className="overflow-auto max-h-[70vh]">
                    <table className="min-w-full border border-gray-300 dark:border-gray-600 text-sm">
                      <thead className="sticky top-0 bg-gray-50 dark:bg-gray-800">
                        <tr>
                          {tablePreviewData.columns.map(col => {
                            const hasTooltip = col.note || col.value;
                            const tooltipContent = [
                              col.note && `Description: ${col.note}`,
                              col.value && `Values: ${col.value}`
                            ].filter(Boolean).join('\n\n');

                            return (
                              <th
                                key={col.name}
                                className={`border border-gray-300 dark:border-gray-600 px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200 ${hasTooltip ? 'cursor-help' : ''}`}
                                title={hasTooltip ? tooltipContent : ''}
                              >
                                <div className="flex flex-col">
                                  <div className="flex items-center gap-1">
                                    <span className="font-mono">{col.name}</span>
                                    {hasTooltip && (
                                      <div className="w-3 h-3 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 flex items-center justify-center text-xs font-bold">
                                        ?
                                      </div>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 dark:text-gray-400 font-normal">{col.type}</span>
                                </div>
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {tablePreviewData.sample_data.map((row, rowIdx) => (
                          <tr key={rowIdx} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                            {tablePreviewData.columns.map(col => (
                              <td key={col.name} className="border border-gray-300 dark:border-gray-600 px-3 py-2 text-gray-800 dark:text-gray-200">
                                <div className="max-w-xs truncate" title={String(row[col.name] || '')}>
                                  {row[col.name] !== null && row[col.name] !== undefined ? String(row[col.name]) : ''}
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                    No data available for this table
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Modals */}
          <EmailFirstAuthModal
            isOpen={authModalOpen}
            onClose={() => setAuthModalOpen(false)}
            onSuccess={(userData) => {
              // Chat history will be loaded when user opens History tab or sidebar
              // No need to load on login to avoid slow initial load
            }}
          />

          {/* Authentication Prompt Modal */}
          {authPromptModalOpen && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
              <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-xl border border-gray-300 dark:border-gray-700">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-900 mb-4">
                    <MessageSquare className="h-6 w-6 text-blue-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Ready to analyze your data?
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    Choose an option to get started with VerbaGPT
                  </p>

                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        setDemoMode(true);
                        setAuthPromptModalOpen(false);
                        const currentTab = tabs.find(t => t.id === activeTabId);
                        if (currentTab?.inputMessage?.trim()) {
                          sendMessage(currentTab.inputMessage);
                        }
                      }}
                      className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Try Demo Mode
                      <p className="text-xs text-blue-200 mt-1">Limited queries, no registration needed</p>
                    </button>

                    <button
                      onClick={() => {
                        setAuthPromptModalOpen(false);
                        setAuthModalOpen(true);
                        setAuthModalMode('login');
                      }}
                      className="w-full px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-medium"
                    >
                      Login
                    </button>

                    <button
                      onClick={() => {
                        setAuthPromptModalOpen(false);
                        setAuthModalOpen(true);
                        setAuthModalMode('register');
                      }}
                      className="w-full px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                    >
                      Sign Up for Full Access
                    </button>
                  </div>

                  <button
                    onClick={() => setAuthPromptModalOpen(false)}
                    className="mt-4 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Agentic workflow modals */}
          <TerminalConsoleModal
            isOpen={terminalConsoleOpen}
            onClose={() => {
              setTerminalConsoleOpen(false);
              setViewingHistoricalConsole(null); // Clear historical console when closing
            }}
            consoleOutput={viewingHistoricalConsole ? viewingHistoricalConsole.consoleOutput : consoleOutput}
            userQuestion={viewingHistoricalConsole ? viewingHistoricalConsole.userQuestion : (activeTab?.messages?.[activeTab.messages.length - 1]?.content)}
            isStreaming={viewingHistoricalConsole ? false : isConsoleStreaming}
            onStop={viewingHistoricalConsole ? null : stopVirgoStream}
          />

          <AddDatasourceModal
            isOpen={addDatasourceModalOpen}
            onClose={() => setAddDatasourceModalOpen(false)}
            onDatasourceAdded={handleDatasourceAdded}
            user={user}
            onOpenFilePanel={() => {
              setChatMode('virgo');
              setShowDataSourcesPanel(true);
            }}
          />

          <EditDatasourceModal
            isOpen={editDatasourceModalOpen}
            onClose={() => setEditDatasourceModalOpen(false)}
            onDatasourceUpdated={handleDatasourceUpdated}
            datasource={editingDatasource}
            user={user}
          />

          <DataNotesModal
            isOpen={dataNotesModalOpen}
            onClose={() => setDataNotesModalOpen(false)}
            datasourceName={selectedDatasource}
            datasource={datasources.find(ds => ds.name === selectedDatasource)}
            onDataNotesUpdated={fetchSampleQuestions}
          />

          {/* Review Notes Modal (Deep Review focus) */}
          <ReviewNotesModal
            isOpen={reviewNotesModalOpen}
            onClose={() => {
              setReviewNotesModalOpen(false);
              setPendingReviewMessage(null);
            }}
            onSubmit={(notes) => {
              if (pendingReviewMessage) {
                callDeepReview(pendingReviewMessage.message, pendingReviewMessage.tabId, notes);
              }
              setPendingReviewMessage(null);
            }}
          />

          {/* Unified Datasource Selection Modal (Taurus & Virgo) */}
          <DatasourceSelectionModal
            isOpen={showDatasourceModal}
            mode={datasourceModalMode}
            onClose={() => {
              setShowDatasourceModal(false);
              setDatasourceSearch('');
            }}
            onSelect={datasourceModalMode === 'taurus' ? handleSelectTaurusDatasource : handleSelectVirgoDatasource}
            datasources={datasources}
            currentSelection={datasourceModalMode === 'taurus' ? taurusSelectedDatasource : activeDatasource}
            searchValue={datasourceSearch}
            onSearchChange={setDatasourceSearch}
          />

          <SnippetModal
            isOpen={showSnippetModal}
            onClose={() => setShowSnippetModal(false)}
            snippets={snippets.filter(snippet => {
              // Show snippets that match current tab's mode or are set to 'both'
              const snippetMode = snippet.mode || 'virgo';
              const currentMode = activeTab?.mode || chatMode;
              return snippetMode === currentMode || snippetMode === 'both';
            })}
            initialText={activeTab?.inputMessage || ''}
            onSubmit={handleSnippetModalSubmit}
            onCreate={handleCreateFootnote}
            onEdit={handleEditFootnote}
            onDelete={handleDeleteFootnote}
            onUse={handleUseFootnote}
            mode={activeTab?.mode || chatMode}
            showNotification={showNotification}
            onImportSuccess={loadFootnotes}
          />

          <ShareChatModal
            isOpen={showShareChatModal}
            onClose={() => setShowShareChatModal(false)}
            messages={activeTab?.messages || []}
            datasource={activeTab?.mode === 'taurus' ? taurusSelectedDatasource?.name || 'Local Files' : activeDatasource?.name || 'Database'}
            mode={activeTab?.mode || chatMode}
            user={user}
            reviewContent={reviewContent[activeTabId]}  // Include review if it was generated
          />

          {/* Create Organization Modal */}
          {showCreateOrgModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full border border-gray-200 dark:border-gray-700">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Create Organization
                  </h2>
                  <button
                    onClick={() => setShowCreateOrgModal(false)}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Form */}
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const name = formData.get('name');
                    const emailDomain = formData.get('email_domain') || null;

                    const success = await createOrganization(name, emailDomain);
                    if (success) {
                      setShowCreateOrgModal(false);
                      e.target.reset();
                    }
                  }}
                  className="p-6 space-y-6"
                >
                  {/* Organization Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Organization Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g., Acme Corporation"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Email Domain */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email Domain (Optional)
                    </label>
                    <input
                      type="text"
                      name="email_domain"
                      placeholder="e.g., acme.com"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      If set, new users with matching email domains will automatically join this organization when they register.
                      Leave blank for manual user assignment only.
                    </p>
                    <p className="text-xs text-yellow-600 dark:text-yellow-500 mt-1">
                      ⚠️ Do not use personal domains like gmail.com, outlook.com, etc.
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setShowCreateOrgModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={orgsLoading}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {orgsLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <PlusCircle className="w-4 h-4" />
                          Create Organization
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add/Edit Promo Code Modal */}
          {showPromoCodeModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full border border-gray-200 dark:border-gray-700 my-8 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900 z-10">
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {editingPromoCode ? 'Edit Promo Code' : 'Add Promo Code'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowPromoCodeModal(false);
                      setEditingPromoCode(null);
                      setPromoCodeForm({ code: '', tier: 'pro', days: 30, expires: '' });
                    }}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Form */}
                <form
                  onSubmit={(e) => {
                    e.preventDefault();

                    // Validation
                    if (!promoCodeForm.code || !promoCodeForm.expires) {
                      alert('Please fill in all required fields');
                      return;
                    }

                    // Update promo codes
                    const updatedCodes = { ...settings.promo_codes };

                    // If editing and code changed, delete old code
                    if (editingPromoCode && editingPromoCode !== promoCodeForm.code) {
                      delete updatedCodes[editingPromoCode];
                    }

                    // Add/update new code
                    updatedCodes[promoCodeForm.code.toUpperCase()] = {
                      tier: promoCodeForm.tier,
                      days: parseInt(promoCodeForm.days),
                      expires: promoCodeForm.expires
                    };

                    setSettings(prev => ({ ...prev, promo_codes: updatedCodes }));
                    setShowPromoCodeModal(false);
                    setEditingPromoCode(null);
                    setPromoCodeForm({ code: '', tier: 'pro', days: 30, expires: '' });
                  }}
                  className="p-4 space-y-4"
                >
                  {/* Promo Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Promo Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={promoCodeForm.code}
                      onChange={(e) => setPromoCodeForm(prev => ({ ...prev, code: e.target.value }))}
                      required
                      placeholder="e.g., PRODUCTHUNT2025"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono uppercase"
                      style={{ textTransform: 'uppercase' }}
                      disabled={!!editingPromoCode}
                    />
                    {editingPromoCode && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Code cannot be changed when editing. Delete and recreate to change code.
                      </p>
                    )}
                  </div>

                  {/* Tier */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Tier <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={promoCodeForm.tier}
                      onChange={(e) => setPromoCodeForm(prev => ({ ...prev, tier: e.target.value }))}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="pro">Pro</option>
                      <option value="team">Team</option>
                    </select>
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Duration (days) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={promoCodeForm.days}
                      onChange={(e) => setPromoCodeForm(prev => ({ ...prev, days: e.target.value }))}
                      required
                      min="1"
                      placeholder="30"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      How many days of {promoCodeForm.tier} tier access
                    </p>
                  </div>

                  {/* Expiration Date */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Expiration Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={promoCodeForm.expires}
                      onChange={(e) => setPromoCodeForm(prev => ({ ...prev, expires: e.target.value }))}
                      required
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Users cannot redeem this code after this date
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => {
                        setShowPromoCodeModal(false);
                        setEditingPromoCode(null);
                        setPromoCodeForm({ code: '', tier: 'pro', days: 30, expires: '' });
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                    >
                      {editingPromoCode ? (
                        <>
                          <Check className="w-4 h-4" />
                          Update Code
                        </>
                      ) : (
                        <>
                          <PlusCircle className="w-4 h-4" />
                          Add Code
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Add Members to Organization Modal */}
          {showAddMembersModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-gray-200 dark:border-gray-700 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Add Members to {selectedOrganization?.org_name}
                  </h2>
                  <button
                    onClick={() => {
                      setShowAddMembersModal(false);
                      setSelectedUserIds([]);
                    }}
                    className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {availableUsers.length === 0 ? (
                    <div className="text-center py-8">
                      <User className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                        No available users
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedOrganization?.email_domain
                          ? `All users with @${selectedOrganization.email_domain} are already members or no such users exist.`
                          : 'All users are already assigned to organizations.'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 mb-4">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          {selectedOrganization?.email_domain && (
                            <>📧 Showing users with @{selectedOrganization.email_domain} who are not in any organization. </>
                          )}
                          Select users to add to this organization.
                        </p>
                      </div>

                      <div className="space-y-2">
                        {availableUsers.map((user) => (
                          <label
                            key={user.user_id}
                            className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg cursor-pointer transition-colors"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUserIds.includes(user.user_id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUserIds([...selectedUserIds, user.user_id]);
                                } else {
                                  setSelectedUserIds(selectedUserIds.filter(id => id !== user.user_id));
                                }
                              }}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.email}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                Registered {new Date(user.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <span className="px-2 py-1 text-xs font-medium rounded bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
                              {user.role}
                            </span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Footer */}
                <div className="border-t border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {selectedUserIds.length} user{selectedUserIds.length !== 1 ? 's' : ''} selected
                    </p>
                    {selectedUserIds.length > 0 && (
                      <button
                        onClick={() => setSelectedUserIds([])}
                        className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Clear selection
                      </button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddMembersModal(false);
                        setSelectedUserIds([]);
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        const success = await bulkAddMembers(selectedOrganization.org_id);
                        if (success) {
                          setShowAddMembersModal(false);
                        }
                      }}
                      disabled={selectedUserIds.length === 0 || orgsLoading}
                      className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {orgsLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Adding...
                        </>
                      ) : (
                        <>
                          <UserPlus className="w-4 h-4" />
                          Add {selectedUserIds.length > 0 ? selectedUserIds.length : ''} Member{selectedUserIds.length !== 1 ? 's' : ''}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Create Prompt Library Modal */}
          {showCreateLibraryModal && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 dark:border-gray-700">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-900">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Create Prompt Library
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreateLibraryModal(false);
                    }}
                    className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Form */}
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);

                    try {
                      setLibrariesLoading(true);
                      const token = localStorage.getItem('token');
                      if (!token) throw new Error('Not authenticated');

                      const response = await axios.post(
                        `${API_ENDPOINTS.main}/admin/prompt-libraries`,
                        formData,
                        {
                          headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'multipart/form-data',
                          },
                        }
                      );

                      alert(`Library created successfully with ${response.data.footnote_count} footnotes!`);
                      setShowCreateLibraryModal(false);
                      await fetchPromptLibraries();
                    } catch (error) {
                      console.error('Failed to create library:', error);
                      alert(error.response?.data?.detail || 'Failed to create library');
                    } finally {
                      setLibrariesLoading(false);
                    }
                  }}
                  className="p-6 space-y-6"
                >
                  {/* Library Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Library Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="library_name"
                      required
                      placeholder="e.g., McKinsey Powerpoints"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      rows="3"
                      placeholder="Brief description of this library..."
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                    />
                  </div>

                  {/* CSV File Upload */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      CSV File <span className="text-red-500">*</span>
                    </label>
                    <div className="flex items-center gap-3">
                      <label className="flex-1 cursor-pointer">
                        <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center hover:border-purple-400 dark:hover:border-purple-500 transition-colors">
                          <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            Click to upload CSV file
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                            Required headers: title, content, tags, mode
                          </p>
                        </div>
                        <input
                          type="file"
                          name="csv_file"
                          accept=".csv"
                          required
                          className="hidden"
                          onChange={(e) => {
                            const fileName = e.target.files?.[0]?.name;
                            if (fileName) {
                              e.target.parentElement.querySelector('p').textContent = fileName;
                            }
                          }}
                        />
                      </label>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      <strong>CSV Format:</strong> title,content,tags,mode<br />
                      <strong>Tags:</strong> Pipe-separated (e.g., "powerpoint|executive|summary")<br />
                      <strong>Mode:</strong> "virgo", "taurus", or "both"
                    </p>
                  </div>

                  {/* Publish to Own Org */}
                  <div>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="publish_to_own_org"
                        defaultChecked={true}
                        className="w-4 h-4 text-purple-600 bg-gray-100 border-gray-300 rounded focus:ring-purple-500 dark:focus:ring-purple-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Publish to my organization
                      </span>
                    </label>
                  </div>

                  {/* Additional Organizations */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Additional Organizations
                    </label>
                    <input
                      type="text"
                      name="additional_org_ids"
                      placeholder="Comma-separated org IDs (optional)"
                      className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-3 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      You can also grant permissions later from the library management screen
                    </p>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setShowCreateLibraryModal(false)}
                      className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 font-medium transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={librariesLoading}
                      className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {librariesLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Create Library
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          <EditFootnoteModal
            isOpen={showEditFootnoteModal}
            onClose={() => setShowEditFootnoteModal(false)}
            footnote={editingFootnote}
            onSave={handleSaveFootnote}
            userRole={user?.role}
          />

          <FilePanel
            isOpen={showFilePanel}
            onClose={() => setShowFilePanel(false)}
            currentPath={filePanelPath}
            onPathChange={setFilePanelPath}
            showNotification={showNotification}
          />

          <DataSourcesPanel
            isOpen={showDataSourcesPanel}
            onClose={() => setShowDataSourcesPanel(false)}
            // Database props
            datasources={datasources}
            activeDatasource={activeDatasource}
            onActivateDatasource={handleSelectVirgoDatasource}
            // File props
            selectedFiles={selectedFiles}
            onSelectFiles={handleSelectFiles}
            includeDatabase={includeDatabase}
            onIncludeDatabaseChange={handleIncludeDatabaseChange}
            // Notification
            showNotification={showNotification}
            // Projects
            activeProject={activeProject}
            onActivateProject={handleActivateProject}
            onDeactivateProject={handleDeactivateProject}
          />

          {/* Taurus Projects Panel */}
          <ProjectsPanel
            isOpen={showProjectsPanel}
            onClose={() => setShowProjectsPanel(false)}
            onNewProject={handleNewProject}
            onEditProject={handleEditProject}
            onRunProject={handleRunProject}
            onDeleteProject={handleDeleteProject}
            savedProjects={savedProjects}
            runningProject={runningProject}
            userTier={user?.tier || 'free'}
            onUpgrade={() => { setShowProjectsPanel(false); openAccountModal('plans'); }}
          />

          <UpgradePlansModal
            isOpen={upgradePlansModalOpen}
            onClose={() => setUpgradePlansModalOpen(false)}
            currentTier={user?.tier || 'free'}
            user={user}
            onBillingPortal={handleBillingPortal}
            billingPortalLoading={billingPortalLoading}
            defaultTab={upgradePlansDefaultTab}
          />

          {/* Feedback Modal */}
          <FeedbackModal
            isOpen={feedbackModalOpen}
            onClose={() => setFeedbackModalOpen(false)}
          />

          {/* Taurus Install Modal */}
          <TaurusInstallModal
            isOpen={taurusInstallModalOpen}
            onClose={() => setTaurusInstallModalOpen(false)}
            userEmail={user?.email}
          />

          {/* Video Tutorial Modal */}
          <VideoModal
            isOpen={videoModalOpen}
            onClose={() => setVideoModalOpen(false)}
            videoUrl="https://youtu.be/1nCY5LfYIGE"
            title="Taurus Installation Guide"
          />

          {/* Guided Tour */}
          <GuidedTour
            isOpen={showGuidedTour}
            onClose={() => setShowGuidedTour(false)}
            onComplete={() => {
              console.log('Guided tour completed!');
              setShowGuidedTour(false);
            }}
            userId={user?.id}
            showNotification={showNotification}
          />

          {/* Project Editor - full screen when editing a project */}
          {projectEditorMode && (
            <ProjectEditor
              project={projectEditorMode}
              onSave={handleSaveProject}
              onBack={handleBackFromEditor}
              onRun={handleRunProject}
            />
          )}

          {/* Taurus Question Modal - for AskUserQuestion tool */}
          {taurusQuestionModal.open && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-xl w-full max-h-[85vh] flex flex-col overflow-hidden">
                <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <span className="text-xl">❓</span>
                    Claude needs clarification
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Answer to help Claude continue:
                  </p>
                </div>
                <form
                  className="flex flex-col flex-1 overflow-hidden"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const answers = {};
                    taurusQuestionModal.questions.forEach((q, idx) => {
                      const questionText = q.question || q;
                      const isMultiSelect = q.multiSelect || (questionText.toLowerCase().includes('select all'));
                      if (isMultiSelect) {
                        // Collect all checked values for multiSelect
                        const values = formData.getAll(`answer_${idx}`);
                        answers[questionText] = values.length > 0 ? values.join(', ') : 'None selected';
                      } else {
                        answers[questionText] = formData.get(`answer_${idx}`) || 'No answer provided';
                      }
                    });
                    submitTaurusAnswer(taurusQuestionModal.requestId, answers);
                  }}
                >
                  <div className="p-4 space-y-4 overflow-y-auto flex-1">
                    {taurusQuestionModal.questions.map((q, idx) => {
                      const questionText = q.question || q;
                      const options = q.options || [];
                      const isMultiSelect = q.multiSelect || (questionText.toLowerCase().includes('select all'));
                      return (
                        <div key={idx} className={`space-y-2 ${idx > 0 ? 'pt-3 border-t border-gray-200 dark:border-gray-700' : ''}`}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                            {questionText}
                            {isMultiSelect && <span className="text-xs text-gray-500 ml-1">(multiple)</span>}
                          </label>
                          {options.length > 0 ? (
                            <div className="space-y-1.5 pl-1">
                              {options.map((opt, optIdx) => (
                                <label key={optIdx} className="flex items-start gap-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded p-1 -ml-1">
                                  <input
                                    type={isMultiSelect ? "checkbox" : "radio"}
                                    name={`answer_${idx}`}
                                    value={opt.label || opt}
                                    className="mt-0.5 h-4 w-4 text-blue-600 focus:ring-blue-500 rounded"
                                    defaultChecked={!isMultiSelect && optIdx === 0}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <span className="text-sm text-gray-900 dark:text-white">{opt.label || opt}</span>
                                    {opt.description && (
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{opt.description}</p>
                                    )}
                                  </div>
                                </label>
                              ))}
                            </div>
                          ) : (
                            <textarea
                              name={`answer_${idx}`}
                              rows={2}
                              className="w-full bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                              placeholder="Type your answer..."
                              autoFocus={idx === 0}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700 flex-shrink-0 bg-gray-50 dark:bg-gray-800/50">
                    <button
                      type="button"
                      onClick={() => {
                        const answers = {};
                        taurusQuestionModal.questions.forEach(q => {
                          const questionText = q.question || q;
                          answers[questionText] = 'Please proceed with your best judgment';
                        });
                        submitTaurusAnswer(taurusQuestionModal.requestId, answers);
                      }}
                      className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Skip
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      Submit
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Changelog Components */}
          <ChangelogToast onOpenModal={() => setShowChangelogModal(true)} />

          <ChangelogModal
            isOpen={showChangelogModal}
            onClose={() => setShowChangelogModal(false)}
          />
        </div>
      } />
    </Routes>
  );
}

export default App;
